import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { onboardingTranslations } from './onboardingTranslations';
import { liteTranslations } from './liteTranslations';
import { tagalogTranslations } from './tagalogTranslations';

import { languageMap, codeToLanguageMap } from './languages';
// Language maps are centralized in languages.js — re-export for backwards compatibility
export { languageMap, codeToLanguageMap, nativeLabels } from './languages';

// Helper function to get language name from browser language code
export const getBrowserLanguageName = (browserLanguage) => {
  const browserLangCode = browserLanguage.split('-')[0]; // Get 'tr' from 'tr-TR'
  return codeToLanguageMap[browserLangCode] || 'English';
};

// Get user language with proper fallback logic
const getUserLanguageCode = () => {
  const urlParams = new URLSearchParams(window.location.search);

  // Priority 1: ?language= parameter (partner integration - highest priority)
  const partnerLanguage = urlParams.get('language');
  if (partnerLanguage && Object.values(languageMap).includes(partnerLanguage)) {
    console.log('🌐 Using partner language from URL:', partnerLanguage);
    return partnerLanguage;
  }

  const userLanguage = localStorage.getItem('userLanguage');
  const i18nextLanguage = localStorage.getItem('i18nextLng');

  // Check querystring for lng parameter (lite mode)
  const querystringLanguage = urlParams.get('lng');

  const browserLanguage = navigator.language || navigator.userLanguage;
  const browserLangCode = browserLanguage.split('-')[0];

  // Priority: ?language= (partner) → userLanguage → ?lng= (explicit) → i18nextLng → browser → default
  if (userLanguage && Object.values(languageMap).includes(userLanguage)) {
    return userLanguage;
  }
  if (querystringLanguage && Object.values(languageMap).includes(querystringLanguage)) {
    return querystringLanguage;
  }
  if (i18nextLanguage && Object.values(languageMap).includes(i18nextLanguage)) {
    return i18nextLanguage;
  }
  if (Object.values(languageMap).includes(browserLangCode)) {
    return browserLangCode;
  }
  return 'en'; // Default fallback
};

const userLanguageCode = getUserLanguageCode();

// Sync i18nextLng to match resolved language, but only persist userLanguage
// if it was already explicitly set (by onboarding, settings, etc.)
// This prevents auto-detected defaults from blocking future ?lng= overrides
if (localStorage.getItem('userLanguage')) {
  if (localStorage.getItem('userLanguage') !== userLanguageCode) {
    localStorage.setItem('userLanguage', userLanguageCode);
  }
}
if (localStorage.getItem('i18nextLng') !== userLanguageCode) {
  localStorage.setItem('i18nextLng', userLanguageCode);
}

console.log('👉👉👉👉 User Language Code:', userLanguageCode);
console.log('👉 Debug localStorage:', {
  userLanguage: localStorage.getItem('userLanguage'),
  i18nextLng: localStorage.getItem('i18nextLng'),
  querystring: new URLSearchParams(window.location.search).get('lng'),
  browserLanguage: navigator.language
});

i18n
  .use(initReactI18next).init({
    resources: {
      en: {
        translation: {
          confirmReset: "Are you sure you want to reset?",
          confirmEndActivity: "Your current activity will end. Are you sure?",
          confirmEndOnboarding: "We recommend completing this introductory conversation — it only takes a few minutes and helps us personalize your learning. Are you sure you want to end?",
          onboarding_complete_title: "Great job!",
          onboarding_complete_body: "You've completed your first conversation. We're now building your personalized learning plan based on everything we learned about you.",
          ...liteTranslations.en,
          planner_edit_hint: "You'll be able to edit this goal anytime.",
          objective_generating: "We're building your personalized learning plan...",
          objective_generating_1: "Analyzing your learning goals...",
          objective_generating_2: "Selecting activities tailored to your level...",
          objective_generating_3: "Building a personalized curriculum just for you...",
          objective_generating_4: "Picking vocabulary relevant to your work...",
          objective_generating_5: "Organizing your learning path step by step...",
          objective_generating_6: "Almost there! Finalizing your plan...",
          objective_ready: "Your learning plan is ready!",
          objective_continue: "Start learning",
          objective_timeout: "Plan generation took too long. You can create one from the main app.",
          objective_activities_count: "{{count}} activities planned for you",
          confirmReplaceLearningPlan: "This will replace your current learning plan and all progress. Are you sure?",
          yes: "Yes",
          commencer: "Start",
          remind_me_later: "Remind me later",
          continue_your_learning_plan: "Continue your learning plan",
          start_your_learning_plan: "Start your learning plan",
          commencer_select_activity: "See all activities",
          or: "or",
          warning_volume: "Make sure your volume and microphone are ON!",
          ai_can_make_mistakes: "Madame AI is also learning! Like many AI tools, she can make mistakes",
          initial_text: "Click the green button and say \"{{targetGreeting}}\"",
          end_conversation: "This will end the conversation. Are you sure?",
          end_conversation_action: "See my learning plan",
          preparing_your_plan: "Preparing your plan…",
          time_remaining: "Time Remaining",
          end_label: "End",
          activity_start_conversation: "Freeform conversation",
          activity_coming_soon: "Coming soon",
          activity_tutorial: "Tutorial",
          cancel: "Cancel",
          new_conversation: "New Conversation",
          reset_conversation: "Reset Everything",
          restart_onboarding: "Restart Onboarding",
          status_disconnected: "You have been disconnected. Try speaking again or refresh the page",
          status_listening: "listening...",
          status_speaking: "speaking!",
          status_prefix: "Madame AI is",
          setting_native_language: "Native language",
          setting_target_language: "Language to learn",
          setting_realtime: "Realtime conversation",
          setting_microphone: "Microphone",
          setting_group_conversation: "Conversation",
          setting_microphone_system_default: "System default",
          setting_microphone_change_hint_chrome: "To change your microphone, click the icon to the left of the address bar and open Site settings → Microphone, or open this address in a new tab:",
          setting_microphone_change_hint_generic: "To change your microphone, open your browser's site settings (the icon to the left of the address bar) → Microphone, or change the default input in your system sound settings.",
          setting_use_custom_agent: "Use Custom Agent",
          premium_feature: "Premium feature",
          language_variant: "Language variant",
          language_france: "France",
          language_quebec: "Quebec",
          setting_echo_cancellation: "Echo cancellation",
          setting_noise_suppression: "Noise suppression",
          setting_auto_gain: "Auto gain control",
          setting_audio_model: "Audio model",
          stt_model_default: "Default",
          stt_model_conversaflex: "Conversaflex AI",
          setting_tts_speed: "Speech speed",
          tts_speed_050: "Very slow (0.5x)",
          tts_speed_075: "Slower (0.75x)",
          tts_speed_085: "Slightly slower (0.85x)",
          tts_speed_100: "Normal (1x)",
          setting_hide_assistant_text: "Hide assistant text",
          setting_preferred_pronoun: "Preferred pronoun",
          pronoun_unspecified: "Unspecified",
          pronoun_masculine: "Masculine (il)",
          pronoun_feminine: "Feminine (elle)",
          pronoun_nonbinary: "They/Them (non-binary)",
          sign_out: "Sign out",
          confirm_variant_change_title: "Change Language variant?",
          confirm_variant_change_body: "Changing the Language variant in realtime will start a new conversation. Do you want to continue?",
          confirm_mode_change_title: "Change conversation mode?",
          confirm_mode_change_body: "This will reset your current activity and start a new conversation. Do you want to continue?",
          confirm_restart: "Yes, restart",
          back: 'Back',
          close: 'Close',
          feedback_question: "What can we do to improve our product?",
          feedback_description: "Give as much details as possible!",
          submit_feedback: "Submit Feedback",
          planner_question: "What is your learning objective?",
          planner_description: "Explain in few words! For example, Make a presentation to a client.",
          submit_objective: "Submit Objective",
          last: 'Start practicing!',
          next: 'Next',
          nextLabelWithProgress: 'Next (Step {step} of {steps})',
          open: 'Open the dialog',
          welcomeMessage: "Welcome to Madame AI!",
          practiceMessage: "Practice conversations in a new language daily without any fear of being judged!",
          ageLabel: "Age:",
          motherTongueLabel: "Mother tongue:",
          levelLabel: "Level:",
          getStarted: "Get Started",
          beginner: "Beginner",
          intermediate: "Intermediate",
          advanced: "Advanced",
          termsMessage: "I have read the terms and privacy policy",
          termsAgreement: "I have read and agree to the",
          startTutorial: "Start tutorial",
          skip: "Skip",
          infoTooltip: "We save the audio and conversation data if you are an adult to customize the AI for you and save relevant info. We do not save audio when the learner is under 18.",
          adult: "Adult",
          step1Content: "Bonjour! This is Madame AI. She will help you learn and practice French!",
          step2Content: "At the top are the options. Go here to start an activity, a freeform conversation, or change some settings. The Realtime mode is a smoother conversation, recommended for more advanced learners.",
          step3Content: "When you are ready to begin, click on \"Commencer\"",
          tutorial_completed_title: "Magnifique!",
          tutorial_completed_message: "Congratulations on completing the activity.\nYou will receive an email soon with a summary.\nPlease return to the activities list to continue your learning journey.",
          go_to_activities: "Go to Activities",
          native_language_label: "What is your native language?",
          email_capture_title: "Let's get started!",
          email_capture_message: "First, enter your work email to continue:",
          first_name_placeholder: "Enter your first name",
          email_placeholder: "Enter your work email",
          continue_button: "Continue",
          invalid_email: "Please enter a valid email address",
          email_consent_label: "I agree to receive follow-up emails about this service.",
          email_newsletter_label: "Add me to the newsletter",
          email_newsletter_note: "Occasional emails when new tests are available.",
          email_work_hint: "Work email is best so we can tailor this to your workplace. You can still continue with this email.",
          work_intent_title: "Are you learning this language for work?",
          work_intent_current_job: "Yes, for my current job",
          work_intent_career: "Yes, for my career or job search",
          work_intent_team: "I'm looking into this for my team (HR / L&D)",
          work_intent_personal: "No, this is personal / school / exam practice",
          col_free_test: "Free Test",
          col_full_platform: "Full Platform",
          col_lite_version: "Lite Version",
          col_full_version: "Full Version",
          feature_lite_preview: "Preview how language learning is evolving",
          feature_lite_slower: "Slower interactions",
          feature_lite_one_activity: "Only one dynamic activity",
          feature_lite_no_domain: "Not adapted to the work domain",
          feature_lite_no_test: "No placement test or analysis",
          feature_lite_no_memory: "No memory",
          feature_lite_no_feedback: "No analysis or feedback from your interactions",
          feature_activity_free_test: "Take a free placement test",
          feature_activity_retake: "Retake tests and track progress",
          feature_activity_personalized: "Personalized learning activities",
          feature_activity_domain: "Adapted to your work domain",
          feature_activity_memory: "AI memory across conversations",
          feature_activity_feedback: "Detailed feedback and analysis",
          email_capture_terms_prefix: "By continuing, you agree to our",
          email_capture_terms_link: "Terms of Service",
          email_capture_terms_and: "and",
          email_capture_privacy_link: "Privacy Policy",
          activities_work: "Work activities",
          activities_basic: "Everyday situations",
          activities_pitch: "Your sales pitch",
          activity_pitch_practice: "Practice your pitch",
          activity_travail: "Start a work-related discussion",
          activity_learning_path: "Continue your learning path",
          activities_test_practices: "Practice a test",
          // Everyday-situation activities
          activity_culture_social_norms: "Culture and Social Norms",
          activity_greetings_introductions: "Greetings and Introductions",
          activity_numbers: "Numbers, Dates and Time",
          activity_people_relationships: "People and Relationships",
          activity_food_drinks: "Food and Drinks",
          activity_places_directions_transport: "Places, Directions and Transportation",
          activity_appointments: "Appointments and Phone Calls",
          activity_weather_small_talk: "Weather and Small Talk",
          activity_questions_requests_clarification: "Questions, Requests and Clarification",
          activity_shopping_errands: "Shopping and Errands",
          activity_colors: "Colors and Descriptions",
          placement_test_completed: "You completed the activity, let's see your results!",
          placement_test_title: "Time is up!",
          fallback_no_audio: "Could not hear, please try again.",
          browser_not_supported: "Your browser does not support audio features. Please try using Chrome, Safari, or Firefox.",
          no_audio_detected: "No audio detected. Please try speaking again.",
          audio_decode_error: "Audio file could not be processed. Please try recording again.",
          recording_timeout: "The connection timed out. Please try recording again.",
          activity_start_timeout: "We couldn't start your activity. Please check your connection and try again.",
          microphone_not_working: "Your microphone isn't reaching us. Please reload the page and start again.",
          connection_error: "Connection error. Please try again.",
          sending_for_analysis: "Sending conversation for analysis...",
          max_recording_reached: "Maximum recording time of 50 seconds reached. Your audio has been submitted.",
          recording_start_label: "Start recording",
          recording_stop_label: "Stop recording",
          recording_hint_idle: "Tap to record, or hold the button while you talk.",
          recording_hint_stop: "Tap again when you're done.",
          recording_hint_release: "Release when you're done.",
          recording_too_short: "Too short to send. Tap once to start recording.",
          placement_test_needed_title: "Before you begin",
          placement_test_needed_body: "In order for Madame AI to be even more accurate, we encourage you to take our placement test. Would you like to start now?",
          placement_test_duration_note: "This usually takes about 15 minutes.",
          test_results_title: "Test Complete",
          test_results_body: "Your results are being analyzed. You will receive your detailed results by email and they will be available in your dashboard shortly.",
          test_results_ok: "Got it!",
          daily_limit_message: "You've already completed this test today. Here are your latest results. Come back tomorrow to try again!",
          trial_session_limit_title: "You've reached today's limit",
          trial_session_limit_message: "You can practise 2 free sessions a day. Learning for work? Get in touch and we'll set you up — otherwise, come back tomorrow.",
          trial_session_limit_cta: "Contact us",
          exam_results_pending: "Your exam is being analyzed! You will receive your detailed results by email shortly. Refresh this page later to see your results.",
          conversation_feedback_title: "How was your conversation?",
          star: "star",
          stars: "stars",
          submit: "Submit",
          feedback_tags_label: "What went wrong?",
          feedback_tag_bug: "There was a bug",
          feedback_tag_not_understood: "She can't understand me",
          feedback_tag_not_interesting: "Topic wasn't interesting",
          feedback_tag_too_hard: "Too hard",
          feedback_tag_too_easy: "Too easy",
          feedback_tags_label_positive: "What did you like?",
          feedback_tag_audio_issues: "Audio problems",
          feedback_tag_too_slow: "Too slow",
          feedback_tag_repetitive: "Too repetitive",
          feedback_tag_good_topic: "Good topic",
          feedback_tag_right_level: "Right level",
          feedback_tag_good_feedback: "Good feedback",
          feedback_tag_understood_me: "Understood me",
          feedback_tag_learned_something: "Learned something new",
          feedback_tag_fun: "Fun/enjoyable",
          feedback_tag_natural_conversation: "Natural conversation",
          feedback_comment_placeholder: "Any additional comments? (optional)",
          feedback_required_hint: "Please pick a tag or add a comment so we can improve.",
          feedback_sent: "Your feedback has been sent!",
          error_no_selection_to_save: "No text selected to save.",
          error_user_not_found_for_library: "User not found. Cannot save to library.",
          text_saved_to_library_success: "Saved to your library!",
          error_saving_to_library: "Could not save to library. {{message}}",
          save_to_library_button: "Save to Library",
          error_no_conversation_id_for_vocab: "Conversation context not found. Item will be saved without it.",
          save_banner_prompt: "Save \"<strong>{{snippet}}</strong>\"?",
          loading_learning_plan: "Loading your learning plan...",
          generating_learning_plan: "Generating Your Learning Plan",
          planner_generation_message: "Please wait while we create your personalized learning plan. Feel free to practice with a Freeform conversation in the meantime!",
          generating: "Generating",
          plannerBeingCreated: "Your learning plan is being created. Please wait a moment...",
          planner_pending_objective: "Creating plan for: \"{{objective}}\"",
          plannerCreationTimeout: "Learning plan creation timed out. Please try again.",
          continue_learning_objective: "Continue your learning objective",
          continue_learning_path: "Continue learning path",
          custom_activities_for_you: "Custom activities for you",
          loading: "Loading...",
          generating_activity: "Generating your activity...",
          generating_activity_1: "Tailoring an activity just for you...",
          generating_activity_2: "Crafting something special for your level...",
          generating_activity_3: "Personalizing your next challenge...",
          generating_activity_4: "Building an activity around your interests...",
          generating_activity_5: "Preparing a fun exercise for you...",
          generating_activity_6: "Designing your personalized lesson...",
          generating_activity_7: "Creating a unique activity for your goals...",
          generating_activity_8: "Getting your next learning adventure ready...",
          loading_test_results: "Loading test results...",
          your_learning_goal: "Your Learning Goal",
          edit_goal: "Edit Goal",
          your_learning_activities: "Your Learning Activities",
          planner_activities_description: "Complete these activities in order to achieve your learning goal.",
          activity: "Activity",
          view_learning_plan: "View Learning Plan",
          test_learning_path: "Test your learning path",
          learning_progress: "Learning Progress",
          activity_progress_label: "Activity progress",
          activity_details_button: "Details",
          activity_details_vocabulary: "Vocabulary",
          activity_details_focus: "Focus",
          activity_details_format: "Format",
          activity_details_current: "This session",
          activity_details_best: "Best result",
          activity_details_attempts: "Attempts",
          activity_details_summary: "Summary so far",
          activity_details_acquired: "Acquired",
          activity_details_error: "Error",
          conversation_highlights: "Learning highlights",
          conversation_highlight_legend: "Transcript highlight legend",
          conversation_legend_error: "Your mistake",
          conversation_legend_correction: "Correction",
          conversation_legend_learned: "First learned",
          conversation_legend_reviewed: "Reviewed",
          conversation_summary_title: "Conversation summary",
          conversation_top_errors: "Top mistakes",
          conversation_no_errors: "No priority mistakes identified.",
          conversation_top_concepts: "Top concepts learned",
          conversation_no_concepts: "No concepts identified yet.",
          conversation_learned_marker: "First learned",
          conversation_reviewed_marker: "Reviewed",
          activities: "activities",
          start_your_first_activity: "Start your first activity to begin tracking progress",
          all_activities_started: "Great progress! You've started all activities",
          learning_plan_complete_title: "You've completed your learning plan!",
          learning_plan_complete_description: "Great work — you finished all {{count}} activities. Take the evaluation to see where you stand and what to work on next, then set your new goal from it.",
          create_new_learning_plan: "Set a new learning goal",
          practice_activity_again: "Practice an activity again",
          learning_plan_complete_hint: "Plan complete! Set a new goal to keep going",
          confirmNewLearningPlanAfterCompletion: "You've finished this plan. Creating a new one replaces it with fresh activities. Continue?",
          keep_practicing_activities: "Keep going! Continue practicing activities to improve",
          no_learning_goal_message: "Set up a personalized learning plan to track your progress and achieve your French learning goals.",
          set_your_learning_goal: "Set Your Learning Goal",
          microphone_permission_required: "Microphone permission required",
          microphone_permission_message: "This application requires microphone access to function in real-time mode.",
          allow_microphone: "Allow microphone",
          // Header tooltips for accessibility
          tooltip_activities: "Activities",
          tooltip_planner: "Learning Plan",
          tooltip_feedback: "Feedback",
          tooltip_more: "More Options",
          // Modal accessibility (WCAG)
          close_modal: "Close dialog",
          // Skip link and main content (WCAG)
          skip_to_main: "Skip to main content",
          main_content_label: "Main content",
          // Speech Profile
          speech_profile_title: "Speech Profile",
          speech_profile_instructions: "Read each sentence aloud so we can calibrate your speech profile.",
          speech_profile_no_sentences: "Diagnostic sentences are not yet available for your language combination. Please check back later.",
          speech_profile_back_to_app: "Back to App",
          speech_profile_listen: "Listen",
          speech_profile_playing: "Playing...",
          speech_profile_record: "Record",
          speech_profile_stop: "Stop",
          speech_profile_analyzing: "Analyzing pronunciation...",
          speech_profile_analysis_error: "Could not analyze pronunciation. Please try again.",
          speech_profile_try_again: "Try Again",
          speech_profile_next: "Next",
          speech_profile_complete_title: "Speech Profile Updated!",
          speech_profile_complete_message: "Your speech profile has been updated. The AI will now better understand your pronunciation.",
          speech_profile_overall_score: "Overall pronunciation score",
          speech_profile_start_practicing: "Start Practicing",
          // Activity page & test results
          analyzing_conversation: "Analyzing your results...",
          analyzing_may_take_time: "This may take up to a minute",
          results_score_overview: "Your Score",
          results_cefr_level: "CEFR Level",
          results_oral_comprehension: "Oral Comprehension",
          results_oral_production: "Oral Production",
          results_feedback: "Personalized Feedback",
          results_learning_plan: "Recommended Next Steps",
          results_audio_analysis: "Audio Analysis",
          results_pronunciation: "Pronunciation",
          results_vocabulary: "Vocabulary Usage",
          results_grammar: "Grammar & Conjugation",
          results_fluency: "Fluency",
          results_email_prompt: "Enter your email to receive your detailed results",
          contact_cta_heading: "Ready to accelerate your team's language learning?",
          contact_cta_text: "Contact us to get your team onboard",
          activity_not_found: "Activity not found",
          // Onboarding translations
          ...onboardingTranslations.en,
        },
      },
      ro: {
        translation: {
          confirmReset: "Sigur vrei să resetezi?",
          confirmEndActivity: "Activitatea ta curentă se va încheia. Ești sigur?",
          confirmEndOnboarding: "Îți recomandăm să finalizezi această conversație introductivă — durează doar câteva minute și ne ajută să îți personalizăm învățarea. Sigur vrei să închei?",
          onboarding_complete_title: "Bravo!",
          onboarding_complete_body: "Ai finalizat prima ta conversație. Acum îți construim planul de învățare personalizat pe baza a tot ce am aflat despre tine.",
          planner_edit_hint: "Vei putea modifica acest obiectiv oricând.",
          objective_generating: "Îți construim planul de învățare personalizat...",
          objective_generating_1: "Analizăm obiectivele tale de învățare...",
          objective_generating_2: "Selectăm activități adaptate nivelului tău...",
          objective_generating_3: "Construim o programă personalizată doar pentru tine...",
          objective_generating_4: "Alegem vocabular relevant pentru munca ta...",
          objective_generating_5: "Îți organizăm parcursul de învățare pas cu pas...",
          objective_generating_6: "Aproape gata! Finalizăm planul tău...",
          objective_ready: "Planul tău de învățare este gata!",
          objective_continue: "Începe să înveți",
          objective_timeout: "Generarea planului a durat prea mult. Îl poți crea din aplicația principală.",
          objective_activities_count: "{{count}} activități planificate pentru tine",
          confirmReplaceLearningPlan: "Aceasta va înlocui planul tău de învățare curent și tot progresul. Ești sigur?",
          yes: "Da",
          commencer: "Începe",
          remind_me_later: "Amintește-mi mai târziu",
          continue_your_learning_plan: "Continuă-ți planul de învățare",
          start_your_learning_plan: "Începe-ți planul de învățare",
          commencer_select_activity: "Vezi toate activitățile",
          or: "sau",
          warning_volume: "Asigură-te că volumul și microfonul sunt PORNITE!",
          ai_can_make_mistakes: "Madame AI învață și ea! Ca multe instrumente AI, poate greși",
          initial_text: "Apasă butonul verde și spune „{{targetGreeting}}”",
          end_conversation: "Aceasta va încheia conversația. Ești sigur?",
          end_conversation_action: "Vezi planul meu de învățare",
          preparing_your_plan: "Se pregătește planul tău…",
          time_remaining: "Timp rămas",
          end_label: "Încheie",
          activity_start_conversation: "Conversație liberă",
          activity_coming_soon: "În curând",
          activity_tutorial: "Tutorial",
          cancel: "Anulează",
          new_conversation: "Conversație nouă",
          reset_conversation: "Resetează tot",
          restart_onboarding: "Reîncepe introducerea",
          status_disconnected: "Ai fost deconectat. Încearcă să vorbești din nou sau reîncarcă pagina",
          status_listening: "ascult...",
          status_speaking: "vorbește!",
          status_prefix: "Madame AI",
          setting_native_language: "Limba maternă",
          setting_target_language: "Limba de învățat",
          setting_realtime: "Conversație în timp real",
          setting_microphone: "Microfon",
          setting_group_conversation: "Conversație",
          setting_microphone_system_default: "Implicit de sistem",
          setting_microphone_change_hint_chrome: "Pentru a schimba microfonul, apasă pictograma din stânga barei de adrese și deschide Setări site → Microfon, sau deschide această adresă într-o filă nouă:",
          setting_microphone_change_hint_generic: "Pentru a schimba microfonul, deschide setările site-ului din browser (pictograma din stânga barei de adrese) → Microfon, sau schimbă intrarea implicită din setările de sunet ale sistemului.",
          setting_use_custom_agent: "Folosește agent personalizat",
          premium_feature: "Funcție premium",
          language_variant: "Varianta limbii",
          language_france: "Franța",
          language_quebec: "Quebec",
          setting_echo_cancellation: "Anularea ecoului",
          setting_noise_suppression: "Suprimarea zgomotului",
          setting_auto_gain: "Control automat al amplificării",
          setting_audio_model: "Model audio",
          stt_model_default: "Implicit",
          stt_model_conversaflex: "Conversaflex AI",
          setting_tts_speed: "Viteza vorbirii",
          tts_speed_050: "Foarte lent (0,5x)",
          tts_speed_075: "Mai lent (0,75x)",
          tts_speed_085: "Puțin mai lent (0,85x)",
          tts_speed_100: "Normal (1x)",
          setting_hide_assistant_text: "Ascunde textul asistentului",
          setting_preferred_pronoun: "Pronume preferat",
          pronoun_unspecified: "Nespecificat",
          pronoun_masculine: "Masculin (il)",
          pronoun_feminine: "Feminin (elle)",
          pronoun_nonbinary: "El/Ea (nebinar)",
          sign_out: "Deconectare",
          confirm_variant_change_title: "Schimbi varianta limbii?",
          confirm_variant_change_body: "Schimbarea variantei limbii în timp real va începe o conversație nouă. Vrei să continui?",
          confirm_mode_change_title: "Schimbi modul de conversație?",
          confirm_mode_change_body: "Aceasta va reseta activitatea curentă și va începe o conversație nouă. Vrei să continui?",
          confirm_restart: "Da, reîncepe",
          back: 'Înapoi',
          close: 'Închide',
          feedback_question: "Ce putem face pentru a îmbunătăți produsul nostru?",
          feedback_description: "Oferă cât mai multe detalii posibil!",
          submit_feedback: "Trimite feedback",
          planner_question: "Care este obiectivul tău de învățare?",
          planner_description: "Explică în câteva cuvinte! De exemplu, Să fac o prezentare pentru un client.",
          submit_objective: "Trimite obiectivul",
          last: 'Începe să exersezi!',
          next: 'Următorul',
          nextLabelWithProgress: 'Următorul (Pasul {step} din {steps})',
          open: 'Deschide dialogul',
          welcomeMessage: "Bine ai venit la Madame AI!",
          practiceMessage: "Exersează zilnic conversații într-o limbă nouă, fără teama de a fi judecat!",
          ageLabel: "Vârstă:",
          motherTongueLabel: "Limba maternă:",
          levelLabel: "Nivel:",
          getStarted: "Începe",
          beginner: "Începător",
          intermediate: "Intermediar",
          advanced: "Avansat",
          termsMessage: "Am citit termenii și politica de confidențialitate",
          termsAgreement: "Am citit și sunt de acord cu",
          startTutorial: "Începe tutorialul",
          skip: "Omite",
          infoTooltip: "Salvăm datele audio și conversația dacă ești adult, pentru a personaliza AI-ul pentru tine și a păstra informații relevante. Nu salvăm audio când cursantul are sub 18 ani.",
          adult: "Adult",
          step1Content: "Bonjour! Aceasta este Madame AI. Te va ajuta să înveți și să exersezi franceza!",
          step2Content: "În partea de sus sunt opțiunile. Mergi aici pentru a începe o activitate, o conversație liberă sau pentru a schimba unele setări. Modul în timp real este o conversație mai fluidă, recomandată cursanților mai avansați.",
          step3Content: "Când ești gata să începi, apasă pe „Commencer”",
          tutorial_completed_title: "Magnifique!",
          tutorial_completed_message: "Felicitări pentru finalizarea activității.\nVei primi în curând un e-mail cu un rezumat.\nTe rugăm să revii la lista de activități pentru a-ți continua parcursul de învățare.",
          go_to_activities: "Mergi la activități",
          native_language_label: "Care este limba ta maternă?",
          email_capture_title: "Să începem!",
          email_capture_message: "Mai întâi, introdu e-mailul tău de serviciu pentru a continua:",
          first_name_placeholder: "Introdu prenumele tău",
          email_placeholder: "Introdu e-mailul tău de serviciu",
          continue_button: "Continuă",
          invalid_email: "Te rugăm să introduci o adresă de e-mail validă",
          email_consent_label: "Sunt de acord să primesc e-mailuri de urmărire despre acest serviciu.",
          email_newsletter_label: "Adaugă-mă la newsletter",
          email_newsletter_note: "E-mailuri ocazionale când sunt disponibile teste noi.",
          email_work_hint: "E-mailul de serviciu este cel mai potrivit ca să adaptăm totul la locul tău de muncă. Poți continua și cu acest e-mail.",
          work_intent_title: "Înveți această limbă pentru muncă?",
          work_intent_current_job: "Da, pentru jobul meu actual",
          work_intent_career: "Da, pentru cariera mea sau căutarea unui job",
          work_intent_team: "Mă informez pentru echipa mea (HR / Training)",
          work_intent_personal: "Nu, este personal / școală / pregătire pentru examen",
          col_free_test: "Test gratuit",
          col_full_platform: "Platformă completă",
          col_lite_version: "Versiune Lite",
          col_full_version: "Versiune completă",
          feature_lite_preview: "Vezi cum evoluează învățarea limbilor",
          feature_lite_slower: "Interacțiuni mai lente",
          feature_lite_one_activity: "O singură activitate dinamică",
          feature_lite_no_domain: "Neadaptată domeniului de lucru",
          feature_lite_no_test: "Fără test de plasare sau analiză",
          feature_lite_no_memory: "Fără memorie",
          feature_lite_no_feedback: "Fără analiză sau feedback din interacțiunile tale",
          feature_activity_free_test: "Susține un test de plasare gratuit",
          feature_activity_retake: "Refă testele și urmărește progresul",
          feature_activity_personalized: "Activități de învățare personalizate",
          feature_activity_domain: "Adaptate domeniului tău de lucru",
          feature_activity_memory: "Memorie AI între conversații",
          feature_activity_feedback: "Feedback și analiză detaliate",
          email_capture_terms_prefix: "Continuând, ești de acord cu",
          email_capture_terms_link: "Termenii serviciului",
          email_capture_terms_and: "și",
          email_capture_privacy_link: "Politica de confidențialitate",
          activities_work: "Activități de lucru",
          activities_basic: "Situații de zi cu zi",
          activities_pitch: "Discursul tău de vânzare",
          activity_pitch_practice: "Exersează-ți discursul de vânzare",
          activity_travail: "Începe o discuție legată de muncă",
          activity_learning_path: "Continuă-ți parcursul de învățare",
          activities_test_practices: "Exersează un test",
          // Everyday-situation activities
          activity_culture_social_norms: "Cultură și norme sociale",
          activity_greetings_introductions: "Salutări și prezentări",
          activity_numbers: "Numere, date și ore",
          activity_people_relationships: "Oameni și relații",
          activity_food_drinks: "Mâncare și băutură",
          activity_places_directions_transport: "Locuri, indicații și transport",
          activity_appointments: "Programări și apeluri telefonice",
          activity_weather_small_talk: "Vremea și conversația ușoară",
          activity_questions_requests_clarification: "Întrebări, cereri și clarificări",
          activity_shopping_errands: "Cumpărături și treburi zilnice",
          activity_colors: "Culori și descrieri",
          placement_test_completed: "Ai finalizat activitatea, hai să vedem rezultatele tale!",
          placement_test_title: "Timpul a expirat!",
          fallback_no_audio: "Nu am putut auzi, te rugăm să încerci din nou.",
          browser_not_supported: "Browserul tău nu acceptă funcțiile audio. Te rugăm să folosești Chrome, Safari sau Firefox.",
          no_audio_detected: "Nu s-a detectat audio. Te rugăm să încerci să vorbești din nou.",
          audio_decode_error: "Fișierul audio nu a putut fi procesat. Te rugăm să încerci să înregistrezi din nou.",
          recording_timeout: "Conexiunea a expirat. Te rugăm să încerci să înregistrezi din nou.",
          activity_start_timeout: "Nu am putut începe activitatea. Verifică-ți conexiunea și încearcă din nou.",
          microphone_not_working: "Microfonul tău nu ajunge la noi. Te rugăm să reîncarci pagina și să începi din nou.",
          connection_error: "Eroare de conexiune. Te rugăm să încerci din nou.",
          sending_for_analysis: "Se trimite conversația pentru analiză...",
          max_recording_reached: "S-a atins timpul maxim de înregistrare de 50 de secunde. Audio-ul tău a fost trimis.",
          recording_start_label: "Începe înregistrarea",
          recording_stop_label: "Oprește înregistrarea",
          recording_hint_idle: "Apasă pentru a înregistra sau ține apăsat butonul cât vorbești.",
          recording_hint_stop: "Apasă din nou când ai terminat.",
          recording_hint_release: "Eliberează butonul când ai terminat.",
          recording_too_short: "Prea scurt pentru a fi trimis. Apasă o singură dată pentru a începe înregistrarea.",
          placement_test_needed_title: "Înainte de a începe",
          placement_test_needed_body: "Pentru ca Madame AI să fie și mai precisă, te încurajăm să susții testul nostru de plasare. Vrei să începi acum?",
          placement_test_duration_note: "De obicei durează aproximativ 15 minute.",
          test_results_title: "Test finalizat",
          test_results_body: "Rezultatele tale sunt analizate. Vei primi rezultatele detaliate prin e-mail și vor fi disponibile în panoul tău în curând.",
          test_results_ok: "Am înțeles!",
          daily_limit_message: "Ai finalizat deja acest test astăzi. Iată cele mai recente rezultate ale tale. Revino mâine pentru a încerca din nou!",
          trial_session_limit_title: "Ai atins limita de astăzi",
          trial_session_limit_message: "Poți exersa 2 sesiuni gratuite pe zi. Înveți pentru muncă? Contactează-ne și te configurăm — altfel, revino mâine.",
          trial_session_limit_cta: "Contactează-ne",
          exam_results_pending: "Examenul tău este analizat! Vei primi rezultatele detaliate prin e-mail în curând. Reîncarcă această pagină mai târziu pentru a vedea rezultatele.",
          conversation_feedback_title: "Cum a fost conversația ta?",
          star: "stea",
          stars: "stele",
          submit: "Trimite",
          feedback_tags_label: "Ce nu a mers bine?",
          feedback_tag_bug: "A fost o eroare",
          feedback_tag_not_understood: "Nu mă poate înțelege",
          feedback_tag_not_interesting: "Subiectul nu a fost interesant",
          feedback_tag_too_hard: "Prea greu",
          feedback_tag_too_easy: "Prea ușor",
          feedback_tags_label_positive: "Ce ți-a plăcut?",
          feedback_tag_audio_issues: "Probleme audio",
          feedback_tag_too_slow: "Prea lent",
          feedback_tag_repetitive: "Prea repetitiv",
          feedback_tag_good_topic: "Subiect bun",
          feedback_tag_right_level: "Nivel potrivit",
          feedback_tag_good_feedback: "Feedback bun",
          feedback_tag_understood_me: "M-a înțeles",
          feedback_tag_learned_something: "Am învățat ceva nou",
          feedback_tag_fun: "Distractiv/plăcut",
          feedback_tag_natural_conversation: "Conversație naturală",
          feedback_comment_placeholder: "Ai comentarii suplimentare? (opțional)",
          feedback_required_hint: "Te rugăm să alegi o etichetă sau să adaugi un comentariu ca să ne putem îmbunătăți.",
          feedback_sent: "Feedbackul tău a fost trimis!",
          error_no_selection_to_save: "Niciun text selectat pentru salvare.",
          error_user_not_found_for_library: "Utilizator negăsit. Nu se poate salva în bibliotecă.",
          text_saved_to_library_success: "Salvat în biblioteca ta!",
          error_saving_to_library: "Nu s-a putut salva în bibliotecă. {{message}}",
          save_to_library_button: "Salvează în bibliotecă",
          error_no_conversation_id_for_vocab: "Contextul conversației nu a fost găsit. Elementul va fi salvat fără el.",
          save_banner_prompt: "Salvezi „<strong>{{snippet}}</strong>”?",
          loading_learning_plan: "Se încarcă planul tău de învățare...",
          generating_learning_plan: "Se generează planul tău de învățare",
          planner_generation_message: "Te rugăm să aștepți cât creăm planul tău de învățare personalizat. Între timp, poți exersa cu o conversație liberă!",
          generating: "Se generează",
          plannerBeingCreated: "Planul tău de învățare este în curs de creare. Te rugăm să aștepți un moment...",
          planner_pending_objective: "Se creează planul pentru: „{{objective}}”",
          plannerCreationTimeout: "Crearea planului de învățare a expirat. Te rugăm să încerci din nou.",
          continue_learning_objective: "Continuă-ți obiectivul de învățare",
          continue_learning_path: "Continuă parcursul de învățare",
          custom_activities_for_you: "Activități personalizate pentru tine",
          loading: "Se încarcă...",
          generating_activity: "Se generează activitatea ta...",
          generating_activity_1: "Adaptăm o activitate special pentru tine...",
          generating_activity_2: "Creăm ceva special pentru nivelul tău...",
          generating_activity_3: "Îți personalizăm următoarea provocare...",
          generating_activity_4: "Construim o activitate în jurul intereselor tale...",
          generating_activity_5: "Îți pregătim un exercițiu distractiv...",
          generating_activity_6: "Îți proiectăm lecția personalizată...",
          generating_activity_7: "Creăm o activitate unică pentru obiectivele tale...",
          generating_activity_8: "Îți pregătim următoarea aventură de învățare...",
          loading_test_results: "Se încarcă rezultatele testului...",
          your_learning_goal: "Obiectivul tău de învățare",
          edit_goal: "Modifică obiectivul",
          your_learning_activities: "Activitățile tale de învățare",
          planner_activities_description: "Finalizează aceste activități în ordine pentru a-ți atinge obiectivul de învățare.",
          activity: "Activitate",
          view_learning_plan: "Vezi planul de învățare",
          test_learning_path: "Testează-ți parcursul de învățare",
          learning_progress: "Progresul învățării",
          activity_progress_label: "Progresul activității",
          activity_details_button: "Detalii",
          activity_details_vocabulary: "Vocabular",
          activity_details_focus: "Focus",
          activity_details_format: "Format",
          activity_details_current: "Această sesiune",
          activity_details_best: "Cel mai bun rezultat",
          activity_details_attempts: "Încercări",
          activity_details_summary: "Rezumat până acum",
          activity_details_acquired: "Însușit",
          activity_details_error: "Greșeală",
          conversation_highlights: "Puncte de învățare",
          conversation_highlight_legend: "Legendă pentru evidențierea transcrierii",
          conversation_legend_error: "Greșeala ta",
          conversation_legend_correction: "Corectare",
          conversation_legend_learned: "Învățat prima dată",
          conversation_legend_reviewed: "Recapitulat",
          conversation_summary_title: "Rezumatul conversației",
          conversation_top_errors: "Principalele greșeli",
          conversation_no_errors: "Nicio greșeală prioritară identificată.",
          conversation_top_concepts: "Principalele noțiuni învățate",
          conversation_no_concepts: "Nicio noțiune identificată încă.",
          conversation_learned_marker: "Învățat prima dată",
          conversation_reviewed_marker: "Recapitulat",
          activities: "activități",
          start_your_first_activity: "Începe prima ta activitate pentru a urmări progresul",
          all_activities_started: "Progres excelent! Ai început toate activitățile",
          learning_plan_complete_title: "Ți-ai finalizat planul de învățare!",
          learning_plan_complete_description: "Felicitări — ai terminat toate cele {{count}} activități. Fă evaluarea ca să vezi unde ești și ce urmează de lucrat, apoi stabilește-ți noul obiectiv pornind de la ea.",
          create_new_learning_plan: "Stabilește un obiectiv nou",
          practice_activity_again: "Reia o activitate",
          learning_plan_complete_hint: "Plan finalizat! Stabilește un obiectiv nou ca să continui",
          confirmNewLearningPlanAfterCompletion: "Ai terminat acest plan. Crearea unuia nou îl va înlocui cu activități noi. Continui?",
          keep_practicing_activities: "Continuă! Exersează în continuare activitățile pentru a te îmbunătăți",
          no_learning_goal_message: "Configurează un plan de învățare personalizat pentru a-ți urmări progresul și a-ți atinge obiectivele de învățare a francezei.",
          set_your_learning_goal: "Setează-ți obiectivul de învățare",
          microphone_permission_required: "Este necesară permisiunea pentru microfon",
          microphone_permission_message: "Această aplicație necesită acces la microfon pentru a funcționa în modul în timp real.",
          allow_microphone: "Permite microfonul",
          // Header tooltips for accessibility
          tooltip_activities: "Activități",
          tooltip_planner: "Plan de învățare",
          tooltip_feedback: "Feedback",
          tooltip_more: "Mai multe opțiuni",
          // Modal accessibility (WCAG)
          close_modal: "Închide dialogul",
          // Skip link and main content (WCAG)
          skip_to_main: "Treci la conținutul principal",
          main_content_label: "Conținut principal",
          // Speech Profile
          speech_profile_title: "Profil de vorbire",
          speech_profile_instructions: "Citește fiecare propoziție cu voce tare ca să îți putem calibra profilul de vorbire.",
          speech_profile_no_sentences: "Propozițiile de diagnostic nu sunt încă disponibile pentru combinația ta de limbi. Te rugăm să revii mai târziu.",
          speech_profile_back_to_app: "Înapoi la aplicație",
          speech_profile_listen: "Ascultă",
          speech_profile_playing: "Se redă...",
          speech_profile_record: "Înregistrează",
          speech_profile_stop: "Oprește",
          speech_profile_analyzing: "Se analizează pronunția...",
          speech_profile_analysis_error: "Nu s-a putut analiza pronunția. Te rugăm să încerci din nou.",
          speech_profile_try_again: "Încearcă din nou",
          speech_profile_next: "Următorul",
          speech_profile_complete_title: "Profilul de vorbire a fost actualizat!",
          speech_profile_complete_message: "Profilul tău de vorbire a fost actualizat. AI-ul îți va înțelege acum mai bine pronunția.",
          speech_profile_overall_score: "Scorul general al pronunției",
          speech_profile_start_practicing: "Începe să exersezi",
          // Activity page & test results
          analyzing_conversation: "Se analizează rezultatele tale...",
          analyzing_may_take_time: "Aceasta poate dura până la un minut",
          results_score_overview: "Scorul tău",
          results_cefr_level: "Nivel CEFR",
          results_oral_comprehension: "Înțelegere orală",
          results_oral_production: "Producție orală",
          results_feedback: "Feedback personalizat",
          results_learning_plan: "Pași următori recomandați",
          results_audio_analysis: "Analiză audio",
          results_pronunciation: "Pronunție",
          results_vocabulary: "Utilizarea vocabularului",
          results_grammar: "Gramatică și conjugare",
          results_fluency: "Fluență",
          results_email_prompt: "Introdu e-mailul tău pentru a primi rezultatele detaliate",
          contact_cta_heading: "Ești gata să accelerezi învățarea limbilor de către echipa ta?",
          contact_cta_text: "Contactează-ne pentru a-ți înscrie echipa",
          activity_not_found: "Activitate negăsită",
          // Onboarding translations
          ...onboardingTranslations.ro,
        },
      },
      fr: {
        translation: {
          confirmEndActivity: "Votre activité actuelle va se terminer. Êtes-vous sûr?",
          confirmEndOnboarding: "Nous recommandons de terminer cette conversation d'introduction — cela ne prend que quelques minutes et nous aide à personnaliser votre apprentissage. Êtes-vous sûr de vouloir arrêter ?",
          onboarding_complete_title: "Bravo !",
          onboarding_complete_body: "Vous avez terminé votre première conversation. Nous créons maintenant votre plan d'apprentissage personnalisé basé sur tout ce que nous avons appris sur vous.",
          ...liteTranslations.fr,
          planner_edit_hint: "Vous pourrez modifier cet objectif à tout moment.",
          objective_generating: "Nous construisons votre plan d'apprentissage personnalisé...",
          objective_generating_1: "Analyse de vos objectifs d'apprentissage...",
          objective_generating_2: "Sélection d'activités adaptées à votre niveau...",
          objective_generating_3: "Création d'un programme personnalisé rien que pour vous...",
          objective_generating_4: "Choix du vocabulaire lié à votre domaine...",
          objective_generating_5: "Organisation de votre parcours étape par étape...",
          objective_generating_6: "Presque fini ! Finalisation de votre plan...",
          objective_ready: "Votre plan d'apprentissage est prêt !",
          objective_continue: "Commencer à apprendre",
          objective_timeout: "La génération du plan a pris trop de temps. Vous pouvez en créer un depuis l'application.",
          objective_activities_count: "{{count}} activités prévues pour vous",
          confirmReplaceLearningPlan: "Cela remplacera votre plan d'apprentissage actuel et tous vos progrès. Êtes-vous sûr?",
          yes: "Oui",
          activity_start_conversation: "Conversation libre",
          setting_native_language: "Langue maternelle",
          setting_echo_cancellation: "Annulation d'écho",
          setting_noise_suppression: "Suppression du bruit",
          setting_auto_gain: "Contrôle automatique du gain",
          setting_audio_model: "Modèle audio",
          stt_model_default: "Défaut",
          stt_model_conversaflex: "Conversaflex AI",
          setting_tts_speed: "Vitesse de parole",
          tts_speed_050: "Très lent (0.5x)",
          tts_speed_075: "Plus lent (0.75x)",
          tts_speed_085: "Légèrement plus lent (0.85x)",
          tts_speed_100: "Normal (1x)",
          setting_hide_assistant_text: "Masquer le texte de l'assistant",
          setting_preferred_pronoun: "Pronom préféré",
          pronoun_unspecified: "Non spécifié",
          pronoun_masculine: "Masculin (il)",
          pronoun_feminine: "Féminin (elle)",
          pronoun_nonbinary: "Non genré (iel)",
          back: "Retour",
          close: "Fermer",
          feedback_question: "Que pouvons-nous faire pour améliorer notre produit ?",
          feedback_description: "Donnez le plus de détails possible !",
          submit_feedback: "Envoyer commentaire",
          planner_question: "Quel est votre objectif d'apprentissage ?",
          planner_description: "Expliquez en quelques mots ! Par exemple, Faire une présentation à un client.",
          submit_objective: "Envoyer objectif",
          last: "Commencer à pratiquer !",
          next: "Suivant",
          nextLabelWithProgress: "Suivant (Étape {step} sur {steps})",
          open: "Ouvrir le dialogue",
          end_conversation: "Cela mettra fin à la conversation. Êtes-vous sûr?",
          end_conversation_action: "Voir mon plan d'apprentissage",
          preparing_your_plan: "Préparation de votre plan…",
          time_remaining: "Temps restant",
          end_label: "Terminer",
          welcomeMessage: "Bienvenue avec Madame AI!",
          practiceMessage: "Pratiquez des conversations en français tous les jours sans aucune crainte d'être jugé!",
          ageLabel: "Âge:",
          motherTongueLabel: "Langue maternelle:",
          levelLabel: "Niveau:",
          getStarted: "Commencer",
          beginner: "Débutant",
          intermediate: "Intermédiaire",
          advanced: "Avancé",
          termsMessage: "J'ai lu les termes et la politique de confidentialité",
          termsAgreement: "J'ai lu et j'accepte les",
          startTutorial: "Commencer le tutoriel",
          skip: "Passer",
          infoTooltip: "Nous enregistrons l'audio et les données de conversation si vous êtes adulte pour personnaliser l'IA pour vous et enregistrer les informations pertinentes. Nous n'enregistrons pas l'audio lorsque l'apprenant a moins de 18 ans.",
          adult: "Adulte",
          step1Content: "Bonjour! C'est Madame AI. Elle vous aidera à apprendre et à pratiquer le français!",
          step2Content: "En haut se trouvent les options. Allez ici pour choisir une activité, recommencer une conversation ou changer certains paramètres. Le mode Realtime est une conversation plus fluide, recommandée pour les apprenants plus avancés.",
          step3Content: "Lorsque vous êtes prêt à commencer, cliquez sur \"Commencer\"",
          tutorial_completed_title: "Terminé!",
          tutorial_completed_message: "Félicitations pour avoir terminé. Vous recevrez un email bientôt. Veuillez revenir à la liste des activités pour continuer votre parcours d'apprentissage.",
          go_to_activities: "Aller aux Activités",
          activities_work: "Activités professionnelles",
          activities_basic: "Situations du quotidien",
          activities_pitch: "Votre argumentaire de vente",
          activity_pitch_practice: "Pratiquez votre argumentaire",
          activity_travail: "Commencer une discussion liée au travail",
          activity_learning_path: "Continuer le plan d'apprentissage",
          activities_test_practices: "S'entraîner à un test",
          // Everyday-situation activities
          activity_culture_social_norms: "Culture et normes sociales",
          activity_greetings_introductions: "Salutations et présentations",
          activity_numbers: "Nombres, dates et heures",
          activity_people_relationships: "Les gens et les relations",
          activity_food_drinks: "Manger et boire",
          activity_places_directions_transport: "Lieux, directions et transports",
          activity_appointments: "Rendez-vous et appels téléphoniques",
          activity_weather_small_talk: "La météo et la petite conversation",
          activity_questions_requests_clarification: "Questions, demandes et clarifications",
          activity_shopping_errands: "Courses et achats",
          activity_colors: "Les couleurs et les descriptions",
          placement_test_completed: "Vous avez terminé l'activité, voyons vos résultats!",
          placement_test_title: "Le temps est écoulé!",
          native_language_label: "Quelle est votre langue maternelle ?",
          email_capture_title: "Commençons !",
          email_capture_message: "D'abord, entrez votre courriel professionnel pour continuer :",
          first_name_placeholder: "Entrez votre prénom",
          email_placeholder: "Entrez votre courriel professionnel",
          continue_button: "Continuer",
          invalid_email: "Veuillez entrer une adresse email valide",
          email_consent_label: "J'accepte de recevoir des suivis par courriel concernant ce service.",
          email_newsletter_label: "Ajoutez-moi à l'infolettre",
          email_newsletter_note: "Quelques courriels à l'occasion lorsque de nouveaux tests sont disponibles.",
          email_work_hint: "Un courriel professionnel est préférable pour adapter l’expérience à votre milieu de travail. Vous pouvez quand même continuer avec ce courriel.",
          work_intent_title: "Apprenez-vous cette langue pour le travail ?",
          work_intent_current_job: "Oui, pour mon emploi actuel",
          work_intent_career: "Oui, pour ma carrière ou ma recherche d’emploi",
          work_intent_team: "Je me renseigne pour mon équipe (RH / Formation)",
          work_intent_personal: "Non, c’est personnel / scolaire / pour un examen",
          col_free_test: "Test gratuit",
          col_full_platform: "Plateforme complète",
          col_lite_version: "Version Lite",
          col_full_version: "Version complète",
          feature_lite_preview: "Aperçu de l'évolution de l'apprentissage des langues",
          feature_lite_slower: "Interactions plus lentes",
          feature_lite_one_activity: "Une seule activité dynamique",
          feature_lite_no_domain: "Non adapté au domaine de travail",
          feature_lite_no_test: "Pas de test de classement ni d'analyse",
          feature_lite_no_memory: "Pas de mémoire",
          feature_lite_no_feedback: "Pas d'analyse ni de rétroaction de vos interactions",
          feature_activity_free_test: "Passer un test de classement gratuit",
          feature_activity_retake: "Reprendre les tests et suivre vos progrès",
          feature_activity_personalized: "Activités d'apprentissage personnalisées",
          feature_activity_domain: "Adapté à votre domaine de travail",
          feature_activity_memory: "Mémoire IA entre les conversations",
          feature_activity_feedback: "Rétroaction et analyse détaillées",
          email_capture_terms_prefix: "En continuant, vous acceptez nos",
          email_capture_terms_link: "Conditions d'utilisation",
          email_capture_terms_and: "et notre",
          email_capture_privacy_link: "Politique de confidentialité",
          fallback_no_audio: "Je n'ai pas entendu, veuillez réessayer.",
          browser_not_supported: "Votre navigateur ne prend pas en charge les fonctionnalités audio. Veuillez essayer Chrome, Safari ou Firefox.",
          no_audio_detected: "Aucun audio détecté. Veuillez réessayer de parler.",
          audio_decode_error: "Le fichier audio n'a pas pu être traité. Veuillez réessayer d'enregistrer.",
          recording_timeout: "La connexion a expiré. Veuillez réessayer d'enregistrer.",
          activity_start_timeout: "Nous n'avons pas pu démarrer votre activité. Vérifiez votre connexion et réessayez.",
          microphone_not_working: "Votre micro ne nous parvient pas. Rechargez la page et recommencez.",
          connection_error: "Erreur de connexion. Veuillez réessayer.",
          sending_for_analysis: "Envoi de la conversation pour analyse...",
          max_recording_reached: "Durée maximale d'enregistrement de 50 secondes atteinte. Votre audio a été soumis.",
          recording_start_label: "Démarrer l'enregistrement",
          recording_stop_label: "Arrêter l'enregistrement",
          recording_hint_idle: "Appuyez pour enregistrer, ou maintenez le bouton pendant que vous parlez.",
          recording_hint_stop: "Appuyez à nouveau quand vous avez terminé.",
          recording_hint_release: "Relâchez le bouton quand vous avez terminé.",
          recording_too_short: "Trop court pour être envoyé. Appuyez une seule fois pour commencer l'enregistrement.",
          placement_test_needed_title: "Avant de commencer",
          placement_test_needed_body: "Afin que Madame AI soit adaptée à votre niveau, nous vous encourageons à prendre un test de classement. Prêt à commencer?",
          placement_test_duration_note: "Cela prend généralement environ 15 minutes.",
          test_results_title: "Test terminé",
          test_results_body: "Vos résultats sont en cours d'analyse. Vous recevrez vos résultats détaillés par courriel et ils seront disponibles dans votre tableau de bord sous peu.",
          test_results_ok: "Compris!",
          daily_limit_message: "Vous avez déjà complété ce test aujourd'hui. Voici vos derniers résultats. Revenez demain pour réessayer !",
          trial_session_limit_title: "Vous avez atteint la limite du jour",
          trial_session_limit_message: "Vous pouvez faire 2 sessions gratuites par jour. Vous apprenez pour le travail ? Contactez-nous et nous vous accompagnerons — sinon, revenez demain.",
          trial_session_limit_cta: "Contactez-nous",
          exam_results_pending: "Votre examen est en cours d'analyse ! Vous recevrez vos résultats détaillés par courriel sous peu. Rafraîchissez cette page plus tard pour voir vos résultats.",
          conversation_feedback_title: "Comment était votre conversation?",
          star: "étoile",
          stars: "étoiles",
          submit: "Soumettre",
          feedback_tags_label: "Qu'est-ce qui n'a pas fonctionné?",
          feedback_tag_bug: "Il y avait un bogue",
          feedback_tag_not_understood: "Elle ne me comprend pas",
          feedback_tag_not_interesting: "Le sujet n'était pas intéressant",
          feedback_tag_too_hard: "Trop difficile",
          feedback_tag_too_easy: "Trop facile",
          feedback_tags_label_positive: "Qu'avez-vous aimé?",
          feedback_tag_audio_issues: "Problèmes audio",
          feedback_tag_too_slow: "Trop lent",
          feedback_tag_repetitive: "Trop répétitif",
          feedback_tag_good_topic: "Bon sujet",
          feedback_tag_right_level: "Bon niveau",
          feedback_tag_good_feedback: "Bons commentaires",
          feedback_tag_understood_me: "M'a bien compris",
          feedback_tag_learned_something: "J'ai appris quelque chose",
          feedback_tag_fun: "Amusant",
          feedback_tag_natural_conversation: "Conversation naturelle",
          feedback_comment_placeholder: "Des commentaires supplémentaires? (optionnel)",
          feedback_required_hint: "Veuillez choisir une étiquette ou ajouter un commentaire pour nous aider à nous améliorer.",
          feedback_sent: "Votre feedback a été envoyé!",
          error_no_selection_to_save: "Aucun texte sélectionné à enregistrer.",
          error_user_not_found_for_library: "Utilisateur non trouvé. Impossible d'enregistrer dans la bibliothèque.",
          text_saved_to_library_success: "Enregistré dans votre bibliothèque !",
          error_saving_to_library: "Impossible d'enregistrer dans la bibliothèque. {{message}}",
          save_to_library_button: "Enregistrer dans la bibliothèque",
          error_no_conversation_id_for_vocab: "Contexte de conversation non trouvé. L'élément sera enregistré sans celui-ci.",
          save_banner_prompt: "Enregistrer \"<strong>{{snippet}}</strong>\" ?",
          loading_learning_plan: "Chargement de votre plan d'apprentissage...",
          generating_learning_plan: "Génération de Votre Plan d'Apprentissage",
          planner_generation_message: "Veuillez patienter pendant que nous créons votre plan d'apprentissage personnalisé. N'hésitez pas à pratiquer avec une conversation libre en attendant !",
          generating: "Génération",
          plannerBeingCreated: "Votre plan d'apprentissage est en cours de création. Veuillez patienter un moment...",
          planner_pending_objective: "Création du plan pour : \"{{objective}}\"",
          plannerCreationTimeout: "La création du plan d'apprentissage a expiré. Veuillez réessayer.",
          continue_learning_objective: "Continuez votre objectif d'apprentissage",
          continue_learning_path: "Continuer le parcours d'apprentissage",
          custom_activities_for_you: "Activités personnalisées pour vous",
          loading: "Chargement...",
          generating_activity: "Génération de votre activité...",
          generating_activity_1: "On prépare une activité sur mesure pour vous...",
          generating_activity_2: "Création d'un exercice adapté à votre niveau...",
          generating_activity_3: "Personnalisation de votre prochain défi...",
          generating_activity_4: "On construit une activité autour de vos intérêts...",
          generating_activity_5: "Préparation d'un exercice amusant pour vous...",
          generating_activity_6: "Conception de votre leçon personnalisée...",
          generating_activity_7: "Création d'une activité unique pour vos objectifs...",
          generating_activity_8: "Votre prochaine aventure d'apprentissage arrive...",
          loading_test_results: "Chargement des résultats du test...",
          your_learning_goal: "Votre Objectif d'Apprentissage",
          edit_goal: "Modifier l'Objectif",
          your_learning_activities: "Vos Activités d'Apprentissage",
          planner_activities_description: "Complétez ces activités dans l'ordre pour atteindre votre objectif d'apprentissage.",
          activity: "Activité",
          view_learning_plan: "Voir le Plan d'Apprentissage",
          test_learning_path: "Tester votre parcours d'apprentissage",
          learning_progress: "Progrès d'Apprentissage",
          activity_progress_label: "Progression de l'activité",
          activity_details_button: "Détails",
          activity_details_vocabulary: "Vocabulaire",
          activity_details_focus: "Focus",
          activity_details_format: "Format",
          activity_details_current: "Cette session",
          activity_details_best: "Meilleur résultat",
          activity_details_attempts: "Tentatives",
          activity_details_summary: "Résumé jusqu’ici",
          activity_details_acquired: "Acquis",
          activity_details_error: "Erreur",
          conversation_highlights: "Points d’apprentissage",
          conversation_highlight_legend: "Légende du surlignage de la transcription",
          conversation_legend_error: "Votre erreur",
          conversation_legend_correction: "Correction",
          conversation_legend_learned: "Appris pour la première fois",
          conversation_legend_reviewed: "Revu",
          conversation_summary_title: "Résumé de la conversation",
          conversation_top_errors: "Principales erreurs",
          conversation_no_errors: "Aucune erreur prioritaire identifiée.",
          conversation_top_concepts: "Principales notions apprises",
          conversation_no_concepts: "Aucune notion identifiée pour le moment.",
          conversation_learned_marker: "Appris pour la première fois",
          conversation_reviewed_marker: "Revu",
          activities: "activités",
          start_your_first_activity: "Commencez votre première activité pour suivre les progrès",
          all_activities_started: "Excellent progrès ! Vous avez commencé toutes les activités",
          learning_plan_complete_title: "Vous avez terminé votre plan d'apprentissage !",
          learning_plan_complete_description: "Bravo — vous avez terminé les {{count}} activités. Faites l'évaluation pour voir où vous en êtes et ce qu'il faut travailler ensuite, puis définissez votre nouvel objectif à partir de là.",
          create_new_learning_plan: "Définir un nouvel objectif",
          practice_activity_again: "Refaire une activité",
          learning_plan_complete_hint: "Plan terminé ! Définissez un nouvel objectif pour continuer",
          confirmNewLearningPlanAfterCompletion: "Vous avez terminé ce plan. En créer un nouveau le remplacera par de nouvelles activités. Continuer ?",
          keep_practicing_activities: "Continuez ! Continuez à pratiquer les activités pour vous améliorer",
          no_learning_goal_message: "Configurez un plan d'apprentissage personnalisé pour suivre vos progrès et atteindre vos objectifs d'apprentissage du français.",
          set_your_learning_goal: "Définir Votre Objectif d'Apprentissage",
          microphone_permission_required: "Permission microphone requise",
          microphone_permission_message: "Cette application nécessite l'accès au microphone pour fonctionner en mode temps réel.",
          allow_microphone: "Autoriser le microphone",
          confirmReset: "Êtes-vous sûr de vouloir tout réinitialiser ?",
          commencer: "Commencer",
          remind_me_later: "Me le rappeler plus tard",
          continue_your_learning_plan: "Continuer votre plan d'apprentissage",
          start_your_learning_plan: "Commencer votre plan d'apprentissage",
          commencer_select_activity: "Voir toutes les activités",
          or: "ou",
          warning_volume: "Assurez-vous que le volume et les microphones sont ALLUMÉS !",
          ai_can_make_mistakes: "Madame AI apprend aussi ! Comme beaucoup d'outils d'IA, elle peut faire des erreurs",
          initial_text: "Cliquez sur le bouton vert et dites \"Bonjour !\"",
          end_conversation: "Cela terminera la conversation. Êtes-vous sûr ?",
          end_conversation_action: "Voir mon plan d'apprentissage",
          preparing_your_plan: "Préparation de votre plan…",
          time_remaining: "Temps restant",
          end_label: "Terminer",
          activity_coming_soon: "Bientôt disponible",
          activity_tutorial: "Tutoriel",
          cancel: "Annuler",
          new_conversation: "Nouvelle conversation",
          reset_conversation: "Réinitialiser tout",
          restart_onboarding: "Recommencer le guide de démarrage",
          status_disconnected: "Vous avez été déconnecté. Essayez de parler à nouveau ou actualisez la page",
          status_listening: "d'écouter...",
          status_speaking: "de parler !",
          status_prefix: "Madame AI est en train",
          setting_target_language: "Langue à apprendre",
          setting_realtime: "Conversation en temps réel",
          setting_microphone: "Microphone",
          setting_group_conversation: "Conversation",
          setting_microphone_system_default: "Par défaut du système",
          setting_microphone_change_hint_chrome: "Pour changer de microphone, cliquez sur l'icône à gauche de la barre d'adresse et ouvrez Paramètres du site → Microphone, ou ouvrez cette adresse dans un nouvel onglet :",
          setting_microphone_change_hint_generic: "Pour changer de microphone, ouvrez les paramètres du site de votre navigateur (l'icône à gauche de la barre d'adresse) → Microphone, ou modifiez l'entrée par défaut dans les paramètres de son de votre système.",
          setting_use_custom_agent: "Utiliser l'agent personnalisé",
          premium_feature: "Fonctionnalité premium",
          language_variant: "Variante du français",
          language_france: "France",
          language_quebec: "Québec",
          sign_out: "Se déconnecter",
          confirm_variant_change_title: "Changer la variante du français ?",
          confirm_variant_change_body: "Changer la variante en temps réel démarrera une nouvelle conversation. Voulez-vous continuer ?",
          confirm_mode_change_title: "Changer le mode de conversation ?",
          confirm_mode_change_body: "Cela réinitialisera votre activité actuelle et démarrera une nouvelle conversation. Voulez-vous continuer ?",
          confirm_restart: "Oui, redémarrer",
          // Header tooltips for accessibility
          tooltip_activities: "Activités",
          tooltip_planner: "Plan d'apprentissage",
          tooltip_feedback: "Envoyer commentaires",
          tooltip_more: "Plus d'options",
          // Modal accessibility (WCAG)
          close_modal: "Fermer le dialogue",
          // Skip link and main content (WCAG)
          skip_to_main: "Aller au contenu principal",
          main_content_label: "Contenu principal",
          // Speech Profile
          speech_profile_title: "Profil vocal",
          speech_profile_instructions: "Lisez chaque phrase à voix haute pour calibrer votre profil vocal.",
          speech_profile_no_sentences: "Les phrases de diagnostic ne sont pas encore disponibles pour votre combinaison de langues. Revenez plus tard.",
          speech_profile_back_to_app: "Retour à l'application",
          speech_profile_listen: "Écouter",
          speech_profile_playing: "Lecture...",
          speech_profile_record: "Enregistrer",
          speech_profile_stop: "Arrêter",
          speech_profile_analyzing: "Analyse de la prononciation...",
          speech_profile_analysis_error: "Impossible d'analyser la prononciation. Veuillez réessayer.",
          speech_profile_try_again: "Réessayer",
          speech_profile_next: "Suivant",
          speech_profile_complete_title: "Profil vocal mis à jour !",
          speech_profile_complete_message: "Votre profil vocal a été mis à jour. L'IA comprendra mieux votre prononciation.",
          speech_profile_overall_score: "Score de prononciation global",
          speech_profile_start_practicing: "Commencer à pratiquer",
          // Activity page & test results
          analyzing_conversation: "Analyse de vos résultats...",
          analyzing_may_take_time: "Cela peut prendre jusqu'à une minute",
          results_score_overview: "Votre score",
          results_cefr_level: "Niveau CECRL",
          results_oral_comprehension: "Compréhension orale",
          results_oral_production: "Production orale",
          results_feedback: "Commentaires personnalisés",
          results_learning_plan: "Prochaines étapes recommandées",
          results_audio_analysis: "Analyse audio",
          results_pronunciation: "Prononciation",
          results_vocabulary: "Utilisation du vocabulaire",
          results_grammar: "Grammaire et conjugaison",
          results_fluency: "Fluidité",
          results_email_prompt: "Entrez votre courriel pour recevoir vos résultats détaillés",
          contact_cta_heading: "Prêt à accélérer l'apprentissage linguistique de votre équipe ?",
          contact_cta_text: "Contactez-nous pour intégrer votre équipe",
          activity_not_found: "Activité introuvable",
          // Onboarding translations
          ...onboardingTranslations.fr,
        },
      },
      es: {
        translation: {
          confirmReset: "¿Estás seguro de que deseas restablecer?",
          confirmEndActivity: "Tu actividad actual terminará. ¿Estás seguro?",
          confirmEndOnboarding: "Te recomendamos completar esta conversación introductoria — solo toma unos minutos y nos ayuda a personalizar tu aprendizaje. ¿Estás seguro de que quieres terminar?",
          onboarding_complete_title: "¡Buen trabajo!",
          onboarding_complete_body: "Has completado tu primera conversación. Estamos creando tu plan de aprendizaje personalizado basado en todo lo que aprendimos sobre ti.",
          planner_edit_hint: "Podrás editar este objetivo en cualquier momento.",
          objective_generating: "Estamos creando tu plan de aprendizaje personalizado...",
          objective_generating_1: "Analizando tus objetivos de aprendizaje...",
          objective_generating_2: "Seleccionando actividades adaptadas a tu nivel...",
          objective_generating_3: "Creando un plan personalizado solo para ti...",
          objective_generating_4: "Eligiendo vocabulario relevante para tu trabajo...",
          objective_generating_5: "Organizando tu camino de aprendizaje paso a paso...",
          objective_generating_6: "¡Ya casi! Finalizando tu plan...",
          objective_ready: "¡Tu plan de aprendizaje está listo!",
          objective_continue: "Empezar a aprender",
          objective_timeout: "La generación del plan tardó demasiado. Puedes crear uno desde la aplicación.",
          objective_activities_count: "{{count}} actividades planificadas para ti",
          confirmReplaceLearningPlan: "Esto reemplazará tu plan de aprendizaje actual y todo tu progreso. ¿Estás seguro?",
          yes: "Sí",
          commencer: "Para empezar",
          remind_me_later: "Recuérdamelo más tarde",
          continue_your_learning_plan: "Continuar tu plan de aprendizaje",
          start_your_learning_plan: "Comenzar tu plan de aprendizaje",
          commencer_select_activity: "Ver todas las actividades",
          or: "o",
          warning_volume: "¡Asegúrate de que el volumen y los micrófonos estén ENCENDIDOS!",
          ai_can_make_mistakes: "¡Madame AI también está aprendiendo! Como muchas herramientas de IA, puede cometer errores",
          initial_text: "Haga clic en el botón verde y diga \"{{targetGreeting}}\"",
          end_conversation: "Esto terminará la conversación. ¿Estás seguro?",
          time_remaining: "Tiempo restante",
          end_conversation_action: "Ver mi plan de aprendizaje",
          preparing_your_plan: "Preparando tu plan…",
          end_label: "Fin",
          activity_start_conversation: "Conversación libre",
          activity_coming_soon: "Próximamente",
          activity_tutorial: "Tutorial",
          feedback_question: "¿Qué podemos hacer para mejorar nuestro producto?",
          feedback_description: "¡Da tantos detalles como sea posible!",
          submit_feedback: "Enviar comentario",
          planner_question: "¿Cuál es tu objetivo de aprendizaje?",
          planner_description: "¡Explica en pocas palabras! Por ejemplo, Hacer una presentación a un cliente.",
          submit_objective: "Enviar objetivo",
          tutorial_completed_title: "¡Completado!",
          tutorial_completed_message: "Felicitaciones por completar. Recibirás un email pronto. Por favor regresa a la lista de actividades para continuar tu recorrido de aprendizaje.",
          go_to_activities: "Ir a Actividades",
          native_language_label: "¿Cuál es tu lengua materna?",
          email_capture_title: "¡Comencemos!",
          email_capture_message: "Primero, ingresa tu correo de trabajo para continuar:",
          first_name_placeholder: "Ingresa tu nombre",
          email_placeholder: "Ingresa tu correo de trabajo",
          continue_button: "Continuar",
          invalid_email: "Por favor ingresa una dirección de email válida",
          email_consent_label: "Acepto recibir seguimientos por email sobre este servicio.",
          email_newsletter_label: "Añádeme al boletín",
          email_newsletter_note: "Correos ocasionales cuando hay nuevas pruebas disponibles.",
          email_work_hint: "Es mejor usar tu correo de trabajo para adaptar la experiencia a tu entorno laboral. Igual puedes continuar con este correo.",
          work_intent_title: "¿Estás aprendiendo este idioma para el trabajo?",
          work_intent_current_job: "Sí, para mi trabajo actual",
          work_intent_career: "Sí, para mi carrera o búsqueda de empleo",
          work_intent_team: "Lo estoy evaluando para mi equipo (RR. HH. / Formación)",
          work_intent_personal: "No, es personal / escolar / para un examen",
          col_free_test: "Prueba gratis",
          col_full_platform: "Plataforma completa",
          col_lite_version: "Versión Lite",
          col_full_version: "Versión completa",
          feature_lite_preview: "Vista previa de cómo evoluciona el aprendizaje de idiomas",
          feature_lite_slower: "Interacciones más lentas",
          feature_lite_one_activity: "Solo una actividad dinámica",
          feature_lite_no_domain: "No adaptado al dominio laboral",
          feature_lite_no_test: "Sin prueba de nivel ni análisis",
          feature_lite_no_memory: "Sin memoria",
          feature_lite_no_feedback: "Sin análisis ni retroalimentación de tus interacciones",
          feature_activity_free_test: "Realizar una prueba de nivel gratuita",
          feature_activity_retake: "Repetir pruebas y seguir tu progreso",
          feature_activity_personalized: "Actividades de aprendizaje personalizadas",
          feature_activity_domain: "Adaptado a tu dominio laboral",
          feature_activity_memory: "Memoria IA entre conversaciones",
          feature_activity_feedback: "Retroalimentación y análisis detallados",
          email_capture_terms_prefix: "Al continuar, aceptas nuestros",
          email_capture_terms_link: "Términos de servicio",
          email_capture_terms_and: "y nuestra",
          email_capture_privacy_link: "Política de privacidad",
          cancel: "Cancelar",
          new_conversation: "Nueva conversación",
          reset_conversation: "Restablecer todo",
          restart_onboarding: "Reiniciar introducción",
          status_disconnected: "Has sido desconectado. Intenta hablar de nuevo o actualiza la página",
          status_listening: "escuchando...",
          status_speaking: "hablando!",
          status_prefix: "Madame AI está",
          language_variant: "Variante de idioma",
          language_france: "Francia",
          language_quebec: "Quebec",
          setting_native_language: "Idioma nativo",
          setting_target_language: "Idioma a aprender",
          setting_realtime: "Conversación en tiempo real",
          setting_microphone: "Micrófono",
          setting_group_conversation: "Conversación",
          setting_microphone_system_default: "Predeterminado del sistema",
          setting_microphone_change_hint_chrome: "Para cambiar tu micrófono, haz clic en el icono a la izquierda de la barra de direcciones y abre Configuración del sitio → Micrófono, o abre esta dirección en una pestaña nueva:",
          setting_microphone_change_hint_generic: "Para cambiar tu micrófono, abre la configuración del sitio de tu navegador (el icono a la izquierda de la barra de direcciones) → Micrófono, o cambia la entrada predeterminada en la configuración de sonido de tu sistema.",
          setting_use_custom_agent: "Usar agente personalizado",
          premium_feature: "Función premium",
          setting_echo_cancellation: "Cancelación de eco",
          setting_noise_suppression: "Supresión de ruido",
          setting_auto_gain: "Control automático de ganancia",
          setting_audio_model: "Modelo de audio",
          stt_model_default: "Predeterminado",
          stt_model_conversaflex: "Conversaflex AI",
          setting_tts_speed: "Velocidad del habla",
          tts_speed_050: "Muy lento (0.5x)",
          tts_speed_075: "Más lento (0.75x)",
          tts_speed_085: "Ligeramente más lento (0.85x)",
          tts_speed_100: "Normal (1x)",
          setting_hide_assistant_text: "Ocultar texto del asistente",
          setting_preferred_pronoun: "Pronombre preferido",
          pronoun_unspecified: "Sin especificar",
          pronoun_masculine: "Masculino (il)",
          pronoun_feminine: "Femenino (elle)",
          pronoun_nonbinary: "No binario (elle)",
          sign_out: "Cerrar sesión",
          confirm_variant_change_title: "¿Cambiar la variante de francés?",
          confirm_variant_change_body: "Cambiar la variante de francés en tiempo real iniciará una nueva conversación. ¿Quieres continuar?",
          confirm_mode_change_title: "¿Cambiar el modo de conversación?",
          confirm_mode_change_body: "Esto reiniciará tu actividad actual e iniciará una nueva conversación. ¿Quieres continuar?",
          confirm_restart: "Sí, reiniciar",
          back: 'Atrás',
          close: 'Cerrar',
          last: 'Último',
          next: 'Siguiente',
          nextLabelWithProgress: 'Siguiente (Paso {step} de {steps})',
          open: 'Abrir el diálogo',
          welcomeMessage: "¡Bienvenido a Madame AI!",
          practiceMessage: "¡Practica conversaciones en francés diariamente sin ningún miedo a ser juzgado!",
          ageLabel: "Edad:",
          motherTongueLabel: "Lengua materna:",
          levelLabel: "Nivel:",
          getStarted: "Comenzar",
          beginner: "Principiante",
          intermediate: "Intermedio",
          advanced: "Avancé",
          termsMessage: "He leído los términos y la política de privacidad",
          termsAgreement: "He leído y acepto los",
          startTutorial: "Iniciar tutorial",
          skip: "Omitir",
          infoTooltip: "Guardamos el audio y los datos de conversación si eres adulto para personalizar la IA para ti y guardar información relevante. No guardamos audio cuando el aprendiz es menor de 18 años.",
          adult: "Adulto",
          step1Content: "Bonjour! Esta es Madame AI. Ella te ayudará a aprender y practicar francés!",
          step2Content: "En la parte superior están las opciones. Ve aquí para comenzar una actividad, una conversación libre o cambiar algunas configuraciones. El modo Realtime es una conversación más fluida, recomendada para los estudiantes más avanzados.",
          step3Content: "Cuando estés listo para comenzar, haz clic en \"Commencer\"",
          activities_work: "Actividades laborales",
          activities_basic: "Situaciones cotidianas",
          activities_pitch: "Tu discurso de ventas",
          activity_pitch_practice: "Practica tu discurso de ventas",
          activity_travail: "Iniciar una discusión relacionada con el trabajo",
          activity_learning_path: "Continúa tu plan de aprendizaje",
          activities_test_practices: "Practicar una prueba",
          // Everyday-situation activities
          activity_culture_social_norms: "Cultura y normas sociales",
          activity_greetings_introductions: "Saludos y presentaciones",
          activity_numbers: "Números, fechas y horas",
          activity_people_relationships: "Personas y relaciones",
          activity_food_drinks: "Comida y bebida",
          activity_places_directions_transport: "Lugares, direcciones y transporte",
          activity_appointments: "Citas y llamadas telefónicas",
          activity_weather_small_talk: "El tiempo y la charla informal",
          activity_questions_requests_clarification: "Preguntas, peticiones y aclaraciones",
          activity_shopping_errands: "Compras y recados",
          activity_colors: "Colores y descripciones",
          placement_test_completed: "¡Has completado la actividad, veamos tus resultados!",
          placement_test_title: "¡Se acabó el tiempo!",
          fallback_no_audio: "No pude escuchar, por favor inténtalo de nuevo.",
          browser_not_supported: "Tu navegador no es compatible con las funciones de audio. Por favor, prueba con Chrome, Safari o Firefox.",
          no_audio_detected: "No se detectó audio. Por favor, intenta hablar de nuevo.",
          audio_decode_error: "No se pudo procesar el archivo de audio. Por favor, intenta grabar de nuevo.",
          recording_timeout: "Se agotó el tiempo de conexión. Por favor, intenta grabar de nuevo.",
          activity_start_timeout: "No pudimos iniciar tu actividad. Comprueba tu conexión e inténtalo de nuevo.",
          microphone_not_working: "Tu micrófono no nos llega. Recarga la página y vuelve a empezar.",
          connection_error: "Error de conexión. Por favor, inténtalo de nuevo.",
          sending_for_analysis: "Enviando conversación para análisis...",
          max_recording_reached: "Se alcanzó el tiempo máximo de grabación de 50 segundos. Tu audio ha sido enviado.",
          recording_start_label: "Empezar a grabar",
          recording_stop_label: "Detener la grabación",
          recording_hint_idle: "Toca para grabar o mantén pulsado el botón mientras hablas.",
          recording_hint_stop: "Toca de nuevo cuando termines.",
          recording_hint_release: "Suelta el botón cuando termines.",
          recording_too_short: "Demasiado corto para enviarlo. Toca una sola vez para empezar a grabar.",
          placement_test_needed_title: "Antes de que empieces",
          placement_test_needed_body: "Para que Madame AI sea aún más precisa, te animamos a que hagas nuestra prueba de nivel. ¿Te gustaría empezar ahora?",
          placement_test_duration_note: "Suele tardar unos 15 minutos.",
          test_results_title: "Prueba completada",
          test_results_body: "Tus resultados están siendo analizados. Recibirás tus resultados detallados por correo electrónico y estarán disponibles en tu panel de control en breve.",
          test_results_ok: "¡Entendido!",
          daily_limit_message: "Ya completaste este test hoy. Aquí están tus últimos resultados. ¡Vuelve mañana para intentarlo de nuevo!",
          trial_session_limit_title: "Has alcanzado el límite de hoy",
          trial_session_limit_message: "Puedes practicar 2 sesiones gratuitas al día. ¿Aprendes por trabajo? Ponte en contacto con nosotros y te ayudaremos; si no, vuelve mañana.",
          trial_session_limit_cta: "Contáctanos",
          exam_results_pending: "¡Tu examen está siendo analizado! Recibirás tus resultados detallados por correo electrónico en breve. Actualiza esta página más tarde para ver tus resultados.",
          conversation_feedback_title: "¿Cómo fue tu conversación?",
          star: "estrella",
          stars: "estrellas",
          submit: "Enviar",
          feedback_tags_label: "¿Qué salió mal?",
          feedback_tag_bug: "Hubo un error",
          feedback_tag_not_understood: "No me entiende",
          feedback_tag_not_interesting: "El tema no era interesante",
          feedback_tag_too_hard: "Muy difícil",
          feedback_tag_too_easy: "Muy fácil",
          feedback_tags_label_positive: "¿Qué te gustó?",
          feedback_tag_audio_issues: "Problemas de audio",
          feedback_tag_too_slow: "Muy lento",
          feedback_tag_repetitive: "Muy repetitivo",
          feedback_tag_good_topic: "Buen tema",
          feedback_tag_right_level: "Nivel adecuado",
          feedback_tag_good_feedback: "Buenos comentarios",
          feedback_tag_understood_me: "Me entendió",
          feedback_tag_learned_something: "Aprendí algo nuevo",
          feedback_tag_fun: "Divertido",
          feedback_tag_natural_conversation: "Conversación natural",
          feedback_comment_placeholder: "¿Algún comentario adicional? (opcional)",
          feedback_required_hint: "Elige una etiqueta o añade un comentario para que podamos mejorar.",
          feedback_sent: "¡Tu feedback ha sido enviado!",
          error_no_selection_to_save: "No se ha seleccionado texto para guardar.",
          error_user_not_found_for_library: "Usuario no encontrado. No se puede guardar en la biblioteca.",
          text_saved_to_library_success: "¡Guardado en tu biblioteca!",
          error_saving_to_library: "No se pudo guardar en la biblioteca. {{message}}",
          save_to_library_button: "Guardar en la biblioteca",
          error_no_conversation_id_for_vocab: "Contexto de conversación no encontrado. El elemento se guardará sin él.",
          save_banner_prompt: "¿Guardar \"<strong>{{snippet}}</strong>\"?",
          loading_learning_plan: "Cargando tu plan de aprendizaje...",
          generating_learning_plan: "Generando Tu Plan de Aprendizaje",
          planner_generation_message: "Por favor espera mientras creamos tu plan de aprendizaje personalizado. ¡Siéntete libre de practicar con una conversación libre mientras tanto!",
          generating: "Generando",
          plannerBeingCreated: "Tu plan de aprendizaje se está creando. Por favor espera un momento...",
          planner_pending_objective: "Creando plan para: \"{{objective}}\"",
          plannerCreationTimeout: "La creación del plan de aprendizaje ha expirado. Por favor intenta de nuevo.",
          continue_learning_objective: "Continúa tu objetivo de aprendizaje",
          custom_activities_for_you: "Actividades personalizadas para ti",
          loading: "Cargando...",
          generating_activity: "Generando tu actividad...",
          generating_activity_1: "Preparando una actividad a tu medida...",
          generating_activity_2: "Creando un ejercicio adaptado a tu nivel...",
          generating_activity_3: "Personalizando tu próximo desafío...",
          generating_activity_4: "Diseñando una actividad según tus intereses...",
          generating_activity_5: "Preparando un ejercicio divertido para ti...",
          generating_activity_6: "Diseñando tu lección personalizada...",
          generating_activity_7: "Creando una actividad única para tus objetivos...",
          generating_activity_8: "Tu próxima aventura de aprendizaje está en camino...",
          loading_test_results: "Cargando resultados de la prueba...",
          your_learning_goal: "Tu Objetivo de Aprendizaje",
          edit_goal: "Editar Objetivo",
          your_learning_activities: "Tus Actividades de Aprendizaje",
          planner_activities_description: "Completa estas actividades en orden para alcanzar tu objetivo de aprendizaje.",
          activity: "Actividad",
          view_learning_plan: "Ver Plan de Aprendizaje",
          test_learning_path: "Prueba tu ruta de aprendizaje",
          learning_progress: "Progreso de Aprendizaje",
          activity_progress_label: "Progreso de la actividad",
          activity_details_button: "Detalles",
          activity_details_vocabulary: "Vocabulario",
          activity_details_focus: "Enfoque",
          activity_details_format: "Formato",
          activity_details_current: "Esta sesión",
          activity_details_best: "Mejor resultado",
          activity_details_attempts: "Intentos",
          activity_details_summary: "Resumen hasta ahora",
          activity_details_acquired: "Adquirido",
          activity_details_error: "Error",
          conversation_highlights: "Puntos de aprendizaje",
          conversation_highlight_legend: "Leyenda de resaltado de la transcripción",
          conversation_legend_error: "Tu error",
          conversation_legend_correction: "Corrección",
          conversation_legend_learned: "Aprendido por primera vez",
          conversation_legend_reviewed: "Repasado",
          conversation_summary_title: "Resumen de la conversación",
          conversation_top_errors: "Errores principales",
          conversation_no_errors: "No se identificaron errores prioritarios.",
          conversation_top_concepts: "Principales conceptos aprendidos",
          conversation_no_concepts: "Aún no se han identificado conceptos.",
          conversation_learned_marker: "Aprendido por primera vez",
          conversation_reviewed_marker: "Repasado",
          activities: "actividades",
          start_your_first_activity: "Comienza tu primera actividad para hacer seguimiento del progreso",
          all_activities_started: "¡Excelente progreso! Has comenzado todas las actividades",
          learning_plan_complete_title: "¡Has completado tu plan de aprendizaje!",
          learning_plan_complete_description: "¡Bien hecho! Has terminado las {{count}} actividades. Haz la evaluación para ver dónde estás y qué trabajar a continuación, y define tu nuevo objetivo a partir de ahí.",
          create_new_learning_plan: "Definir un nuevo objetivo",
          practice_activity_again: "Repetir una actividad",
          learning_plan_complete_hint: "¡Plan completado! Define un nuevo objetivo para seguir avanzando",
          confirmNewLearningPlanAfterCompletion: "Has terminado este plan. Crear uno nuevo lo reemplazará con nuevas actividades. ¿Continuar?",
          keep_practicing_activities: "¡Sigue así! Continúa practicando actividades para mejorar",
          no_learning_goal_message: "Configura un plan de aprendizaje personalizado para seguir tu progreso y alcanzar tus objetivos de aprendizaje de francés.",
          set_your_learning_goal: "Establecer Tu Objetivo de Aprendizaje",
          microphone_permission_required: "Permiso de micrófono requerido",
          microphone_permission_message: "Esta aplicación requiere acceso al micrófono para funcionar en modo tiempo real.",
          allow_microphone: "Permitir micrófono",
          // Header tooltips for accessibility
          tooltip_activities: "Actividades",
          tooltip_planner: "Plan de Aprendizaje",
          tooltip_feedback: "Comentarios",
          tooltip_more: "Más Opciones",
          // Modal accessibility (WCAG)
          close_modal: "Cerrar diálogo",
          // Skip link and main content (WCAG)
          skip_to_main: "Saltar al contenido principal",
          main_content_label: "Contenido principal",
          // Speech Profile
          speech_profile_title: "Perfil de voz",
          speech_profile_instructions: "Lea cada frase en voz alta para calibrar su perfil de voz.",
          speech_profile_no_sentences: "Las frases de diagnóstico aún no están disponibles para su combinación de idiomas. Vuelva más tarde.",
          speech_profile_back_to_app: "Volver a la aplicación",
          speech_profile_listen: "Escuchar",
          speech_profile_playing: "Reproduciendo...",
          speech_profile_record: "Grabar",
          speech_profile_stop: "Parar",
          speech_profile_analyzing: "Analizando pronunciación...",
          speech_profile_analysis_error: "No se pudo analizar la pronunciación. Inténtelo de nuevo.",
          speech_profile_try_again: "Reintentar",
          speech_profile_next: "Siguiente",
          speech_profile_complete_title: "¡Perfil de voz actualizado!",
          speech_profile_complete_message: "Su perfil de voz ha sido actualizado. La IA entenderá mejor su pronunciación.",
          speech_profile_overall_score: "Puntuación general de pronunciación",
          speech_profile_start_practicing: "Empezar a practicar",
          // Activity page & test results
          analyzing_conversation: "Analizando tus resultados...",
          analyzing_may_take_time: "Esto puede tardar hasta un minuto",
          results_score_overview: "Tu puntuación",
          results_cefr_level: "Nivel MCER",
          results_oral_comprehension: "Comprensión oral",
          results_oral_production: "Producción oral",
          results_feedback: "Comentarios personalizados",
          results_learning_plan: "Próximos pasos recomendados",
          results_audio_analysis: "Análisis de audio",
          results_pronunciation: "Pronunciación",
          results_vocabulary: "Uso del vocabulario",
          results_grammar: "Gramática y conjugación",
          results_fluency: "Fluidez",
          results_email_prompt: "Introduce tu correo electrónico para recibir tus resultados detallados",
          contact_cta_heading: "¿Listo para acelerar el aprendizaje de idiomas de tu equipo?",
          contact_cta_text: "Contáctanos para incorporar a tu equipo",
          activity_not_found: "Actividad no encontrada",
          // Onboarding translations
          ...onboardingTranslations.es,
          ...liteTranslations.es,
          continue_learning_path: "Continuar el plan de aprendizaje",
        },
      },
      zh: {
        translation: {
          confirmReset: "确定要重置吗？",
          confirmEndActivity: "您当前的活动将结束。您确定吗？",
          confirmEndOnboarding: "我们建议您完成这段介绍性对话——只需几分钟，有助于我们个性化您的学习。您确定要结束吗？",
          onboarding_complete_title: "做得好！",
          onboarding_complete_body: "您已完成第一次对话。我们正在根据对您的了解为您制定个性化学习计划。",
          planner_edit_hint: "您可以随时编辑此目标。",
          objective_generating: "我们正在为您创建个性化学习计划...",
          objective_ready: "您的学习计划已准备就绪！",
          objective_continue: "开始学习",
          objective_timeout: "计划生成时间过长。您可以从主应用中创建一个。",
          objective_activities_count: "为您规划了 {{count}} 个活动",
          commencer: "开始",
          remind_me_later: "稍后提醒我",
          continue_your_learning_plan: "继续你的学习计划",
          start_your_learning_plan: "开始你的学习计划",
          commencer_select_activity: "查看所有活动",
          or: "或",
          warning_volume: "确保您的音量已打开！",
          ai_can_make_mistakes: "Madame AI 也在学习！和许多 AI 工具一样，她也可能会犯错",
          initial_text: "点击绿色按钮并说\"{{targetGreeting}}\"",
          end_conversation: "这将结束对话。您确定吗？",
          end_conversation_action: "查看我的学习计划",
          preparing_your_plan: "正在准备您的计划…",
          time_remaining: "剩余时间",
          end_label: "结束",
          activity_start_conversation: "开始对话",
          activity_coming_soon: "即将推出",
          activity_tutorial: "教程",
          cancel: "取消",
          new_conversation: "新的对话",
          reset_conversation: "重置所有",
          restart_onboarding: "重新开始引导",
          status_disconnected: "您已断开连接。请尝试再次说话或刷新页面",
          status_listening: "正在听...",
          status_speaking: "正在说话!",
          status_prefix: "Madame AI 是",
          setting_native_language: "母语",
          setting_target_language: "要学习的语言",
          setting_realtime: "实时对话",
          setting_microphone: "麦克风",
          setting_group_conversation: "对话",
          setting_microphone_system_default: "系统默认",
          setting_microphone_change_hint_chrome: "要更改麦克风，请点击地址栏左侧的图标并打开网站设置 → 麦克风，或在新标签页中打开此地址：",
          setting_microphone_change_hint_generic: "要更改麦克风，请打开浏览器的网站设置（地址栏左侧的图标）→ 麦克风，或在系统声音设置中更改默认输入设备。",
          setting_use_custom_agent: "使用自定义代理",
          premium_feature: "高级功能",
          language_variant: "语言变体",
          language_france: "法国",
          language_quebec: "魁北克",
          setting_echo_cancellation: "回声消除",
          setting_noise_suppression: "噪音抑制",
          setting_auto_gain: "自动增益控制",
          setting_audio_model: "语音模型",
          stt_model_default: "默认",
          stt_model_conversaflex: "Conversaflex AI",
          setting_tts_speed: "语速",
          tts_speed_050: "非常慢 (0.5x)",
          tts_speed_075: "较慢 (0.75x)",
          tts_speed_085: "稍慢 (0.85x)",
          tts_speed_100: "正常 (1x)",
          setting_hide_assistant_text: "隐藏助手文本",
          setting_preferred_pronoun: "首选代词",
          pronoun_unspecified: "未指定",
          pronoun_masculine: "阳性 (il)",
          pronoun_feminine: "阴性 (elle)",
          pronoun_nonbinary: "非二元 (iel)",
          sign_out: "登出",
          confirm_variant_change_title: "更改法语变体？",
          confirm_variant_change_body: "在实时模式下更改法语变体将开始新的对话。是否继续？",
          confirm_restart: "是的，重新开始",
          back: '返回',
          close: '关闭',
          last: '最后',
          next: '下一个',
          nextLabelWithProgress: '下一个（步骤 {step} / {steps}）',
          open: '打开对话框',
          welcomeMessage: "欢迎来到 Madame AI!",
          practiceMessage: "每天练习法语对话，无需担心被评判！",
          ageLabel: "年龄:",
          motherTongueLabel: "母语:",
          levelLabel: "水平:",
          getStarted: "开始",
          beginner: "初学者",
          intermediate: "中级",
          advanced: "Avancé",
          termsMessage: "我已阅读条款和隐私政策",
          termsAgreement: "我已阅读并同意",
          startTutorial: "开始教程",
          skip: "跳过",
          infoTooltip: "如果您是成年人，我们会保存音频和对话数据，以便为您定制 AI 并保存相关信息。当学习者未满 18 岁时，我们不会保存音频。",
          adult: "成人",
          step1Content: "Bonjour! 这是 Madame AI。她将帮助您学习和练习法语！",
          step2Content: "顶部是选项。去这里开始一个活动，自由对话，或更改一些设置。实时模式是一种更流畅的对话，推荐给更高级的学习者。",
          step3Content: "当您准备好开始时，点击 \"Commencer\"",
          activities_work: "工作活动",
          activities_basic: "日常情境",
          activities_pitch: "您的销售话术",
          activity_pitch_practice: "练习您的销售话术",
          activity_travail: "开始与工作相关的讨论",
          activity_learning_path: "继续你的学习计划",
          activities_test_practices: "练习测试",
          // Everyday-situation activities
          activity_culture_social_norms: "文化与社会规范",
          activity_greetings_introductions: "打招呼和自我介绍",
          activity_numbers: "数字、日期和时间",
          activity_people_relationships: "人际关系",
          activity_food_drinks: "饮食",
          activity_places_directions_transport: "地点、方向和交通",
          activity_appointments: "预约和打电话",
          activity_weather_small_talk: "天气和寒暄",
          activity_questions_requests_clarification: "提问、请求和澄清",
          activity_shopping_errands: "购物和日常事务",
          activity_colors: "颜色和描述",
          placement_test_completed: "您已完成活动，让我们看看您的结果！",
          placement_test_title: "时间到了！",
          fallback_no_audio: "没听清楚，请再试一次。",
          browser_not_supported: "您的浏览器不支持音频功能。请尝试使用 Chrome、Safari 或 Firefox。",
          no_audio_detected: "未检测到音频。请再次尝试说话。",
          audio_decode_error: "无法处理音频文件。请重新尝试录音。",
          recording_timeout: "连接超时。请重新尝试录音。",
          activity_start_timeout: "无法开始您的活动。请检查网络连接后重试。",
          microphone_not_working: "我们收不到您的麦克风声音。请重新加载页面后再试。",
          connection_error: "连接错误。请重试。",
          sending_for_analysis: "正在发送对话进行分析...",
          max_recording_reached: "已达到50秒的最大录音时间。您的音频已提交。",
          recording_start_label: "开始录音",
          recording_stop_label: "停止录音",
          recording_hint_idle: "点击录音，或在说话时按住按钮。",
          recording_hint_stop: "说完后再次点击。",
          recording_hint_release: "说完后松开按钮。",
          recording_too_short: "录音太短，无法发送。请点击一次开始录音。",
          placement_test_needed_title: "在你开始之前",
          placement_test_needed_body: "为了让 Madame AI 更加准确，我们鼓励你参加我们的水平测试。你现在想开始吗？",
          placement_test_duration_note: "这通常需要大约15分钟。",
          test_results_title: "测试已完成",
          test_results_body: "您的结果正在分析中。您将通过电子邮件收到详细结果，它们也将很快在您的仪表板中显示。",
          test_results_ok: "知道了！",
          daily_limit_message: "您今天已经完成了此测试。以下是您的最新结果。明天再来试试吧！",
          trial_session_limit_title: "您已达到今天的上限",
          trial_session_limit_message: "每天可免费练习 2 次。为工作而学习？请联系我们，我们会为您安排；否则请明天再来。",
          trial_session_limit_cta: "联系我们",
          exam_results_pending: "您的考试正在分析中！您将很快通过电子邮件收到详细结果。稍后刷新此页面查看您的结果。",
          conversation_feedback_title: "您的对话如何？",
          star: "星",
          stars: "星",
          submit: "提交",
          feedback_tags_label: "出了什么问题？",
          feedback_tag_bug: "出现了错误",
          feedback_tag_not_understood: "她听不懂我说的",
          feedback_tag_not_interesting: "话题不有趣",
          feedback_tag_too_hard: "太难了",
          feedback_tag_too_easy: "太简单了",
          feedback_tags_label_positive: "您喜欢什么？",
          feedback_tag_audio_issues: "音频问题",
          feedback_tag_too_slow: "太慢了",
          feedback_tag_repetitive: "太重复",
          feedback_tag_good_topic: "话题很好",
          feedback_tag_right_level: "难度合适",
          feedback_tag_good_feedback: "反馈很好",
          feedback_tag_understood_me: "理解我说的",
          feedback_tag_learned_something: "学到新东西",
          feedback_tag_fun: "有趣",
          feedback_tag_natural_conversation: "对话自然",
          feedback_comment_placeholder: "还有其他评论吗？（可选）",
          feedback_required_hint: "请选择一个标签或添加评论，以便我们改进。",
          feedback_sent: "您的反馈已发送！",
          error_no_selection_to_save: "未选择要保存的文本。",
          error_user_not_found_for_library: "找不到用户。无法保存到库。",
          text_saved_to_library_success: "已保存到您的库！",
          error_saving_to_library: "无法保存到库。 {{message}}",
          save_to_library_button: "保存到库",
          error_no_conversation_id_for_vocab: "找不到对话上下文。项目将不带上下文保存。",
          loading_learning_plan: "正在加载您的学习计划...",
          generating_learning_plan: "正在生成您的学习计划",
          planner_generation_message: "请稍候，我们正在为您创建个性化学习计划。您可以在等待期间练习自由对话！",
          generating: "生成中",
          continue_learning_objective: "继续您的学习目标",
          custom_activities_for_you: "为您定制的活动",
          loading: "加载中...",
          generating_activity: "正在生成您的活动...",
          loading_test_results: "正在加载测试结果...",
          your_learning_goal: "您的学习目标",
          edit_goal: "编辑目标",
          your_learning_activities: "您的学习活动",
          planner_activities_description: "按顺序完成这些活动以实现您的学习目标。",
          activity: "活动",
          view_learning_plan: "查看学习计划",
          test_learning_path: "测试您的学习路径",
          learning_progress: "学习进度",
          activity_progress_label: "活动进度",
          activity_details_button: "详情",
          activity_details_vocabulary: "词汇",
          activity_details_focus: "重点",
          activity_details_format: "形式",
          activity_details_current: "本次会话",
          activity_details_best: "最佳成绩",
          activity_details_attempts: "尝试次数",
          activity_details_summary: "目前的总结",
          activity_details_acquired: "已掌握",
          activity_details_error: "错误",
          conversation_highlights: "学习要点",
          conversation_highlight_legend: "文字记录高亮图例",
          conversation_legend_error: "你的错误",
          conversation_legend_correction: "订正",
          conversation_legend_learned: "首次学会",
          conversation_legend_reviewed: "已复习",
          conversation_summary_title: "对话总结",
          conversation_top_errors: "主要错误",
          conversation_no_errors: "未发现需要优先纠正的错误。",
          conversation_top_concepts: "主要掌握的知识点",
          conversation_no_concepts: "暂未发现已掌握的知识点。",
          conversation_learned_marker: "首次学会",
          conversation_reviewed_marker: "已复习",
          activities: "活动",
          start_your_first_activity: "开始你的第一个活动来跟踪进度",
          all_activities_started: "进度很好！你已经开始了所有活动",
          learning_plan_complete_title: "你已完成学习计划！",
          learning_plan_complete_description: "做得好——你完成了全部 {{count}} 个活动。先做评估，看看你现在的水平和接下来该练什么，再据此设定新目标。",
          create_new_learning_plan: "设定新的学习目标",
          practice_activity_again: "再练习一个活动",
          learning_plan_complete_hint: "计划已完成！设定新目标继续学习",
          confirmNewLearningPlanAfterCompletion: "你已完成此计划。创建新计划会用新的活动替换它。要继续吗？",
          keep_practicing_activities: "继续努力！继续练习活动来提高",
          no_learning_goal_message: "设置个性化学习计划来跟踪您的进度并实现您的法语学习目标。",
          set_your_learning_goal: "设置您的学习目标",
          save_banner_prompt: "保存 “<strong>{{snippet}}</strong>”？",
          microphone_permission_required: "需要麦克风权限",
          microphone_permission_message: "此应用程序需要麦克风访问权限才能在实时模式下运行。",
          allow_microphone: "允许麦克风",
          // Header tooltips for accessibility
          tooltip_activities: "活动",
          tooltip_planner: "学习计划",
          tooltip_feedback: "反馈",
          tooltip_more: "更多选项",
          // Modal accessibility (WCAG)
          close_modal: "关闭对话框",
          // Skip link and main content (WCAG)
          skip_to_main: "跳转到主内容",
          main_content_label: "主要内容",
          // Speech Profile
          speech_profile_title: "语音档案",
          speech_profile_instructions: "请大声朗读每个句子，以便我们校准您的语音档案。",
          speech_profile_no_sentences: "您的语言组合的诊断句子尚不可用。请稍后再来。",
          speech_profile_back_to_app: "返回应用",
          speech_profile_listen: "听一听",
          speech_profile_playing: "播放中...",
          speech_profile_record: "录音",
          speech_profile_stop: "停止",
          speech_profile_analyzing: "正在分析发音...",
          speech_profile_analysis_error: "无法分析发音，请重试。",
          speech_profile_try_again: "重试",
          speech_profile_next: "下一个",
          speech_profile_complete_title: "语音档案已更新！",
          speech_profile_complete_message: "您的语音档案已更新。AI 将更好地理解您的发音。",
          speech_profile_overall_score: "总体发音分数",
          speech_profile_start_practicing: "开始练习",
          // Activity page & test results
          analyzing_conversation: "正在分析您的结果...",
          analyzing_may_take_time: "这可能需要一分钟",
          results_score_overview: "您的分数",
          results_cefr_level: "CEFR 等级",
          results_oral_comprehension: "口语理解",
          results_oral_production: "口语表达",
          results_feedback: "个性化反馈",
          results_learning_plan: "建议的后续步骤",
          results_audio_analysis: "音频分析",
          results_pronunciation: "发音",
          results_vocabulary: "词汇运用",
          results_grammar: "语法与变位",
          results_fluency: "流利度",
          results_email_prompt: "输入您的电子邮件以接收详细结果",
          contact_cta_heading: "准备好加速您团队的语言学习了吗？",
          contact_cta_text: "联系我们以让您的团队加入",
          activity_not_found: "未找到活动",
          // Onboarding translations
          ...onboardingTranslations.zh,
          objective_generating_1: "正在分析你的学习目标……",
          objective_generating_2: "正在挑选适合你水平的活动……",
          objective_generating_3: "正在为你定制专属课程……",
          objective_generating_4: "正在挑选与你工作相关的词汇……",
          objective_generating_5: "正在逐步规划你的学习路径……",
          objective_generating_6: "就快好了！正在完成你的计划……",
          confirmReplaceLearningPlan: "此操作将替换你当前的学习计划和全部进度。确定吗？",
          yes: "是",
          confirm_mode_change_title: "更改对话模式？",
          confirm_mode_change_body: "此操作将重置当前活动并开始新的对话。要继续吗？",
          feedback_question: "我们可以做些什么来改进产品？",
          feedback_description: "请尽量提供详细信息！",
          submit_feedback: "提交反馈",
          planner_question: "你的学习目标是什么？",
          planner_description: "用几句话说明即可！例如：向客户做一次演示。",
          submit_objective: "提交目标",
          tutorial_completed_title: "Magnifique!",
          tutorial_completed_message: "恭喜你完成本次活动。\n你很快会收到一封包含总结的邮件。\n请返回活动列表，继续你的学习之旅。",
          go_to_activities: "前往活动列表",
          native_language_label: "你的母语是什么？",
          email_capture_title: "开始吧！",
          email_capture_message: "请先输入你的工作邮箱以继续：",
          first_name_placeholder: "请输入你的名字",
          email_placeholder: "请输入你的工作邮箱",
          continue_button: "继续",
          invalid_email: "请输入有效的电子邮箱地址",
          email_consent_label: "我同意接收与此服务相关的后续邮件。",
          email_newsletter_label: "订阅新闻通讯",
          email_newsletter_note: "有新测试时，我们会偶尔发送邮件。",
          email_work_hint: "使用工作邮箱最好，这样我们可以根据你的工作环境进行定制。你也可以继续使用当前邮箱。",
          work_intent_title: "你学习这门语言是为了工作吗？",
          work_intent_current_job: "是的，为了我目前的工作",
          work_intent_career: "是的，为了我的职业发展或求职",
          work_intent_team: "我在为我的团队了解这项服务（人力资源／培训）",
          work_intent_personal: "不是，这是个人／学校／备考用途",
          col_free_test: "免费测试",
          col_full_platform: "完整平台",
          col_lite_version: "轻量版",
          col_full_version: "完整版",
          feature_lite_preview: "抢先体验语言学习的新方式",
          feature_lite_slower: "互动速度较慢",
          feature_lite_one_activity: "仅有一个动态活动",
          feature_lite_no_domain: "未针对工作领域定制",
          feature_lite_no_test: "没有分级测试或分析",
          feature_lite_no_memory: "没有记忆功能",
          feature_lite_no_feedback: "不会分析你的对话，也没有反馈",
          feature_activity_free_test: "参加免费分级测试",
          feature_activity_retake: "重考测试并跟踪进度",
          feature_activity_personalized: "个性化学习活动",
          feature_activity_domain: "针对你的工作领域定制",
          feature_activity_memory: "跨对话的 AI 记忆",
          feature_activity_feedback: "详细的反馈与分析",
          email_capture_terms_prefix: "继续即表示你同意我们的",
          email_capture_terms_link: "服务条款",
          email_capture_terms_and: "和",
          email_capture_privacy_link: "隐私政策",
          plannerBeingCreated: "正在创建你的学习计划，请稍候……",
          planner_pending_objective: "正在为以下目标创建计划：\"{{objective}}\"",
          plannerCreationTimeout: "创建学习计划超时，请重试。",
          continue_learning_path: "继续学习路径",
          generating_activity_1: "正在为你量身准备一个活动……",
          generating_activity_2: "正在为你的水平准备特别内容……",
          generating_activity_3: "正在为你定制下一个挑战……",
          generating_activity_4: "正在围绕你的兴趣设计活动……",
          generating_activity_5: "正在为你准备一个有趣的练习……",
          generating_activity_6: "正在设计你的专属课程……",
          generating_activity_7: "正在为你的目标创建专属活动……",
          generating_activity_8: "正在准备你的下一段学习旅程……",
        },
      },
      de: {
        translation: {
          confirmReset: "Sind Sie sicher, dass Sie zurücksetzen möchten?",
          confirmEndActivity: "Ihre aktuelle Aktivität wird beendet. Sind Sie sicher?",
          confirmEndOnboarding: "Wir empfehlen, dieses Einführungsgespräch abzuschließen — es dauert nur wenige Minuten und hilft uns, Ihr Lernen zu personalisieren. Sind Sie sicher, dass Sie beenden möchten?",
          onboarding_complete_title: "Gut gemacht!",
          onboarding_complete_body: "Sie haben Ihr erstes Gespräch abgeschlossen. Wir erstellen jetzt Ihren personalisierten Lernplan basierend auf allem, was wir über Sie erfahren haben.",
          planner_edit_hint: "Sie können dieses Ziel jederzeit bearbeiten.",
          objective_generating: "Wir erstellen Ihren personalisierten Lernplan...",
          objective_ready: "Ihr Lernplan ist fertig!",
          objective_continue: "Mit dem Lernen beginnen",
          objective_timeout: "Die Planerstellung hat zu lange gedauert. Sie können einen in der App erstellen.",
          objective_activities_count: "{{count}} Aktivitäten für Sie geplant",
          commencer: "Starten",
          remind_me_later: "Später erinnern",
          continue_your_learning_plan: "Lernplan fortsetzen",
          start_your_learning_plan: "Lernplan starten",
          commencer_select_activity: "Alle Aktivitäten anzeigen",
          or: "oder",
          warning_volume: "Stellen Sie sicher, dass Ihre Lautstärke AN ist!",
          ai_can_make_mistakes: "Madame AI lernt auch noch! Wie viele KI-Tools kann sie Fehler machen",
          initial_text: "Klicken Sie auf den grünen Knopf und sagen Sie \"{{targetGreeting}}\"",
          end_conversation: "Das wird das Gespräch beenden. Sind Sie sicher?",
          end_conversation_action: "Meinen Lernplan ansehen",
          preparing_your_plan: "Ihr Plan wird vorbereitet…",
          time_remaining: "Verbleibende Zeit",
          end_label: "Beenden",
          activity_start_conversation: "Ein Gespräch beginnen",
          activity_coming_soon: "Demnächst verfügbar",
          activity_tutorial: "Tutorial",
          cancel: "Abbrechen",
          new_conversation: "Neues Gespräch",
          reset_conversation: "Alles zurücksetzen",
          restart_onboarding: "Einführung neu starten",
          status_disconnected: "Sie wurden getrennt. Versuchen Sie es erneut zu sprechen oder aktualisieren Sie die Seite",
          status_listening: "hört zu...",
          status_speaking: "spricht!",
          status_prefix: "Madame AI ist",
          setting_native_language: "Muttersprache",
          setting_target_language: "Sprache zum Lernen",
          setting_realtime: "Echtzeitgespräch",
          setting_microphone: "Mikrofon",
          setting_group_conversation: "Konversation",
          setting_microphone_system_default: "Systemstandard",
          setting_microphone_change_hint_chrome: "Um dein Mikrofon zu ändern, klicke auf das Symbol links neben der Adressleiste und öffne Website-Einstellungen → Mikrofon, oder öffne diese Adresse in einem neuen Tab:",
          setting_microphone_change_hint_generic: "Um dein Mikrofon zu ändern, öffne die Website-Einstellungen deines Browsers (das Symbol links neben der Adressleiste) → Mikrofon, oder ändere das Standard-Eingabegerät in den Soundeinstellungen deines Systems.",
          setting_use_custom_agent: "Benutzerdefinierten Agenten verwenden",
          premium_feature: "Premium-Funktion",
          language_variant: "Sprachvariante",
          language_france: "Frankreich",
          language_quebec: "Québec",
          setting_echo_cancellation: "Echounterdrückung",
          setting_noise_suppression: "Rauschunterdrückung",
          setting_auto_gain: "Automatische Verstärkungsregelung",
          setting_audio_model: "Audiomodell",
          stt_model_default: "Standard",
          stt_model_conversaflex: "Conversaflex AI",
          setting_tts_speed: "Sprechgeschwindigkeit",
          tts_speed_050: "Sehr langsam (0.5x)",
          tts_speed_075: "Langsamer (0.75x)",
          tts_speed_085: "Etwas langsamer (0.85x)",
          tts_speed_100: "Normal (1x)",
          setting_hide_assistant_text: "Assistententext ausblenden",
          setting_preferred_pronoun: "Bevorzugtes Pronomen",
          pronoun_unspecified: "Nicht angegeben",
          pronoun_masculine: "Männlich (il)",
          pronoun_feminine: "Weiblich (elle)",
          pronoun_nonbinary: "Nicht-binär (xier)",
          sign_out: "Abmelden",
          confirm_variant_change_title: "Französische Variante ändern?",
          confirm_variant_change_body: "Das Ändern der französischen Variante im Echtzeitmodus startet ein neues Gespräch. Möchten Sie fortfahren?",
          confirm_restart: "Ja, neu starten",
          back: 'Zurück',
          close: 'Schließen',
          last: 'Letzte',
          next: 'Nächste',
          nextLabelWithProgress: 'Nächste (Schritt {step} von {steps})',
          open: 'Dialog öffnen',
          welcomeMessage: "Willkommen bei Madame AI!",
          practiceMessage: "Üben Sie täglich französische Gespräche, ohne Angst vor Beurteilung!",
          ageLabel: "Alter:",
          motherTongueLabel: "Muttersprache:",
          levelLabel: "Niveau:",
          getStarted: "Loslegen",
          beginner: "Anfänger",
          intermediate: "Mittelstufe",
          advanced: "Avancé",
          termsMessage: "Ich habe die Bedingungen und die Datenschutzrichtlinie gelesen",
          termsAgreement: "Ich habe gelesen und stimme zu den",
          startTutorial: "Tutorial starten",
          skip: "Überspringen",
          infoTooltip: "Wir speichern die Audio- und Gesprächsdaten, wenn Sie erwachsen sind, um die KI für Sie anzupassen und relevante Informationen zu speichern. Wir speichern keine Audiodaten, wenn der Lernende unter 18 Jahre alt ist.",
          adult: "Erwachsener",
          step1Content: "Bonjour! Das ist Madame AI. Sie wird Ihnen helfen, Französisch zu lernen und zu üben!",
          step2Content: "Oben sind die Optionen. Gehen Sie hierher, um eine Aktivität zu starten, ein freies Gespräch zu führen oder einige Einstellungen zu ändern. Der Echtzeitmodus ist ein flüssigeres Gespräch, empfohlen für fortgeschrittenere Lernende.",
          step3Content: "Wenn Sie bereit sind zu beginnen, klicken Sie auf \"Commencer\"",
          activities_work: "Arbeitsaktivitäten",
          activities_basic: "Alltagssituationen",
          activities_pitch: "Ihr Verkaufsgespräch",
          activity_pitch_practice: "Üben Sie Ihr Verkaufsgespräch",
          activity_travail: "Beginnen Sie eine arbeitsbezogene Diskussion",
          activity_learning_path: "Setze deinen Lernplan fort",
          activities_test_practices: "Einen Test üben",
          // Everyday-situation activities
          activity_culture_social_norms: "Kultur und soziale Normen",
          activity_greetings_introductions: "Begrüßung und Vorstellung",
          activity_numbers: "Zahlen, Datum und Uhrzeit",
          activity_people_relationships: "Menschen und Beziehungen",
          activity_food_drinks: "Essen und Trinken",
          activity_places_directions_transport: "Orte, Wegbeschreibung und Verkehr",
          activity_appointments: "Termine und Telefonate",
          activity_weather_small_talk: "Wetter und Small Talk",
          activity_questions_requests_clarification: "Fragen, Bitten und Nachfragen",
          activity_shopping_errands: "Einkaufen und Erledigungen",
          activity_colors: "Farben und Beschreibungen",
          placement_test_completed: "Sie haben die Aktivität abgeschlossen, sehen wir uns Ihre Ergebnisse an!",
          placement_test_title: "Die Zeit ist um!",
          fallback_no_audio: "Konnte nicht hören, bitte versuche es erneut.",
          browser_not_supported: "Ihr Browser unterstützt keine Audiofunktionen. Bitte versuchen Sie es mit Chrome, Safari oder Firefox.",
          no_audio_detected: "Kein Audio erkannt. Bitte versuchen Sie erneut zu sprechen.",
          audio_decode_error: "Audiodatei konnte nicht verarbeitet werden. Bitte versuchen Sie erneut aufzunehmen.",
          recording_timeout: "Zeitüberschreitung der Verbindung. Bitte versuchen Sie erneut aufzunehmen.",
          activity_start_timeout: "Wir konnten Ihre Aktivität nicht starten. Bitte prüfen Sie Ihre Verbindung und versuchen Sie es erneut.",
          microphone_not_working: "Ihr Mikrofon erreicht uns nicht. Bitte laden Sie die Seite neu und beginnen Sie erneut.",
          connection_error: "Verbindungsfehler. Bitte versuchen Sie es erneut.",
          sending_for_analysis: "Gespräch wird zur Analyse gesendet...",
          max_recording_reached: "Maximale Aufnahmezeit von 50 Sekunden erreicht. Ihr Audio wurde übermittelt.",
          recording_start_label: "Aufnahme starten",
          recording_stop_label: "Aufnahme beenden",
          recording_hint_idle: "Zum Aufnehmen tippen oder die Taste gedrückt halten, während Sie sprechen.",
          recording_hint_stop: "Tippen Sie erneut, wenn Sie fertig sind.",
          recording_hint_release: "Lassen Sie die Taste los, wenn Sie fertig sind.",
          recording_too_short: "Zu kurz zum Senden. Tippen Sie einmal, um die Aufnahme zu starten.",
          placement_test_needed_title: "Bevor du anfängst",
          placement_test_needed_body: "Damit Madame AI noch genauer wird, empfehlen wir dir, unseren Einstufungstest zu machen. Möchtest du jetzt beginnen?",
          placement_test_duration_note: "Das dauert normalerweise etwa 15 Minuten.",
          test_results_title: "Test abgeschlossen",
          test_results_body: "Deine Ergebnisse werden analysiert. Du erhältst deine detaillierten Ergebnisse per E-Mail und sie werden in Kürze in deinem Dashboard verfügbar sein.",
          test_results_ok: "Verstanden!",
          daily_limit_message: "Du hast diesen Test heute bereits abgeschlossen. Hier sind deine letzten Ergebnisse. Komm morgen wieder, um es erneut zu versuchen!",
          trial_session_limit_title: "Du hast das heutige Limit erreicht",
          trial_session_limit_message: "Du kannst 2 kostenlose Sitzungen pro Tag üben. Lernst du für die Arbeit? Kontaktiere uns und wir richten dich ein – ansonsten komm morgen wieder.",
          trial_session_limit_cta: "Kontaktiere uns",
          exam_results_pending: "Deine Prüfung wird analysiert! Du erhältst deine detaillierten Ergebnisse in Kürze per E-Mail. Aktualisiere diese Seite später, um deine Ergebnisse zu sehen.",
          conversation_feedback_title: "Wie war dein Gespräch?",
          star: "Stern",
          stars: "Sterne",
          submit: "Absenden",
          feedback_tags_label: "Was ist schiefgelaufen?",
          feedback_tag_bug: "Es gab einen Fehler",
          feedback_tag_not_understood: "Sie versteht mich nicht",
          feedback_tag_not_interesting: "Das Thema war nicht interessant",
          feedback_tag_too_hard: "Zu schwer",
          feedback_tag_too_easy: "Zu einfach",
          feedback_tags_label_positive: "Was hat dir gefallen?",
          feedback_tag_audio_issues: "Audioprobleme",
          feedback_tag_too_slow: "Zu langsam",
          feedback_tag_repetitive: "Zu repetitiv",
          feedback_tag_good_topic: "Gutes Thema",
          feedback_tag_right_level: "Richtiges Niveau",
          feedback_tag_good_feedback: "Gutes Feedback",
          feedback_tag_understood_me: "Hat mich verstanden",
          feedback_tag_learned_something: "Etwas Neues gelernt",
          feedback_tag_fun: "Unterhaltsam",
          feedback_tag_natural_conversation: "Natürliches Gespräch",
          feedback_comment_placeholder: "Weitere Kommentare? (optional)",
          feedback_required_hint: "Bitte wähle ein Tag oder füge einen Kommentar hinzu, damit wir uns verbessern können.",
          feedback_sent: "Ihre Rückmeldung wurde gesendet!",
          error_no_selection_to_save: "Kein Text zum Speichern ausgewählt.",
          error_user_not_found_for_library: "Benutzer nicht gefunden. Speichern in Bibliothek nicht möglich.",
          text_saved_to_library_success: "In Ihrer Bibliothek gespeichert!",
          error_saving_to_library: "Speichern in Bibliothek fehlgeschlagen. {{message}}",
          save_to_library_button: "In Bibliothek speichern",
          error_no_conversation_id_for_vocab: "Konversationskontext nicht gefunden. Element wird ohne Kontext gespeichert.",
          save_banner_prompt: "\"<strong>{{snippet}}</strong>\" speichern?",
          loading_learning_plan: "Laden Ihres Lernplans...",
          generating_learning_plan: "Ihr Lernplan wird Erstellt",
          planner_generation_message: "Bitte warten Sie, während wir Ihren personalisierten Lernplan erstellen. Sie können in der Zwischenzeit ein Freiformgespräch üben!",
          generating: "Wird erstellt",
          continue_learning_objective: "Setzen Sie Ihr Lernziel fort",
          custom_activities_for_you: "Benutzerdefinierte Aktivitäten für Sie",
          loading: "Wird geladen...",
          generating_activity: "Ihre Aktivität wird erstellt...",
          loading_test_results: "Testergebnisse werden geladen...",
          your_learning_goal: "Ihr Lernziel",
          edit_goal: "Ziel bearbeiten",
          your_learning_activities: "Ihre Lernaktivitäten",
          planner_activities_description: "Vervollständigen Sie diese Aktivitäten in der richtigen Reihenfolge, um Ihr Lernziel zu erreichen.",
          activity: "Aktivität",
          view_learning_plan: "Lernplan anzeigen",
          test_learning_path: "Testen Sie Ihren Lernpfad",
          learning_progress: "Lernfortschritt",
          activity_progress_label: "Aktivitätsfortschritt",
          activity_details_button: "Details",
          activity_details_vocabulary: "Wortschatz",
          activity_details_focus: "Schwerpunkt",
          activity_details_format: "Format",
          activity_details_current: "Diese Sitzung",
          activity_details_best: "Bestes Ergebnis",
          activity_details_attempts: "Versuche",
          activity_details_summary: "Bisherige Zusammenfassung",
          activity_details_acquired: "Gelernt",
          activity_details_error: "Fehler",
          conversation_highlights: "Lern-Höhepunkte",
          conversation_highlight_legend: "Legende der Transkript-Markierungen",
          conversation_legend_error: "Dein Fehler",
          conversation_legend_correction: "Korrektur",
          conversation_legend_learned: "Zum ersten Mal gelernt",
          conversation_legend_reviewed: "Wiederholt",
          conversation_summary_title: "Zusammenfassung des Gesprächs",
          conversation_top_errors: "Häufigste Fehler",
          conversation_no_errors: "Keine vorrangigen Fehler festgestellt.",
          conversation_top_concepts: "Wichtigste gelernte Konzepte",
          conversation_no_concepts: "Noch keine Konzepte festgestellt.",
          conversation_learned_marker: "Zum ersten Mal gelernt",
          conversation_reviewed_marker: "Wiederholt",
          activities: "Aktivitäten",
          start_your_first_activity: "Starten Sie Ihre erste Aktivität, um den Fortschritt zu verfolgen",
          all_activities_started: "Toller Fortschritt! Sie haben alle Aktivitäten begonnen",
          learning_plan_complete_title: "Sie haben Ihren Lernplan abgeschlossen!",
          learning_plan_complete_description: "Gut gemacht — Sie haben alle {{count}} Aktivitäten abgeschlossen. Machen Sie die Auswertung, um zu sehen, wo Sie stehen und was als Nächstes dran ist, und setzen Sie darauf Ihr neues Ziel.",
          create_new_learning_plan: "Neues Lernziel setzen",
          practice_activity_again: "Eine Aktivität wiederholen",
          learning_plan_complete_hint: "Plan abgeschlossen! Setzen Sie ein neues Ziel, um weiterzumachen",
          confirmNewLearningPlanAfterCompletion: "Sie haben diesen Plan abgeschlossen. Ein neuer Plan ersetzt ihn durch neue Aktivitäten. Fortfahren?",
          keep_practicing_activities: "Weiter so! Üben Sie weiter, um sich zu verbessern",
          no_learning_goal_message: "Richten Sie einen personalisierten Lernplan ein, um Ihren Fortschritt zu verfolgen und Ihre französischen Lernziele zu erreichen.",
          set_your_learning_goal: "Ihr Lernziel Festlegen",
          microphone_permission_required: "Mikrofonberechtigung erforderlich",
          microphone_permission_message: "Diese Anwendung benötigt Mikrofonzugriff, um im Echtzeitmodus zu funktionieren.",
          allow_microphone: "Mikrofon erlauben",
          // Header tooltips for accessibility
          tooltip_activities: "Aktivitäten",
          tooltip_planner: "Lernplan",
          tooltip_feedback: "Feedback senden",
          tooltip_more: "Mehr Optionen",
          // Modal accessibility (WCAG)
          close_modal: "Dialog schließen",
          // Skip link and main content (WCAG)
          skip_to_main: "Zum Hauptinhalt springen",
          main_content_label: "Hauptinhalt",
          // Speech Profile
          speech_profile_title: "Sprachprofil",
          speech_profile_instructions: "Lesen Sie jeden Satz laut vor, damit wir Ihr Sprachprofil kalibrieren können.",
          speech_profile_no_sentences: "Diagnosesätze sind für Ihre Sprachkombination noch nicht verfügbar. Schauen Sie später wieder vorbei.",
          speech_profile_back_to_app: "Zurück zur App",
          speech_profile_listen: "Anhören",
          speech_profile_playing: "Wird abgespielt...",
          speech_profile_record: "Aufnehmen",
          speech_profile_stop: "Stopp",
          speech_profile_analyzing: "Aussprache wird analysiert...",
          speech_profile_analysis_error: "Aussprache konnte nicht analysiert werden. Bitte versuchen Sie es erneut.",
          speech_profile_try_again: "Erneut versuchen",
          speech_profile_next: "Weiter",
          speech_profile_complete_title: "Sprachprofil aktualisiert!",
          speech_profile_complete_message: "Ihr Sprachprofil wurde aktualisiert. Die KI wird Ihre Aussprache jetzt besser verstehen.",
          speech_profile_overall_score: "Gesamtaussprachebewertung",
          speech_profile_start_practicing: "Üben beginnen",
          // Activity page & test results
          analyzing_conversation: "Ihre Ergebnisse werden analysiert...",
          analyzing_may_take_time: "Dies kann bis zu einer Minute dauern",
          results_score_overview: "Ihr Ergebnis",
          results_cefr_level: "GER-Niveau",
          results_oral_comprehension: "Hörverstehen",
          results_oral_production: "Mündliche Produktion",
          results_feedback: "Persönliches Feedback",
          results_learning_plan: "Empfohlene nächste Schritte",
          results_audio_analysis: "Audioanalyse",
          results_pronunciation: "Aussprache",
          results_vocabulary: "Wortschatzverwendung",
          results_grammar: "Grammatik & Konjugation",
          results_fluency: "Flüssigkeit",
          results_email_prompt: "Geben Sie Ihre E-Mail-Adresse ein, um Ihre detaillierten Ergebnisse zu erhalten",
          contact_cta_heading: "Bereit, das Sprachlernen Ihres Teams zu beschleunigen?",
          contact_cta_text: "Kontaktieren Sie uns, um Ihr Team einzubinden",
          activity_not_found: "Aktivität nicht gefunden",
          // Onboarding translations
          ...onboardingTranslations.de,
          objective_generating_1: "Deine Lernziele werden analysiert ...",
          objective_generating_2: "Passende Aktivitäten für dein Niveau werden ausgewählt ...",
          objective_generating_3: "Ein persönlicher Lernplan wird erstellt ...",
          objective_generating_4: "Relevanter Wortschatz für deinen Beruf wird ausgewählt ...",
          objective_generating_5: "Dein Lernpfad wird Schritt für Schritt aufgebaut ...",
          objective_generating_6: "Fast geschafft! Dein Plan wird fertiggestellt ...",
          confirmReplaceLearningPlan: "Dadurch werden dein aktueller Lernplan und dein gesamter Fortschritt ersetzt. Bist du sicher?",
          yes: "Ja",
          confirm_mode_change_title: "Gesprächsmodus wechseln?",
          confirm_mode_change_body: "Dadurch wird deine aktuelle Aktivität zurückgesetzt und ein neues Gespräch gestartet. Möchtest du fortfahren?",
          feedback_question: "Was können wir tun, um unser Produkt zu verbessern?",
          feedback_description: "Gib so viele Details wie möglich an!",
          submit_feedback: "Feedback senden",
          planner_question: "Was ist dein Lernziel?",
          planner_description: "Erkläre es in wenigen Worten! Zum Beispiel: eine Präsentation für einen Kunden halten.",
          submit_objective: "Ziel senden",
          tutorial_completed_title: "Magnifique!",
          tutorial_completed_message: "Herzlichen Glückwunsch zum Abschluss der Aktivität.\nDu erhältst in Kürze eine E-Mail mit einer Zusammenfassung.\nKehre zur Aktivitätenliste zurück, um deinen Lernweg fortzusetzen.",
          go_to_activities: "Zu den Aktivitäten",
          native_language_label: "Was ist deine Muttersprache?",
          email_capture_title: "Los geht's!",
          email_capture_message: "Gib zunächst deine geschäftliche E-Mail-Adresse ein, um fortzufahren:",
          first_name_placeholder: "Gib deinen Vornamen ein",
          email_placeholder: "Gib deine geschäftliche E-Mail-Adresse ein",
          continue_button: "Weiter",
          invalid_email: "Bitte gib eine gültige E-Mail-Adresse ein",
          email_consent_label: "Ich bin damit einverstanden, Folge-E-Mails zu diesem Dienst zu erhalten.",
          email_newsletter_label: "Newsletter abonnieren",
          email_newsletter_note: "Gelegentliche E-Mails, wenn neue Tests verfügbar sind.",
          email_work_hint: "Eine geschäftliche E-Mail-Adresse ist am besten, damit wir das Angebot auf deinen Arbeitsplatz zuschneiden können. Du kannst aber auch mit dieser Adresse fortfahren.",
          work_intent_title: "Lernst du diese Sprache für den Beruf?",
          work_intent_current_job: "Ja, für meinen aktuellen Job",
          work_intent_career: "Ja, für meine Karriere oder Jobsuche",
          work_intent_team: "Ich prüfe das für mein Team (HR / Personalentwicklung)",
          work_intent_personal: "Nein, das ist privat / für die Schule / zur Prüfungsvorbereitung",
          col_free_test: "Kostenloser Test",
          col_full_platform: "Vollständige Plattform",
          col_lite_version: "Lite-Version",
          col_full_version: "Vollversion",
          feature_lite_preview: "Sieh, wie sich Sprachenlernen weiterentwickelt",
          feature_lite_slower: "Langsamere Interaktionen",
          feature_lite_one_activity: "Nur eine dynamische Aktivität",
          feature_lite_no_domain: "Nicht auf den Arbeitsbereich zugeschnitten",
          feature_lite_no_test: "Kein Einstufungstest und keine Analyse",
          feature_lite_no_memory: "Kein Gedächtnis",
          feature_lite_no_feedback: "Keine Analyse oder Rückmeldung zu deinen Interaktionen",
          feature_activity_free_test: "Mach einen kostenlosen Einstufungstest",
          feature_activity_retake: "Tests wiederholen und Fortschritt verfolgen",
          feature_activity_personalized: "Personalisierte Lernaktivitäten",
          feature_activity_domain: "Auf deinen Arbeitsbereich zugeschnitten",
          feature_activity_memory: "KI-Gedächtnis über Gespräche hinweg",
          feature_activity_feedback: "Ausführliches Feedback und Analyse",
          email_capture_terms_prefix: "Wenn du fortfährst, stimmst du unseren",
          email_capture_terms_link: "Nutzungsbedingungen",
          email_capture_terms_and: "und",
          email_capture_privacy_link: "Datenschutzerklärung",
          plannerBeingCreated: "Dein Lernplan wird erstellt. Bitte warte einen Moment ...",
          planner_pending_objective: "Plan wird erstellt für: \"{{objective}}\"",
          plannerCreationTimeout: "Zeitüberschreitung beim Erstellen des Lernplans. Bitte versuche es erneut.",
          continue_learning_path: "Lernpfad fortsetzen",
          generating_activity_1: "Eine Aktivität nur für dich wird vorbereitet ...",
          generating_activity_2: "Etwas Besonderes für dein Niveau wird erstellt ...",
          generating_activity_3: "Deine nächste Herausforderung wird personalisiert ...",
          generating_activity_4: "Eine Aktivität rund um deine Interessen wird aufgebaut ...",
          generating_activity_5: "Eine unterhaltsame Übung wird für dich vorbereitet ...",
          generating_activity_6: "Deine persönliche Lektion wird gestaltet ...",
          generating_activity_7: "Eine einzigartige Aktivität für deine Ziele wird erstellt ...",
          generating_activity_8: "Dein nächstes Lernabenteuer wird vorbereitet ...",
        },
      },
      ar: {
        translation: {
          confirmReset: "هل أنت متأكد أنك تريد إعادة التعيين؟",
          confirmEndActivity: "نشاطك الحالي سينتهي. هل أنت متأكد؟",
          confirmEndOnboarding: "نوصي بإكمال هذه المحادثة التعريفية — لا تستغرق سوى بضع دقائق وتساعدنا في تخصيص تعلمك. هل أنت متأكد أنك تريد الإنهاء؟",
          onboarding_complete_title: "أحسنت!",
          onboarding_complete_body: "لقد أكملت محادثتك الأولى. نحن الآن نبني خطة تعلم مخصصة لك بناءً على كل ما تعلمناه عنك.",
          planner_edit_hint: "يمكنك تعديل هذا الهدف في أي وقت.",
          objective_generating: "نحن نبني خطة تعلم مخصصة لك...",
          objective_ready: "خطة التعلم الخاصة بك جاهزة!",
          objective_continue: "ابدأ التعلم",
          objective_timeout: "استغرق إنشاء الخطة وقتًا طويلاً. يمكنك إنشاء واحدة من التطبيق.",
          objective_activities_count: "{{count}} نشاط مخطط لك",
          confirmReplaceLearningPlan: "سيؤدي هذا إلى استبدال خطة التعلم الحالية وجميع التقدم. هل أنت متأكد؟",
          yes: "نعم",
          commencer: "ابدأ",
          remind_me_later: "ذكرني لاحقًا",
          continue_your_learning_plan: "تابع خطة التعلم الخاصة بك",
          start_your_learning_plan: "ابدأ خطة التعلم الخاصة بك",
          commencer_select_activity: "عرض جميع الأنشطة",
          or: "أو",
          warning_volume: "تأكد من تشغيل الصوت!",
          ai_can_make_mistakes: "مدام AI تتعلّم أيضًا! ومثل العديد من أدوات الذكاء الاصطناعي، قد ترتكب أخطاء",
          initial_text: "اضغط على الزر الأخضر وقل \"{{targetGreeting}}\"",
          end_conversation: "سيتم إنهاء المحادثة. هل أنت متأكد؟",
          end_conversation_action: "عرض خطة التعلم الخاصة بي",
          preparing_your_plan: "جارٍ تحضير خطتك…",
          time_remaining: "الوقت المتبقي",
          end_label: "إنهاء",
          activity_start_conversation: "ابدأ محادثة",
          activity_coming_soon: "قريباً",
          activity_tutorial: "دورة تعليمية",
          cancel: "إلغاء",
          new_conversation: "محادثة جديدة",
          reset_conversation: "إعادة تعيين كل شيء",
          restart_onboarding: "إعادة بدء الإعداد",
          status_disconnected: "لقد تم قطع الاتصال. حاول التحدث مرة أخرى أو قم بتحديث الصفحة",
          status_listening: "الاستماع...",
          status_speaking: "يتحدث!",
          status_prefix: "مدام AI هي",
          setting_native_language: "اللغة الأم",
          setting_target_language: "اللغة المراد تعلمها",
          setting_realtime: "محادثة في الوقت الحقيقي",
          setting_microphone: "الميكروفون",
          setting_group_conversation: "محادثة",
          setting_microphone_system_default: "الإعداد الافتراضي للنظام",
          setting_microphone_change_hint_chrome: "لتغيير الميكروفون، انقر على الأيقونة الموجودة على يسار شريط العنوان وافتح إعدادات الموقع → الميكروفون، أو افتح هذا العنوان في علامة تبويب جديدة:",
          setting_microphone_change_hint_generic: "لتغيير الميكروفون، افتح إعدادات الموقع في متصفحك (الأيقونة الموجودة على يسار شريط العنوان) → الميكروفون، أو غيّر جهاز الإدخال الافتراضي في إعدادات الصوت بنظامك.",
          setting_use_custom_agent: "استخدام الوكيل المخصص",
          premium_feature: "ميزة مميزة",
          language_variant: "لهجة اللغة",
          language_france: "فرنسا",
          language_quebec: "كيبيك",
          setting_echo_cancellation: "إلغاء الصدى",
          setting_noise_suppression: "قمع الضوضاء",
          setting_auto_gain: "التحكم التلقائي في الكسب",
          setting_audio_model: "نموذج الصوت",
          stt_model_default: "افتراضي",
          stt_model_conversaflex: "Conversaflex AI",
          setting_tts_speed: "سرعة الكلام",
          tts_speed_050: "بطيء جداً (0.5x)",
          tts_speed_075: "أبطأ (0.75x)",
          tts_speed_085: "أبطأ قليلاً (0.85x)",
          tts_speed_100: "عادي (1x)",
          setting_hide_assistant_text: "إخفاء نص المساعد",
          setting_preferred_pronoun: "الضمير المفضل",
          pronoun_unspecified: "غير محدد",
          pronoun_masculine: "مذكر (il)",
          pronoun_feminine: "مؤنث (elle)",
          pronoun_nonbinary: "غير ثنائي (iel)",
          sign_out: "تسجيل الخروج",
          confirm_variant_change_title: "تغيير لهجة الفرنسية؟",
          confirm_variant_change_body: "سيؤدي تغيير لهجة الفرنسية في الوضع الفوري إلى بدء محادثة جديدة. هل تريد المتابعة؟",
          confirm_restart: "نعم، أعد البدء",
          back: 'رجوع',
          close: 'إغلاق',
          feedback_question: "ما الذي يمكننا فعله لتحسين منتجنا؟",
          feedback_description: "قدم أكبر قدر من التفاصيل الممكنة!",
          submit_feedback: "إرسال تعليق",
          planner_question: "ما هو هدف التعلم الخاص بك؟",
          planner_description: "اشرح في كلمات قليلة! على سبيل المثال، تقديم عرض لعميل.",
          submit_objective: "إرسال الهدف",
          tutorial_completed_title: "مكتمل!",
          tutorial_completed_message: "تهانينا على الإنجاز. ستتلقى رسالة إلكترونية قريباً. يرجى العودة إلى قائمة الأنشطة لمتابعة رحلة التعلم.",
          go_to_activities: "الذهاب إلى الأنشطة",
          native_language_label: "ما هي لغتك الأم؟",
          email_capture_title: "لنبدأ!",
          email_capture_message: "أولاً، أدخل بريد العمل الإلكتروني للمتابعة:",
          first_name_placeholder: "أدخل اسمك الأول",
          email_placeholder: "أدخل بريد العمل الإلكتروني",
          continue_button: "متابعة",
          invalid_email: "يرجى إدخال عنوان بريد إلكتروني صالح",
          email_consent_label: "أوافق على تلقي متابعات عبر البريد الإلكتروني حول هذه الخدمة.",
          email_newsletter_label: "أضِفني إلى النشرة البريدية",
          email_newsletter_note: "رسائل بريد إلكتروني من حين لآخر عند توفّر اختبارات جديدة.",
          email_work_hint: "بريد العمل أفضل كي نتمكن من تكييف التجربة مع مكان عملك. لا يزال بإمكانك المتابعة بهذا البريد.",
          col_free_test: "اختبار مجاني",
          col_full_platform: "المنصة الكاملة",
          col_lite_version: "النسخة المبسطة",
          col_full_version: "النسخة الكاملة",
          feature_lite_preview: "معاينة كيف يتطور تعلم اللغات",
          feature_lite_slower: "تفاعلات أبطأ",
          feature_lite_one_activity: "نشاط ديناميكي واحد فقط",
          feature_lite_no_domain: "غير مُكيّف لمجال العمل",
          feature_lite_no_test: "بدون اختبار تحديد مستوى أو تحليل",
          feature_lite_no_memory: "بدون ذاكرة",
          feature_lite_no_feedback: "بدون تحليل أو ملاحظات من تفاعلاتك",
          feature_activity_free_test: "إجراء اختبار تحديد مستوى مجاني",
          feature_activity_retake: "إعادة الاختبارات وتتبع التقدم",
          feature_activity_personalized: "أنشطة تعلم مخصصة",
          feature_activity_domain: "مُكيّف لمجال عملك",
          feature_activity_memory: "ذاكرة ذكاء اصطناعي عبر المحادثات",
          feature_activity_feedback: "ملاحظات وتحليل مفصل",
          email_capture_terms_prefix: "بالمتابعة، فإنك توافق على",
          email_capture_terms_link: "شروط الخدمة",
          email_capture_terms_and: "و",
          email_capture_privacy_link: "سياسة الخصوصية",
          placement_test_completed: "لقد أكملت النشاط، دعونا نرى نتائجك!",
          placement_test_title: "انتهى الوقت!",
          last: 'الأخير',
          next: 'التالي',
          nextLabelWithProgress: 'التالي (الخطوة {step} من {steps})',
          open: 'افتح الحوار',
          welcomeMessage: "مرحبًا بك في Madame AI!",
          practiceMessage: "مارس المحادثات الفرنسية يوميًا دون أي خوف من الحكم عليك!",
          ageLabel: "العمر:",
          motherTongueLabel: "اللغة الأم:",
          levelLabel: "المستوى:",
          getStarted: "ابدأ",
          beginner: "مبتدئ",
          intermediate: "متوسط",
          advanced: "Avancé",
          termsMessage: "لقد قرأت الشروط وسياسة الخصوصية",
          termsAgreement: "لقد قرأت ووافقت على",
          startTutorial: "ابدأ البرنامج التعليمي",
          skip: "تخطي",
          infoTooltip: "نحن نحفظ الصوت وبيانات المحادثة إذا كنت بالغًا لتخصيص الذكاء الاصطناعي لك وحفظ المعلومات ذات الصلة. لا نحفظ الصوت عندما يكون المتعلم أقل من 18 عامًا.",
          adult: "بالغ",
          step1Content: "Bonjour! هذه هي Madame AI. ستساعدك على تعلم وممارسة الفرنسية!",
          step2Content: "في الأعلى توجد الخيارات. اذهب هنا لبدء نشاط، محادثة حرة، أو تغيير بعض الإعدادات. وضع الوقت الحقيقي هو محادثة أكثر سلاسة، موصى بها للمتعلمين الأكثر تقدمًا.",
          step3Content: "عندما تكون جاهزًا للبدء، انقر على \"Commencer\"",
          activities_work: "أنشطة العمل",
          activities_basic: "مواقف الحياة اليومية",
          activities_pitch: "عرضك البيعي",
          activity_pitch_practice: "تدرّب على عرضك البيعي",
          activity_travail: "ابدأ مناقشة متعلقة بالعمل",
          activity_learning_path: "تابع خطة التعلم الخاصة بك",
          activities_test_practices: "تدرب على اختبار",
          // Everyday-situation activities
          activity_culture_social_norms: "الثقافة والأعراف الاجتماعية",
          activity_greetings_introductions: "التحيات والتعريف بالنفس",
          activity_numbers: "الأرقام والتواريخ والأوقات",
          activity_people_relationships: "الناس والعلاقات",
          activity_food_drinks: "الطعام والشراب",
          activity_places_directions_transport: "الأماكن والاتجاهات والمواصلات",
          activity_appointments: "المواعيد والمكالمات الهاتفية",
          activity_weather_small_talk: "الطقس والحديث العابر",
          activity_questions_requests_clarification: "الأسئلة والطلبات والاستفسار",
          activity_shopping_errands: "التسوق والمهام اليومية",
          activity_colors: "الألوان والأوصاف",
          fallback_no_audio: "لم أسمع، يرجى المحاولة مرة أخرى.",
          browser_not_supported: "متصفحك لا يدعم ميزات الصوت. يرجى تجربة Chrome أو Safari أو Firefox.",
          no_audio_detected: "لم يتم اكتشاف صوت. يرجى المحاولة مرة أخرى.",
          audio_decode_error: "تعذر معالجة ملف الصوت. يرجى محاولة التسجيل مرة أخرى.",
          recording_timeout: "انتهت مهلة الاتصال. يرجى محاولة التسجيل مرة أخرى.",
          activity_start_timeout: "تعذّر بدء نشاطك. يرجى التحقق من اتصالك والمحاولة مرة أخرى.",
          microphone_not_working: "لا يصلنا صوت الميكروفون. يرجى إعادة تحميل الصفحة والبدء من جديد.",
          connection_error: "خطأ في الاتصال. يرجى المحاولة مرة أخرى.",
          sending_for_analysis: "جارٍ إرسال المحادثة للتحليل...",
          max_recording_reached: "تم الوصول إلى الحد الأقصى لوقت التسجيل وهو 50 ثانية. تم إرسال الصوت الخاص بك.",
          recording_start_label: "بدء التسجيل",
          recording_stop_label: "إيقاف التسجيل",
          recording_hint_idle: "اضغط للتسجيل، أو استمر بالضغط على الزر أثناء التحدث.",
          recording_hint_stop: "اضغط مرة أخرى عند الانتهاء.",
          recording_hint_release: "ارفع إصبعك عن الزر عند الانتهاء.",
          recording_too_short: "التسجيل قصير جدًا ولا يمكن إرساله. اضغط مرة واحدة لبدء التسجيل.",
          placement_test_needed_title: "قبل أن تبدأ",
          placement_test_needed_body: "لكي تكون Madame AI أكثر دقة، نشجعك على إجراء اختبار تحديد المستوى الخاص بنا. هل ترغب في البدء الآن؟",
          placement_test_duration_note: "يستغرق هذا عادةً حوالي 15 دقيقة.",
          test_results_title: "اكتمل الاختبار",
          test_results_body: "يتم تحليل نتائجك. ستتلقى نتائجك التفصيلية عبر البريد الإلكتروني وستكون متاحة في لوحة التحكم الخاصة بك قريبًا.",
          test_results_ok: "حسنًا!",
          daily_limit_message: "لقد أكملت هذا الاختبار اليوم بالفعل. إليك أحدث نتائجك. عُد غدًا للمحاولة مرة أخرى!",
          trial_session_limit_title: "لقد وصلت إلى حد اليوم",
          trial_session_limit_message: "يمكنك التدرّب في جلستين مجانيتين يوميًا. هل تتعلّم لأغراض العمل؟ تواصل معنا وسنساعدك على البدء، وإلا فعُد غدًا.",
          trial_session_limit_cta: "تواصل معنا",
          exam_results_pending: "يتم تحليل امتحانك! ستتلقى نتائجك التفصيلية عبر البريد الإلكتروني قريبًا. قم بتحديث هذه الصفحة لاحقًا لرؤية نتائجك.",
          conversation_feedback_title: "كيف كانت محادثتك؟",
          star: "نجمة",
          stars: "نجوم",
          submit: "إرسال",
          feedback_tags_label: "ما الخطأ الذي حدث؟",
          feedback_tag_bug: "كان هناك خطأ",
          feedback_tag_not_understood: "لا تفهمني",
          feedback_tag_not_interesting: "الموضوع لم يكن مثيرًا للاهتمام",
          feedback_tag_too_hard: "صعب جدًا",
          feedback_tag_too_easy: "سهل جدًا",
          feedback_tags_label_positive: "ما الذي أعجبك؟",
          feedback_tag_audio_issues: "مشاكل صوتية",
          feedback_tag_too_slow: "بطيء جدًا",
          feedback_tag_repetitive: "متكرر جدًا",
          feedback_tag_good_topic: "موضوع جيد",
          feedback_tag_right_level: "المستوى مناسب",
          feedback_tag_good_feedback: "تعليقات جيدة",
          feedback_tag_understood_me: "فهمتني",
          feedback_tag_learned_something: "تعلمت شيئًا جديدًا",
          feedback_tag_fun: "ممتع",
          feedback_tag_natural_conversation: "محادثة طبيعية",
          feedback_comment_placeholder: "أي تعليقات إضافية؟ (اختياري)",
          feedback_required_hint: "يرجى اختيار وسم أو إضافة تعليق حتى نتمكن من التحسين.",
          feedback_sent: "تم إرسال تعليقك!",
          error_no_selection_to_save: "لم يتم تحديد نص للحفظ.",
          error_user_not_found_for_library: "المستخدم غير موجود. لا يمكن الحفظ في المكتبة.",
          text_saved_to_library_success: "تم الحفظ في مكتبتك!",
          error_saving_to_library: "لا يمكن الحفظ في المكتبة. {{message}}",
          save_to_library_button: "حفظ في المكتبة",
          error_no_conversation_id_for_vocab: "سياق المحادثة غير موجود. سيتم حفظ العنصر بدونه.",
          save_banner_prompt: "هل تريد حفظ \"<strong>{{snippet}}</strong>\"؟",
          loading_learning_plan: "جاري تحميل خطة التعلم الخاصة بك...",
          generating_learning_plan: "جاري إنشاء خطة التعلم الخاصة بك",
          planner_generation_message: "يرجى الانتظار بينما ننشئ خطة التعلم المخصصة الخاصة بك. لا تتردد في ممارسة محادثة حرة في هذه الأثناء!",
          generating: "جاري الإنشاء",
          continue_learning_objective: "تابع هدف التعلم الخاص بك",
          custom_activities_for_you: "أنشطة مخصصة لك",
          loading: "جاري التحميل...",
          generating_activity: "جاري إنشاء نشاطك...",
          loading_test_results: "جاري تحميل نتائج الاختبار...",
          your_learning_goal: "هدف التعلم الخاص بك",
          edit_goal: "تعديل الهدف",
          your_learning_activities: "أنشطة التعلم الخاصة بك",
          planner_activities_description: "أكمل هذه الأنشطة بالترتيب لتحقيق هدف التعلم الخاص بك.",
          activity: "نشاط",
          view_learning_plan: "عرض خطة التعلم",
          test_learning_path: "اختبر مسار التعلم الخاص بك",
          learning_progress: "تقدم التعلم",
          activity_progress_label: "تقدم النشاط",
          activity_details_button: "التفاصيل",
          activity_details_vocabulary: "المفردات",
          activity_details_focus: "التركيز",
          activity_details_format: "الصيغة",
          activity_details_current: "هذه الجلسة",
          activity_details_best: "أفضل نتيجة",
          activity_details_attempts: "المحاولات",
          activity_details_summary: "الملخص حتى الآن",
          activity_details_acquired: "مكتسب",
          activity_details_error: "خطأ",
          conversation_highlights: "أبرز نقاط التعلّم",
          conversation_highlight_legend: "مفتاح تمييز النص",
          conversation_legend_error: "خطؤك",
          conversation_legend_correction: "التصحيح",
          conversation_legend_learned: "تعلّمته لأول مرة",
          conversation_legend_reviewed: "تمت مراجعته",
          conversation_summary_title: "ملخص المحادثة",
          conversation_top_errors: "أبرز الأخطاء",
          conversation_no_errors: "لم يتم تحديد أخطاء ذات أولوية.",
          conversation_top_concepts: "أبرز المفاهيم المكتسبة",
          conversation_no_concepts: "لم يتم تحديد أي مفاهيم بعد.",
          conversation_learned_marker: "تعلّمته لأول مرة",
          conversation_reviewed_marker: "تمت مراجعته",
          activities: "أنشطة",
          start_your_first_activity: "ابدأ نشاطك الأول لبدء تتبع التقدم",
          all_activities_started: "تقدم ممتاز! لقد بدأت جميع الأنشطة",
          learning_plan_complete_title: "لقد أكملت خطة التعلم الخاصة بك!",
          learning_plan_complete_description: "أحسنت — لقد أنهيت جميع الأنشطة البالغ عددها {{count}}. قم بالتقييم لمعرفة مستواك الحالي وما يجب العمل عليه بعد ذلك، ثم حدد هدفك الجديد بناءً عليه.",
          create_new_learning_plan: "تحديد هدف تعلم جديد",
          practice_activity_again: "إعادة نشاط",
          learning_plan_complete_hint: "اكتملت الخطة! حدد هدفًا جديدًا للمتابعة",
          confirmNewLearningPlanAfterCompletion: "لقد أنهيت هذه الخطة. إنشاء خطة جديدة سيستبدلها بأنشطة جديدة. هل تريد المتابعة؟",
          keep_practicing_activities: "استمر! واصل ممارسة الأنشطة للتحسن",
          no_learning_goal_message: "قم بإعداد خطة تعلم شخصية لتتبع تقدمك وتحقق أهداف تعلم الفرنسية الخاصة بك.",
          set_your_learning_goal: "حدد هدفك التعليمي",
          microphone_permission_required: "مطلوب إذن استخدام الميكروفون",
          microphone_permission_message: "يتطلب هذا التطبيق الوصول إلى الميكروفون للعمل في وضع الوقت الحقيقي.",
          allow_microphone: "السماح باستخدام الميكروفون",
          // Header tooltips for accessibility
          tooltip_activities: "أنشطة",
          tooltip_planner: "خطة التعلم",
          tooltip_feedback: "إرسال ملاحظات",
          tooltip_more: "المزيد من الخيارات",
          // Modal accessibility (WCAG)
          close_modal: "إغلاق مربع الحوار",
          // Skip link and main content (WCAG)
          skip_to_main: "الانتقال إلى المحتوى الرئيسي",
          main_content_label: "المحتوى الرئيسي",
          // Speech Profile
          speech_profile_title: "الملف الصوتي",
          speech_profile_instructions: "اقرأ كل جملة بصوت عالٍ حتى نتمكن من معايرة ملفك الصوتي.",
          speech_profile_no_sentences: "جمل التشخيص غير متوفرة بعد لمزيج اللغات الخاص بك. يرجى المحاولة لاحقاً.",
          speech_profile_back_to_app: "العودة إلى التطبيق",
          speech_profile_listen: "استمع",
          speech_profile_playing: "جارٍ التشغيل...",
          speech_profile_record: "تسجيل",
          speech_profile_stop: "إيقاف",
          speech_profile_analyzing: "جارٍ تحليل النطق...",
          speech_profile_analysis_error: "تعذر تحليل النطق. يرجى المحاولة مرة أخرى.",
          speech_profile_try_again: "إعادة المحاولة",
          speech_profile_next: "التالي",
          speech_profile_complete_title: "تم تحديث الملف الصوتي!",
          speech_profile_complete_message: "تم تحديث ملفك الصوتي. سيفهم الذكاء الاصطناعي نطقك بشكل أفضل.",
          speech_profile_overall_score: "نتيجة النطق الإجمالية",
          speech_profile_start_practicing: "ابدأ التدريب",
          // Activity page & test results
          analyzing_conversation: "جارٍ تحليل نتائجك...",
          analyzing_may_take_time: "قد يستغرق هذا دقيقة واحدة",
          results_score_overview: "نتيجتك",
          results_cefr_level: "مستوى CEFR",
          results_oral_comprehension: "الفهم الشفهي",
          results_oral_production: "الإنتاج الشفهي",
          results_feedback: "ملاحظات مخصصة",
          results_learning_plan: "الخطوات التالية الموصى بها",
          results_audio_analysis: "تحليل الصوت",
          results_pronunciation: "النطق",
          results_vocabulary: "استخدام المفردات",
          results_grammar: "القواعد والتصريف",
          results_fluency: "الطلاقة",
          results_email_prompt: "أدخل بريدك الإلكتروني لتلقي نتائجك المفصلة",
          contact_cta_heading: "هل أنت مستعد لتسريع تعلم اللغة لفريقك؟",
          contact_cta_text: "تواصل معنا لضم فريقك",
          activity_not_found: "النشاط غير موجود",
          // Onboarding translations
          ...onboardingTranslations.ar,
          objective_generating_1: "جارٍ تحليل أهدافك التعليمية...",
          objective_generating_2: "جارٍ اختيار أنشطة تناسب مستواك...",
          objective_generating_3: "جارٍ إعداد منهج مخصص لك...",
          objective_generating_4: "جارٍ اختيار المفردات المرتبطة بعملك...",
          objective_generating_5: "جارٍ تنظيم مسار تعلمك خطوة بخطوة...",
          objective_generating_6: "أوشكنا على الانتهاء! جارٍ إنهاء خطتك...",
          confirm_mode_change_title: "تغيير وضع المحادثة؟",
          confirm_mode_change_body: "سيؤدي هذا إلى إعادة ضبط النشاط الحالي وبدء محادثة جديدة. هل تريد المتابعة؟",
          work_intent_title: "هل تتعلم هذه اللغة من أجل العمل؟",
          work_intent_current_job: "نعم، من أجل عملي الحالي",
          work_intent_career: "نعم، من أجل مسيرتي المهنية أو البحث عن عمل",
          work_intent_team: "أبحث عن ذلك من أجل فريقي (الموارد البشرية / التدريب)",
          work_intent_personal: "لا، هذا لأغراض شخصية / دراسية / للتحضير لاختبار",
          plannerBeingCreated: "يتم الآن إنشاء خطة تعلمك. يرجى الانتظار قليلاً...",
          planner_pending_objective: "جارٍ إنشاء خطة لـ: \"{{objective}}\"",
          plannerCreationTimeout: "انتهت المهلة أثناء إنشاء خطة التعلم. يرجى المحاولة مرة أخرى.",
          continue_learning_path: "متابعة مسار التعلم",
          generating_activity_1: "جارٍ إعداد نشاط مخصص لك...",
          generating_activity_2: "جارٍ إنشاء شيء مميز يناسب مستواك...",
          generating_activity_3: "جارٍ تخصيص تحديك التالي...",
          generating_activity_4: "جارٍ بناء نشاط يناسب اهتماماتك...",
          generating_activity_5: "جارٍ تحضير تمرين ممتع لك...",
          generating_activity_6: "جارٍ تصميم درسك المخصص...",
          generating_activity_7: "جارٍ إنشاء نشاط فريد يناسب أهدافك...",
          generating_activity_8: "جارٍ تجهيز رحلتك التعليمية التالية...",
        },
      },
      it: {
        translation: {
          confirmReset: "Sei sicuro di voler ripristinare?",
          confirmEndActivity: "La tua attività corrente finirà. Sei sicuro?",
          confirmEndOnboarding: "Ti consigliamo di completare questa conversazione introduttiva — ci vogliono solo pochi minuti e ci aiuta a personalizzare il tuo apprendimento. Sei sicuro di voler terminare?",
          onboarding_complete_title: "Ottimo lavoro!",
          onboarding_complete_body: "Hai completato la tua prima conversazione. Stiamo creando il tuo piano di apprendimento personalizzato basato su tutto ciò che abbiamo imparato su di te.",
          planner_edit_hint: "Potrai modificare questo obiettivo in qualsiasi momento.",
          objective_generating: "Stiamo creando il tuo piano di apprendimento personalizzato...",
          objective_ready: "Il tuo piano di apprendimento è pronto!",
          objective_continue: "Inizia a imparare",
          objective_timeout: "La generazione del piano ha richiesto troppo tempo. Puoi crearne uno dall'app.",
          objective_activities_count: "{{count}} attività pianificate per te",
          commencer: "Inizia",
          remind_me_later: "Ricordamelo più tardi",
          continue_your_learning_plan: "Continua il tuo piano di apprendimento",
          start_your_learning_plan: "Inizia il tuo piano di apprendimento",
          commencer_select_activity: "Vedi tutte le attività",
          or: "o",
          warning_volume: "Assicurati che il volume sia ATTIVO!",
          ai_can_make_mistakes: "Anche Madame AI sta imparando! Come molti strumenti di IA, può commettere errori",
          initial_text: "Fai clic sul pulsante verde e dì \"{{targetGreeting}}\"",
          end_conversation: "Questo terminerà la conversazione. Sei sicuro?",
          end_conversation_action: "Vedi il mio piano di apprendimento",
          preparing_your_plan: "Preparazione del tuo piano…",
          time_remaining: "Tempo rimanente",
          end_label: "Fine",
          activity_start_conversation: "Inizia una conversazione",
          activity_coming_soon: "Prossimamente",
          activity_tutorial: "Tutorial",
          cancel: "Annulla",
          new_conversation: "Nuova conversazione",
          reset_conversation: "Ripristina tutto",
          restart_onboarding: "Riavvia l'introduzione",
          status_disconnected: "Sei stato disconnesso. Prova a parlare di nuovo o aggiorna la pagina",
          status_listening: "ascoltando...",
          status_speaking: "parlando!",
          status_prefix: "Madame AI è",
          setting_native_language: "Lingua madre",
          setting_target_language: "Lingua da imparare",
          setting_realtime: "Conversazione in tempo reale",
          setting_microphone: "Microfono",
          setting_group_conversation: "Conversazione",
          setting_microphone_system_default: "Predefinito del sistema",
          setting_microphone_change_hint_chrome: "Per cambiare il microfono, fai clic sull'icona a sinistra della barra degli indirizzi e apri Impostazioni sito → Microfono, oppure apri questo indirizzo in una nuova scheda:",
          setting_microphone_change_hint_generic: "Per cambiare il microfono, apri le impostazioni del sito del tuo browser (l'icona a sinistra della barra degli indirizzi) → Microfono, oppure cambia l'ingresso predefinito nelle impostazioni audio del tuo sistema.",
          setting_use_custom_agent: "Usa agente personalizzato",
          premium_feature: "Funzione premium",
          language_variant: "Variante del francese",
          language_france: "Francia",
          language_quebec: "Québec",
          setting_echo_cancellation: "Cancellazione dell'eco",
          setting_noise_suppression: "Soppressione del rumore",
          setting_auto_gain: "Controllo automatico del guadagno",
          setting_audio_model: "Modello audio",
          stt_model_default: "Predefinito",
          stt_model_conversaflex: "Conversaflex AI",
          setting_tts_speed: "Velocità del parlato",
          tts_speed_050: "Molto lento (0.5x)",
          tts_speed_075: "Più lento (0.75x)",
          tts_speed_085: "Leggermente più lento (0.85x)",
          tts_speed_100: "Normale (1x)",
          setting_hide_assistant_text: "Nascondi testo assistente",
          setting_preferred_pronoun: "Pronome preferito",
          pronoun_unspecified: "Non specificato",
          pronoun_masculine: "Maschile (il)",
          pronoun_feminine: "Femminile (elle)",
          pronoun_nonbinary: "Non binario (loro)",
          sign_out: "Disconnettersi",
          confirm_variant_change_title: "Cambiare la variante del francese?",
          confirm_variant_change_body: "Cambiare la variante del francese in tempo reale avvierà una nuova conversazione. Vuoi continuare?",
          confirm_restart: "Sì, riavvia",
          back: 'Indietro',
          close: 'Chiudi',
          last: 'Ultimo',
          next: 'Prossimo',
          nextLabelWithProgress: 'Prossimo (Passo {step} di {steps})',
          open: 'Apri il dialogo',
          welcomeMessage: "Benvenuto in Madame AI!",
          practiceMessage: "Pratica conversazioni in francese ogni giorno senza paura di essere giudicato!",
          ageLabel: "Età:",
          motherTongueLabel: "Lingua madre:",
          levelLabel: "Livello:",
          getStarted: "Inizia",
          beginner: "Principiante",
          intermediate: "Intermedio",
          advanced: "Avanzato",
          termsMessage: "Ho letto i termini e la politica sulla privacy",
          termsAgreement: "Ho letto e accetto i",
          startTutorial: "Inizia tutorial",
          skip: "Pular",
          infoTooltip: "Salvamos o áudio e os dados da conversa se você for adulto para personalizar a IA para você e salvar informações relevantes. Não salvamos áudio quando o aprendiz tem menos de 18 anos.",
          adult: "Adulto",
          step1Content: "Bonjour! Questa è Madame AI. Ti aiuterà a imparare e praticare il francese!",
          step2Content: "In alto ci sono le opzioni. Vai qui per iniziare un'attività, una conversazione libera o cambiare alcune impostazioni. La modalità Realtime è una conversazione più fluida, consigliata per gli studenti più avanzati.",
          step3Content: "Quando sei pronto per iniziare, fai clic su \"Commencer\"",
          activities_work: "Attività lavorative",
          activities_basic: "Situazioni quotidiane",
          activities_pitch: "Il tuo discorso di vendita",
          activity_pitch_practice: "Esercitati sul tuo discorso di vendita",
          activity_travail: "Inizia una discussione legata al lavoro",
          activity_learning_path: "Continua il tuo percorso di apprendimento",
          activities_test_practices: "Esercitati con un test",
          // Everyday-situation activities
          activity_culture_social_norms: "Cultura e norme sociali",
          activity_greetings_introductions: "Saluti e presentazioni",
          activity_numbers: "Numeri, date e orari",
          activity_people_relationships: "Persone e relazioni",
          activity_food_drinks: "Cibo e bevande",
          activity_places_directions_transport: "Luoghi, indicazioni e trasporti",
          activity_appointments: "Appuntamenti e telefonate",
          activity_weather_small_talk: "Il tempo e le chiacchiere",
          activity_questions_requests_clarification: "Domande, richieste e chiarimenti",
          activity_shopping_errands: "Spese e commissioni",
          activity_colors: "Colori e descrizioni",
          placement_test_completed: "Hai completato l'attività, vediamo i tuoi risultati!",
          placement_test_title: "Il tempo è scaduto!",
          fallback_no_audio: "Non ho sentito, per favore riprova.",
          browser_not_supported: "Il tuo browser non supporta le funzionalità audio. Prova a utilizzare Chrome, Safari o Firefox.",
          no_audio_detected: "Nessun audio rilevato. Per favore prova a parlare di nuovo.",
          audio_decode_error: "Impossibile elaborare il file audio. Per favore prova a registrare di nuovo.",
          recording_timeout: "Connessione scaduta. Per favore prova a registrare di nuovo.",
          activity_start_timeout: "Non siamo riusciti ad avviare la tua attività. Controlla la connessione e riprova.",
          microphone_not_working: "Il tuo microfono non ci arriva. Ricarica la pagina e ricomincia.",
          connection_error: "Errore di connessione. Per favore riprova.",
          sending_for_analysis: "Invio della conversazione per l'analisi...",
          max_recording_reached: "Raggiunto il tempo massimo di registrazione di 50 secondi. Il tuo audio è stato inviato.",
          recording_start_label: "Avvia registrazione",
          recording_stop_label: "Ferma registrazione",
          recording_hint_idle: "Tocca per registrare, oppure tieni premuto il pulsante mentre parli.",
          recording_hint_stop: "Tocca di nuovo quando hai finito.",
          recording_hint_release: "Rilascia il pulsante quando hai finito.",
          recording_too_short: "Troppo breve per essere inviato. Tocca una sola volta per iniziare a registrare.",
          placement_test_needed_title: "Prima di iniziare",
          placement_test_needed_body: "Affinché Madame AI sia ancora più precisa, ti invitiamo a fare il nostro test di livello. Vuoi iniziare ora?",
          placement_test_duration_note: "Di solito richiede circa 15 minuti.",
          test_results_title: "Test completato",
          test_results_body: "I tuoi risultati sono in fase di analisi. Riceverai i risultati dettagliati via email e saranno disponibili nella tua dashboard a breve.",
          test_results_ok: "Capito!",
          daily_limit_message: "Hai già completato questo test oggi. Ecco i tuoi ultimi risultati. Torna domani per riprovare!",
          trial_session_limit_title: "Hai raggiunto il limite di oggi",
          trial_session_limit_message: "Puoi esercitarti in 2 sessioni gratuite al giorno. Stai imparando per lavoro? Contattaci e ti aiuteremo a iniziare, altrimenti torna domani.",
          trial_session_limit_cta: "Contattaci",
          exam_results_pending: "Il tuo esame è in fase di analisi! Riceverai i risultati dettagliati via email a breve. Aggiorna questa pagina più tardi per vedere i tuoi risultati.",
          conversation_feedback_title: "Com'è stata la tua conversazione?",
          star: "stella",
          stars: "stelle",
          submit: "Invia",
          feedback_tags_label: "Cosa è andato storto?",
          feedback_tag_bug: "C'è stato un errore",
          feedback_tag_not_understood: "Non mi capisce",
          feedback_tag_not_interesting: "L'argomento non era interessante",
          feedback_tag_too_hard: "Troppo difficile",
          feedback_tag_too_easy: "Troppo facile",
          feedback_tags_label_positive: "Cosa ti è piaciuto?",
          feedback_tag_audio_issues: "Problemi audio",
          feedback_tag_too_slow: "Troppo lento",
          feedback_tag_repetitive: "Troppo ripetitivo",
          feedback_tag_good_topic: "Buon argomento",
          feedback_tag_right_level: "Livello giusto",
          feedback_tag_good_feedback: "Buon feedback",
          feedback_tag_understood_me: "Mi ha capito",
          feedback_tag_learned_something: "Ho imparato qualcosa",
          feedback_tag_fun: "Divertente",
          feedback_tag_natural_conversation: "Conversazione naturale",
          feedback_comment_placeholder: "Ulteriori commenti? (opzionale)",
          feedback_required_hint: "Scegli un'etichetta o aggiungi un commento così possiamo migliorare.",
          feedback_sent: "Il tuo feedback è stato inviato!",
          error_no_selection_to_save: "Nessun testo selezionato da salvare.",
          error_user_not_found_for_library: "Utente non trovato. Impossibile salvare nella libreria.",
          text_saved_to_library_success: "Salvato nella tua libreria!",
          error_saving_to_library: "Impossibile salvare nella libreria. {{message}}",
          save_to_library_button: "Salva nella libreria",
          error_no_conversation_id_for_vocab: "Contesto della conversazione non trovato. L'elemento verrà salvato senza.",
          save_banner_prompt: "Salvare \"<strong>{{snippet}}</strong>\"?",
          loading_learning_plan: "Caricamento del tuo piano di apprendimento...",
          generating_learning_plan: "Generazione del Tuo Piano di Apprendimento",
          planner_generation_message: "Attendere prego mentre creiamo il tuo piano di apprendimento personalizzato. Nel frattempo, sentiti libero di praticare con una conversazione libera!",
          generating: "Generazione",
          continue_learning_objective: "Continua il tuo obiettivo di apprendimento",
          custom_activities_for_you: "Attività personalizzate per te",
          loading: "Caricamento...",
          generating_activity: "Generazione della tua attività...",
          loading_test_results: "Caricamento risultati del test...",
          your_learning_goal: "Il Tuo Obiettivo di Apprendimento",
          edit_goal: "Modifica Obiettivo",
          your_learning_activities: "Le Tue Attività di Apprendimento",
          planner_activities_description: "Completa queste attività in ordine per raggiungere il tuo obiettivo di apprendimento.",
          activity: "Attività",
          view_learning_plan: "Visualizza Piano di Apprendimento",
          test_learning_path: "Prova il tuo percorso di apprendimento",
          learning_progress: "Progresso di Apprendimento",
          activity_progress_label: "Progresso dell'attività",
          activity_details_button: "Dettagli",
          activity_details_vocabulary: "Vocabolario",
          activity_details_focus: "Focus",
          activity_details_format: "Formato",
          activity_details_current: "Questa sessione",
          activity_details_best: "Miglior risultato",
          activity_details_attempts: "Tentativi",
          activity_details_summary: "Riepilogo finora",
          activity_details_acquired: "Acquisito",
          activity_details_error: "Errore",
          conversation_highlights: "Punti di apprendimento",
          conversation_highlight_legend: "Legenda delle evidenziazioni della trascrizione",
          conversation_legend_error: "Il tuo errore",
          conversation_legend_correction: "Correzione",
          conversation_legend_learned: "Imparato per la prima volta",
          conversation_legend_reviewed: "Ripassato",
          conversation_summary_title: "Riepilogo della conversazione",
          conversation_top_errors: "Errori principali",
          conversation_no_errors: "Nessun errore prioritario individuato.",
          conversation_top_concepts: "Principali concetti appresi",
          conversation_no_concepts: "Nessun concetto individuato per ora.",
          conversation_learned_marker: "Imparato per la prima volta",
          conversation_reviewed_marker: "Ripassato",
          activities: "attività",
          start_your_first_activity: "Inizia la tua prima attività per tracciare i progressi",
          all_activities_started: "Ottimi progressi! Hai iniziato tutte le attività",
          learning_plan_complete_title: "Hai completato il tuo piano di apprendimento!",
          learning_plan_complete_description: "Ottimo lavoro — hai completato tutte le {{count}} attività. Fai la valutazione per vedere a che punto sei e su cosa lavorare, poi imposta il nuovo obiettivo partendo da lì.",
          create_new_learning_plan: "Imposta un nuovo obiettivo",
          practice_activity_again: "Ripeti un'attività",
          learning_plan_complete_hint: "Piano completato! Imposta un nuovo obiettivo per continuare",
          confirmNewLearningPlanAfterCompletion: "Hai completato questo piano. Crearne uno nuovo lo sostituirà con nuove attività. Continuare?",
          keep_practicing_activities: "Continua così! Continua a praticare le attività per migliorare",
          no_learning_goal_message: "Configura un piano di apprendimento personalizzato per monitorare i tuoi progressi e raggiungere i tuoi obiettivi di apprendimento del francese.",
          set_your_learning_goal: "Imposta Il Tuo Obiettivo Di Apprendimento",
          microphone_permission_required: "Autorizzazione al microfono richiesta",
          microphone_permission_message: "Questa applicazione richiede l'accesso al microfono per funzionare in modalità in tempo reale.",
          allow_microphone: "Consenti microfono",
          // Header tooltips for accessibility
          tooltip_activities: "Attività",
          tooltip_planner: "Piano di Apprendimento",
          tooltip_feedback: "Invia Feedback",
          tooltip_more: "Altre Opzioni",
          // Modal accessibility (WCAG)
          close_modal: "Chiudi finestra di dialogo",
          // Skip link and main content (WCAG)
          skip_to_main: "Vai al contenuto principale",
          main_content_label: "Contenuto principale",
          // Speech Profile
          speech_profile_title: "Profilo vocale",
          speech_profile_instructions: "Leggi ogni frase ad alta voce per calibrare il tuo profilo vocale.",
          speech_profile_no_sentences: "Le frasi diagnostiche non sono ancora disponibili per la tua combinazione linguistica. Riprova più tardi.",
          speech_profile_back_to_app: "Torna all'app",
          speech_profile_listen: "Ascolta",
          speech_profile_playing: "Riproduzione...",
          speech_profile_record: "Registra",
          speech_profile_stop: "Ferma",
          speech_profile_analyzing: "Analisi della pronuncia...",
          speech_profile_analysis_error: "Impossibile analizzare la pronuncia. Riprova.",
          speech_profile_try_again: "Riprova",
          speech_profile_next: "Avanti",
          speech_profile_complete_title: "Profilo vocale aggiornato!",
          speech_profile_complete_message: "Il tuo profilo vocale è stato aggiornato. L'IA capirà meglio la tua pronuncia.",
          speech_profile_overall_score: "Punteggio di pronuncia complessivo",
          speech_profile_start_practicing: "Inizia a esercitarti",
          // Activity page & test results
          analyzing_conversation: "Analisi dei tuoi risultati...",
          analyzing_may_take_time: "Questo potrebbe richiedere fino a un minuto",
          results_score_overview: "Il tuo punteggio",
          results_cefr_level: "Livello QCER",
          results_oral_comprehension: "Comprensione orale",
          results_oral_production: "Produzione orale",
          results_feedback: "Feedback personalizzato",
          results_learning_plan: "Prossimi passi consigliati",
          results_audio_analysis: "Analisi audio",
          results_pronunciation: "Pronuncia",
          results_vocabulary: "Uso del vocabolario",
          results_grammar: "Grammatica e coniugazione",
          results_fluency: "Fluidità",
          results_email_prompt: "Inserisci la tua email per ricevere i risultati dettagliati",
          contact_cta_heading: "Pronto ad accelerare l'apprendimento linguistico del tuo team?",
          contact_cta_text: "Contattaci per integrare il tuo team",
          activity_not_found: "Attività non trovata",
          // Onboarding translations
          ...onboardingTranslations.it,
          objective_generating_1: "Analisi dei tuoi obiettivi di apprendimento...",
          objective_generating_2: "Selezione delle attività adatte al tuo livello...",
          objective_generating_3: "Creazione di un percorso personalizzato per te...",
          objective_generating_4: "Scelta del vocabolario utile per il tuo lavoro...",
          objective_generating_5: "Organizzazione del tuo percorso passo dopo passo...",
          objective_generating_6: "Ci siamo quasi! Completamento del piano...",
          confirmReplaceLearningPlan: "Questo sostituirà il tuo piano di apprendimento attuale e tutti i progressi. Sei sicuro?",
          yes: "Sì",
          confirm_mode_change_title: "Cambiare modalità di conversazione?",
          confirm_mode_change_body: "Questo azzererà l'attività in corso e avvierà una nuova conversazione. Vuoi continuare?",
          feedback_question: "Cosa possiamo fare per migliorare il nostro prodotto?",
          feedback_description: "Fornisci più dettagli possibile!",
          submit_feedback: "Invia feedback",
          planner_question: "Qual è il tuo obiettivo di apprendimento?",
          planner_description: "Spiegalo in poche parole! Ad esempio, fare una presentazione a un cliente.",
          submit_objective: "Invia obiettivo",
          tutorial_completed_title: "Magnifique!",
          tutorial_completed_message: "Complimenti per aver completato l'attività.\nRiceverai presto un'email con un riepilogo.\nTorna all'elenco delle attività per continuare il tuo percorso.",
          go_to_activities: "Vai alle attività",
          native_language_label: "Qual è la tua lingua madre?",
          email_capture_title: "Iniziamo!",
          email_capture_message: "Per prima cosa, inserisci la tua email di lavoro per continuare:",
          first_name_placeholder: "Inserisci il tuo nome",
          email_placeholder: "Inserisci la tua email di lavoro",
          continue_button: "Continua",
          invalid_email: "Inserisci un indirizzo email valido",
          email_consent_label: "Accetto di ricevere email di follow-up su questo servizio.",
          email_newsletter_label: "Iscrivimi alla newsletter",
          email_newsletter_note: "Email occasionali quando sono disponibili nuovi test.",
          email_work_hint: "Un'email di lavoro è preferibile per adattare il servizio al tuo contesto professionale. Puoi comunque continuare con questa email.",
          work_intent_title: "Stai imparando questa lingua per lavoro?",
          work_intent_current_job: "Sì, per il mio lavoro attuale",
          work_intent_career: "Sì, per la mia carriera o la ricerca di lavoro",
          work_intent_team: "Sto valutando questo servizio per il mio team (HR / Formazione)",
          work_intent_personal: "No, è per uso personale / scuola / preparazione a un esame",
          col_free_test: "Test gratuito",
          col_full_platform: "Piattaforma completa",
          col_lite_version: "Versione Lite",
          col_full_version: "Versione completa",
          feature_lite_preview: "Scopri come si sta evolvendo l'apprendimento delle lingue",
          feature_lite_slower: "Interazioni più lente",
          feature_lite_one_activity: "Una sola attività dinamica",
          feature_lite_no_domain: "Non adattato al settore professionale",
          feature_lite_no_test: "Nessun test di livello né analisi",
          feature_lite_no_memory: "Nessuna memoria",
          feature_lite_no_feedback: "Nessuna analisi o feedback sulle tue interazioni",
          feature_activity_free_test: "Fai un test di livello gratuito",
          feature_activity_retake: "Ripeti i test e monitora i progressi",
          feature_activity_personalized: "Attività di apprendimento personalizzate",
          feature_activity_domain: "Adattato al tuo settore professionale",
          feature_activity_memory: "Memoria IA tra una conversazione e l'altra",
          feature_activity_feedback: "Feedback e analisi dettagliati",
          email_capture_terms_prefix: "Continuando, accetti i nostri",
          email_capture_terms_link: "Termini di servizio",
          email_capture_terms_and: "e",
          email_capture_privacy_link: "Informativa sulla privacy",
          plannerBeingCreated: "Il tuo piano di apprendimento è in fase di creazione. Attendi un momento...",
          planner_pending_objective: "Creazione del piano per: \"{{objective}}\"",
          plannerCreationTimeout: "La creazione del piano di apprendimento è scaduta. Riprova.",
          continue_learning_path: "Continua il percorso di apprendimento",
          generating_activity_1: "Stiamo preparando un'attività su misura per te...",
          generating_activity_2: "Creiamo qualcosa di speciale per il tuo livello...",
          generating_activity_3: "Personalizziamo la tua prossima sfida...",
          generating_activity_4: "Costruiamo un'attività intorno ai tuoi interessi...",
          generating_activity_5: "Prepariamo un esercizio divertente per te...",
          generating_activity_6: "Progettiamo la tua lezione personalizzata...",
          generating_activity_7: "Creiamo un'attività unica per i tuoi obiettivi...",
          generating_activity_8: "Prepariamo la tua prossima avventura di apprendimento...",
        },
      },
      pt: {
        translation: {
          confirmReset: "Tem certeza de que deseja redefinir?",
          confirmEndActivity: "Sua atividade atual será encerrada. Tem certeza?",
          confirmEndOnboarding: "Recomendamos completar esta conversa introdutória — leva apenas alguns minutos e nos ajuda a personalizar seu aprendizado. Tem certeza de que deseja encerrar?",
          onboarding_complete_title: "Bom trabalho!",
          onboarding_complete_body: "Você completou sua primeira conversa. Estamos criando seu plano de aprendizado personalizado com base em tudo o que aprendemos sobre você.",
          planner_edit_hint: "Você poderá editar este objetivo a qualquer momento.",
          commencer: "Começar",
          remind_me_later: "Lembrar mais tarde",
          continue_your_learning_plan: "Continuar seu plano de aprendizagem",
          start_your_learning_plan: "Começar seu plano de aprendizagem",
          commencer_select_activity: "Ver todas as atividades",
          or: "ou",
          warning_volume: "Certifique-se de que o volume está LIGADO!",
          ai_can_make_mistakes: "A Madame AI também está aprendendo! Como muitas ferramentas de IA, ela pode cometer erros",
          initial_text: "Clique no botão verde e diga \"{{targetGreeting}}\"",
          end_conversation: "Isso encerrará a conversa. Tem certeza?",
          end_conversation_action: "Ver meu plano de aprendizado",
          preparing_your_plan: "Preparando seu plano…",
          time_remaining: "Tempo restante",
          end_label: "Fim",
          activity_start_conversation: "Iniciar uma conversa",
          activity_coming_soon: "Em breve",
          activity_custom: "Atividade personalizada (avançado)",
          activity_tutorial: "Tutorial",
          cancel: "Cancelar",
          new_conversation: "Nova conversa",
          reset_conversation: "Redefinir tudo",
          restart_onboarding: "Reiniciar a introdução",
          status_disconnected: "Você foi desconectado. Tente falar novamente ou atualize a página",
          status_listening: "ouvindo...",
          status_speaking: "falando!",
          status_prefix: "Madame AI é",
          setting_native_language: "Língua materna",
          setting_target_language: "Língua a aprender",
          setting_realtime: "Conversa em tempo real",
          setting_microphone: "Microfone",
          setting_group_conversation: "Conversa",
          setting_microphone_system_default: "Padrão do sistema",
          setting_microphone_change_hint_chrome: "Para mudar o microfone, clique no ícone à esquerda da barra de endereço e abra Configurações do site → Microfone, ou abra este endereço numa nova aba:",
          setting_microphone_change_hint_generic: "Para mudar o microfone, abra as configurações do site do seu navegador (o ícone à esquerda da barra de endereço) → Microfone, ou altere a entrada padrão nas configurações de som do seu sistema.",
          setting_use_custom_agent: "Usar agente personalizado",
          premium_feature: "Recurso premium",
          language_variant: "Variante do francês",
          language_france: "França",
          language_quebec: "Québec",
          setting_echo_cancellation: "Cancelamento de eco",
          setting_noise_suppression: "Supressão de ruído",
          setting_auto_gain: "Controle automático de ganho",
          setting_audio_model: "Modelo de áudio",
          stt_model_default: "Padrão",
          stt_model_conversaflex: "Conversaflex AI",
          setting_tts_speed: "Velocidade da fala",
          tts_speed_050: "Muito lento (0.5x)",
          tts_speed_075: "Mais lento (0.75x)",
          tts_speed_085: "Ligeiramente mais lento (0.85x)",
          tts_speed_100: "Normal (1x)",
          setting_hide_assistant_text: "Ocultar texto do assistente",
          setting_preferred_pronoun: "Pronome preferido",
          pronoun_unspecified: "Não especificado",
          pronoun_masculine: "Masculino (il)",
          pronoun_feminine: "Feminino (elle)",
          pronoun_nonbinary: "Não binário (elu)",
          sign_out: "Sair",
          confirm_variant_change_title: "Alterar a variante do francês?",
          confirm_variant_change_body: "Alterar a variante do francês em tempo real iniciará uma nova conversa. Deseja continuar?",
          confirm_restart: "Sim, reiniciar",
          back: 'Voltar',
          close: 'Fechar',
          last: 'Último',
          next: 'Próximo',
          nextLabelWithProgress: 'Próximo (Passo {step} de {steps})',
          open: 'Abrir o diálogo',
          welcomeMessage: "Bem-vindo ao Madame AI!",
          practiceMessage: "Pratique conversas em francês diariamente sem medo de ser julgado!",
          ageLabel: "Idade:",
          motherTongueLabel: "Língua materna:",
          levelLabel: "Nível:",
          getStarted: "Começar",
          beginner: "Iniciante",
          intermediate: "Intermediário",
          advanced: "Avancé",
          termsMessage: "Li os termos e a política de privacidade",
          startTutorial: "Iniciar tutorial",
          skip: "Pular",
          infoTooltip: "Salvamos o áudio e os dados da conversa se você for adulto para personalizar a IA para você e salvar informações relevantes. Não salvamos áudio quando o aprendiz tem menos de 18 anos.",
          adult: "Adulto",
          step1Content: "Bonjour! Esta é a Madame AI. Ela vai te ajudar a aprender e praticar francês!",
          step2Content: "No topo estão as opções. Vá aqui para iniciar uma atividade, uma conversa livre ou alterar algumas configurações. O modo Realtime é uma conversa mais suave, recomendada para alunos mais avançados.",
          step3Content: "Quando estiver pronto para começar, clique em \"Commencer\"",
          activities_work: "Atividades de trabalho",
          activities_basic: "Situações do dia a dia",
          activities_pitch: "Seu discurso de vendas",
          activity_pitch_practice: "Pratique seu discurso de vendas",
          activity_travail: "Iniciar uma discussão relacionada ao trabalho",
          activity_learning_path: "Continue seu plano de aprendizagem",
          activities_test_practices: "Praticar um teste",
          // Everyday-situation activities
          activity_culture_social_norms: "Cultura e normas sociais",
          activity_greetings_introductions: "Saudações e apresentações",
          activity_numbers: "Números, datas e horas",
          activity_people_relationships: "Pessoas e relacionamentos",
          activity_food_drinks: "Comida e bebida",
          activity_places_directions_transport: "Lugares, direções e transportes",
          activity_appointments: "Compromissos e ligações telefônicas",
          activity_weather_small_talk: "O tempo e conversa casual",
          activity_questions_requests_clarification: "Perguntas, pedidos e esclarecimentos",
          activity_shopping_errands: "Compras e tarefas do dia a dia",
          activity_colors: "Cores e descrições",
          fallback_no_audio: "Não consegui ouvir, tente novamente.",
          browser_not_supported: "Seu navegador não suporta recursos de áudio. Por favor, tente usar Chrome, Safari ou Firefox.",
          no_audio_detected: "Nenhum áudio detectado. Por favor, tente falar novamente.",
          audio_decode_error: "Não foi possível processar o arquivo de áudio. Por favor, tente gravar novamente.",
          recording_timeout: "A conexão expirou. Por favor, tente gravar novamente.",
          activity_start_timeout: "Não conseguimos iniciar sua atividade. Verifique sua conexão e tente novamente.",
          microphone_not_working: "Seu microfone não está chegando até nós. Recarregue a página e comece de novo.",
          connection_error: "Erro de conexão. Por favor, tente novamente.",
          sending_for_analysis: "Enviando conversa para análise...",
          max_recording_reached: "Tempo máximo de gravação de 50 segundos atingido. Seu áudio foi enviado.",
          recording_start_label: "Iniciar gravação",
          recording_stop_label: "Parar gravação",
          recording_hint_idle: "Toque para gravar ou mantenha o botão pressionado enquanto fala.",
          recording_hint_stop: "Toque novamente quando terminar.",
          recording_hint_release: "Solte o botão quando terminar.",
          recording_too_short: "Curto demais para enviar. Toque uma vez para começar a gravar.",
          placement_test_needed_title: "Antes de você começar",
          placement_test_needed_body: "Para que Madame AI seja ainda mais precisa, encorajamos você a fazer nosso teste de nivelamento. Gostaria de começar agora?",
          placement_test_duration_note: "Isso geralmente leva cerca de 15 minutos.",
          test_results_title: "Teste concluído",
          test_results_body: "Seus resultados estão sendo analisados. Você receberá seus resultados detalhados por e-mail e eles estarão disponíveis em seu painel em breve.",
          test_results_ok: "Entendi!",
          daily_limit_message: "Você já completou este teste hoje. Aqui estão seus últimos resultados. Volte amanhã para tentar novamente!",
          trial_session_limit_title: "Você atingiu o limite de hoje",
          trial_session_limit_message: "Você pode praticar 2 sessões gratuitas por dia. Está aprendendo para o trabalho? Fale conosco e vamos ajudá-lo; caso contrário, volte amanhã.",
          trial_session_limit_cta: "Fale conosco",
          exam_results_pending: "Seu exame está sendo analisado! Você receberá seus resultados detalhados por e-mail em breve. Atualize esta página mais tarde para ver seus resultados.",
          conversation_feedback_title: "Como foi sua conversa?",
          star: "estrela",
          stars: "estrelas",
          submit: "Enviar",
          feedback_tags_label: "O que deu errado?",
          feedback_tag_bug: "Houve um erro",
          feedback_tag_not_understood: "Ela não me entende",
          feedback_tag_not_interesting: "O tema não foi interessante",
          feedback_tag_too_hard: "Muito difícil",
          feedback_tag_too_easy: "Muito fácil",
          feedback_tags_label_positive: "O que você gostou?",
          feedback_tag_audio_issues: "Problemas de áudio",
          feedback_tag_too_slow: "Muito lento",
          feedback_tag_repetitive: "Muito repetitivo",
          feedback_tag_good_topic: "Bom tema",
          feedback_tag_right_level: "Nível certo",
          feedback_tag_good_feedback: "Bom feedback",
          feedback_tag_understood_me: "Me entendeu",
          feedback_tag_learned_something: "Aprendi algo novo",
          feedback_tag_fun: "Divertido",
          feedback_tag_natural_conversation: "Conversa natural",
          feedback_comment_placeholder: "Algum comentário adicional? (opcional)",
          feedback_required_hint: "Escolha uma etiqueta ou adicione um comentário para podermos melhorar.",
          feedback_sent: "Seu feedback foi enviado!",
          error_no_selection_to_save: "Nenhum texto selecionado para salvar.",
          error_user_not_found_for_library: "Usuário não encontrado. Não é possível salvar na biblioteca.",
          text_saved_to_library_success: "Salvo na sua biblioteca!",
          error_saving_to_library: "Não foi possível salvar na biblioteca. {{message}}",
          save_to_library_button: "Salvar na Biblioteca",
          error_no_conversation_id_for_vocab: "Contexto da conversa não encontrado. O item será salvo sem ele.",
          save_banner_prompt: "Salvar \"<strong>{{snippet}}</strong>\"?",
          loading_learning_plan: "Carregando seu plano de aprendizagem...",
          generating_learning_plan: "Gerando Seu Plano de Aprendizagem",
          objective_generating: "Estamos criando seu plano de aprendizagem personalizado...",
          objective_ready: "Seu plano de aprendizagem está pronto!",
          objective_continue: "Começar a aprender",
          objective_timeout: "A geração do plano demorou muito. Você pode criar um no aplicativo.",
          objective_activities_count: "{{count}} atividades planejadas para você",
          planner_generation_message: "Por favor aguarde enquanto criamos seu plano de aprendizagem personalizado. Sinta-se à vontade para praticar com uma conversa livre enquanto isso!",
          generating: "Gerando",
          continue_learning_objective: "Continue seu objetivo de aprendizagem",
          custom_activities_for_you: "Atividades personalizadas para você",
          loading: "Carregando...",
          generating_activity: "Gerando sua atividade...",
          loading_test_results: "Carregando resultados do teste...",
          your_learning_goal: "Seu Objetivo de Aprendizagem",
          edit_goal: "Editar Objetivo",
          your_learning_activities: "Suas Atividades de Aprendizagem",
          planner_activities_description: "Complete essas atividades em ordem para alcançar seu objetivo de aprendizagem.",
          activity: "Atividade",
          view_learning_plan: "Ver Plano de Aprendizagem",
          test_learning_path: "Teste seu caminho de aprendizagem",
          learning_progress: "Progresso de Aprendizagem",
          activity_progress_label: "Progresso da atividade",
          activity_details_button: "Detalhes",
          activity_details_vocabulary: "Vocabulário",
          activity_details_focus: "Foco",
          activity_details_format: "Formato",
          activity_details_current: "Esta sessão",
          activity_details_best: "Melhor resultado",
          activity_details_attempts: "Tentativas",
          activity_details_summary: "Resumo até agora",
          activity_details_acquired: "Adquirido",
          activity_details_error: "Erro",
          conversation_highlights: "Destaques de aprendizagem",
          conversation_highlight_legend: "Legenda dos destaques da transcrição",
          conversation_legend_error: "O seu erro",
          conversation_legend_correction: "Correção",
          conversation_legend_learned: "Aprendido pela primeira vez",
          conversation_legend_reviewed: "Revisto",
          conversation_summary_title: "Resumo da conversa",
          conversation_top_errors: "Principais erros",
          conversation_no_errors: "Nenhum erro prioritário identificado.",
          conversation_top_concepts: "Principais conceitos aprendidos",
          conversation_no_concepts: "Ainda não foram identificados conceitos.",
          conversation_learned_marker: "Aprendido pela primeira vez",
          conversation_reviewed_marker: "Revisto",
          activities: "atividades",
          start_your_first_activity: "Comece sua primeira atividade para acompanhar o progresso",
          all_activities_started: "Ótimo progresso! Você começou todas as atividades",
          learning_plan_complete_title: "Você concluiu seu plano de aprendizagem!",
          learning_plan_complete_description: "Muito bem — você concluiu todas as {{count}} atividades. Faça a avaliação para ver onde está e o que praticar em seguida, e defina seu novo objetivo a partir dela.",
          create_new_learning_plan: "Definir um novo objetivo",
          practice_activity_again: "Repetir uma atividade",
          learning_plan_complete_hint: "Plano concluído! Defina um novo objetivo para continuar",
          confirmNewLearningPlanAfterCompletion: "Você concluiu este plano. Criar um novo o substituirá por novas atividades. Continuar?",
          keep_practicing_activities: "Continue assim! Continue praticando atividades para melhorar",
          no_learning_goal_message: "Configure um plano de aprendizagem personalizado para acompanhar seu progresso e alcançar seus objetivos de aprendizagem de francês.",
          set_your_learning_goal: "Definir Seu Objetivo de Aprendizagem",
          microphone_permission_required: "Permissão de microfone necessária",
          microphone_permission_message: "Este aplicativo requer acesso ao microfone para funcionar no modo em tempo real.",
          allow_microphone: "Permitir microfone",
          // Header tooltips for accessibility
          tooltip_activities: "Atividades",
          tooltip_planner: "Plano de Aprendizagem",
          tooltip_feedback: "Enviar Feedback",
          tooltip_more: "Mais Opções",
          // Modal accessibility (WCAG)
          close_modal: "Fechar caixa de diálogo",
          // Skip link and main content (WCAG)
          skip_to_main: "Pular para o conteúdo principal",
          main_content_label: "Conteúdo principal",
          // Speech Profile
          speech_profile_title: "Perfil de voz",
          speech_profile_instructions: "Leia cada frase em voz alta para calibrar seu perfil de voz.",
          speech_profile_no_sentences: "As frases de diagnóstico ainda não estão disponíveis para sua combinação de idiomas. Volte mais tarde.",
          speech_profile_back_to_app: "Voltar ao aplicativo",
          speech_profile_listen: "Ouvir",
          speech_profile_playing: "Reproduzindo...",
          speech_profile_record: "Gravar",
          speech_profile_stop: "Parar",
          speech_profile_analyzing: "Analisando pronúncia...",
          speech_profile_analysis_error: "Não foi possível analisar a pronúncia. Tente novamente.",
          speech_profile_try_again: "Tentar novamente",
          speech_profile_next: "Próximo",
          speech_profile_complete_title: "Perfil de voz atualizado!",
          speech_profile_complete_message: "Seu perfil de voz foi atualizado. A IA entenderá melhor sua pronúncia.",
          speech_profile_overall_score: "Pontuação geral de pronúncia",
          speech_profile_start_practicing: "Começar a praticar",
          // Activity page & test results
          analyzing_conversation: "Analisando seus resultados...",
          analyzing_may_take_time: "Isso pode levar até um minuto",
          results_score_overview: "Sua pontuação",
          results_cefr_level: "Nível QECR",
          results_oral_comprehension: "Compreensão oral",
          results_oral_production: "Produção oral",
          results_feedback: "Feedback personalizado",
          results_learning_plan: "Próximos passos recomendados",
          results_audio_analysis: "Análise de áudio",
          results_pronunciation: "Pronúncia",
          results_vocabulary: "Uso de vocabulário",
          results_grammar: "Gramática e conjugação",
          results_fluency: "Fluência",
          results_email_prompt: "Digite seu e-mail para receber seus resultados detalhados",
          contact_cta_heading: "Pronto para acelerar o aprendizado de idiomas da sua equipe?",
          contact_cta_text: "Entre em contato para integrar sua equipe",
          activity_not_found: "Atividade não encontrada",
          // Onboarding translations
          ...onboardingTranslations.pt,
          objective_generating_1: "Analisando seus objetivos de aprendizagem...",
          objective_generating_2: "Selecionando atividades adaptadas ao seu nível...",
          objective_generating_3: "Montando um currículo personalizado para você...",
          objective_generating_4: "Escolhendo o vocabulário relevante para o seu trabalho...",
          objective_generating_5: "Organizando seu plano de aprendizagem passo a passo...",
          objective_generating_6: "Quase lá! Finalizando seu plano...",
          confirmReplaceLearningPlan: "Isto substituirá seu plano de aprendizagem atual e todo o progresso. Tem certeza?",
          yes: "Sim",
          confirm_mode_change_title: "Mudar o modo de conversa?",
          confirm_mode_change_body: "Isto reiniciará sua atividade atual e começará uma nova conversa. Deseja continuar?",
          feedback_question: "O que podemos fazer para melhorar nosso produto?",
          feedback_description: "Dê o máximo de detalhes possível!",
          submit_feedback: "Enviar comentário",
          planner_question: "Qual é o seu objetivo de aprendizagem?",
          planner_description: "Explique em poucas palavras! Por exemplo, fazer uma apresentação para um cliente.",
          submit_objective: "Enviar objetivo",
          termsAgreement: "Li e concordo com os",
          tutorial_completed_title: "Magnifique!",
          tutorial_completed_message: "Parabéns por concluir a atividade.\nVocê receberá em breve um e-mail com um resumo.\nVolte à lista de atividades para continuar sua jornada de aprendizagem.",
          go_to_activities: "Ir para as atividades",
          native_language_label: "Qual é a sua língua materna?",
          email_capture_title: "Vamos começar!",
          email_capture_message: "Primeiro, informe seu e-mail profissional para continuar:",
          first_name_placeholder: "Digite seu primeiro nome",
          email_placeholder: "Digite seu e-mail profissional",
          continue_button: "Continuar",
          invalid_email: "Digite um endereço de e-mail válido",
          email_consent_label: "Concordo em receber e-mails de acompanhamento sobre este serviço.",
          email_newsletter_label: "Quero receber a newsletter",
          email_newsletter_note: "E-mails ocasionais quando novos testes estiverem disponíveis.",
          email_work_hint: "Um e-mail profissional é melhor para adaptarmos isto ao seu ambiente de trabalho. Você também pode continuar com este e-mail.",
          work_intent_title: "Você está aprendendo este idioma para o trabalho?",
          work_intent_current_job: "Sim, para o meu trabalho atual",
          work_intent_career: "Sim, para minha carreira ou busca de emprego",
          work_intent_team: "Estou avaliando isto para minha equipe (RH / T&D)",
          work_intent_personal: "Não, é pessoal / escola / preparação para exame",
          col_free_test: "Teste gratuito",
          col_full_platform: "Plataforma completa",
          col_lite_version: "Versão Lite",
          col_full_version: "Versão completa",
          feature_lite_preview: "Veja como o aprendizado de idiomas está evoluindo",
          feature_lite_slower: "Interações mais lentas",
          feature_lite_one_activity: "Apenas uma atividade dinâmica",
          feature_lite_no_domain: "Não adaptado à área de trabalho",
          feature_lite_no_test: "Sem teste de nivelamento ou análise",
          feature_lite_no_memory: "Sem memória",
          feature_lite_no_feedback: "Sem análise ou feedback das suas interações",
          feature_activity_free_test: "Faça um teste de nivelamento gratuito",
          feature_activity_retake: "Refaça testes e acompanhe seu progresso",
          feature_activity_personalized: "Atividades de aprendizagem personalizadas",
          feature_activity_domain: "Adaptado à sua área de trabalho",
          feature_activity_memory: "Memória de IA entre as conversas",
          feature_activity_feedback: "Feedback e análise detalhados",
          email_capture_terms_prefix: "Ao continuar, você concorda com nossos",
          email_capture_terms_link: "Termos de Serviço",
          email_capture_terms_and: "e",
          email_capture_privacy_link: "Política de Privacidade",
          placement_test_completed: "Você concluiu a atividade, vamos ver seus resultados!",
          placement_test_title: "O tempo acabou!",
          plannerBeingCreated: "Seu plano de aprendizagem está sendo criado. Aguarde um momento...",
          planner_pending_objective: "Criando plano para: \"{{objective}}\"",
          plannerCreationTimeout: "O tempo para criar o plano de aprendizagem esgotou. Tente novamente.",
          continue_learning_path: "Continuar plano de aprendizagem",
          generating_activity_1: "Preparando uma atividade só para você...",
          generating_activity_2: "Criando algo especial para o seu nível...",
          generating_activity_3: "Personalizando seu próximo desafio...",
          generating_activity_4: "Montando uma atividade com base nos seus interesses...",
          generating_activity_5: "Preparando um exercício divertido para você...",
          generating_activity_6: "Desenhando sua aula personalizada...",
          generating_activity_7: "Criando uma atividade única para seus objetivos...",
          generating_activity_8: "Deixando sua próxima aventura de aprendizagem pronta...",
        },
      },
      vi: {
        translation: {
          confirmReset: "Bạn có chắc chắn muốn đặt lại không?",
          confirmEndActivity: "Hoạt động hiện tại của bạn sẽ kết thúc. Bạn có chắc không?",
          confirmEndOnboarding: "Chúng tôi khuyên bạn nên hoàn thành cuộc trò chuyện giới thiệu này — chỉ mất vài phút và giúp chúng tôi cá nhân hóa việc học của bạn. Bạn có chắc muốn kết thúc không?",
          onboarding_complete_title: "Làm tốt lắm!",
          onboarding_complete_body: "Bạn đã hoàn thành cuộc trò chuyện đầu tiên. Chúng tôi đang xây dựng kế hoạch học tập cá nhân hóa dựa trên mọi thứ chúng tôi đã tìm hiểu về bạn.",
          planner_edit_hint: "Bạn có thể chỉnh sửa mục tiêu này bất cứ lúc nào.",
          commencer: "Bắt đầu",
          remind_me_later: "Nhắc tôi sau",
          continue_your_learning_plan: "Tiếp tục kế hoạch học tập của bạn",
          start_your_learning_plan: "Bắt đầu kế hoạch học tập của bạn",
          commencer_select_activity: "Xem tất cả hoạt động",
          or: "hoặc",
          warning_volume: "Hãy chắc chắn rằng âm lượng của bạn ĐANG BẬT!",
          ai_can_make_mistakes: "Madame AI cũng đang học hỏi! Giống như nhiều công cụ AI khác, cô ấy có thể mắc lỗi",
          initial_text: "Nhấn nút xanh và nói \"{{targetGreeting}}\"",
          end_conversation: "Điều này sẽ kết thúc cuộc trò chuyện. Bạn có chắc không?",
          end_conversation_action: "Xem kế hoạch học tập của tôi",
          preparing_your_plan: "Đang chuẩn bị kế hoạch của bạn…",
          time_remaining: "Thời gian còn lại",
          end_label: "Kết thúc",
          activity_start_conversation: "Bắt đầu một cuộc trò chuyện",
          activity_coming_soon: "Sắp có",
          activity_custom: "Hoạt động tùy chỉnh (nâng cao)",
          activity_tutorial: "Hướng dẫn",
          cancel: "Hủy",
          new_conversation: "Cuộc trò chuyện mới",
          reset_conversation: "Đặt lại mọi thứ",
          restart_onboarding: "Khởi động lại hướng dẫn",
          status_disconnected: "Bạn đã bị ngắt kết nối. Hãy thử nói lại hoặc làm mới trang",
          status_listening: "đang nghe...",
          status_speaking: "đang nói!",
          status_prefix: "Madame AI là",
          setting_native_language: "Tiếng mẹ đẻ",
          setting_target_language: "Ngôn ngữ muốn học",
          setting_realtime: "Cuộc trò chuyện thời gian thực",
          setting_microphone: "Micrô",
          setting_group_conversation: "Hội thoại",
          setting_microphone_system_default: "Mặc định của hệ thống",
          setting_microphone_change_hint_chrome: "Để thay đổi micrô, hãy nhấp vào biểu tượng ở bên trái thanh địa chỉ và mở Cài đặt trang web → Micrô, hoặc mở địa chỉ này trong một tab mới:",
          setting_microphone_change_hint_generic: "Để thay đổi micrô, hãy mở cài đặt trang web của trình duyệt (biểu tượng ở bên trái thanh địa chỉ) → Micrô, hoặc thay đổi thiết bị đầu vào mặc định trong cài đặt âm thanh của hệ thống.",
          setting_use_custom_agent: "Sử dụng đại lý tùy chỉnh",
          premium_feature: "Tính năng cao cấp",
          language_variant: "Biến thể tiếng Pháp",
          language_france: "Pháp",
          language_quebec: "Québec",
          setting_echo_cancellation: "Hủy tiếng vang",
          setting_noise_suppression: "Giảm tiếng ồn",
          setting_auto_gain: "Điều khiển tăng ích tự động",
          setting_audio_model: "Mô hình âm thanh",
          stt_model_default: "Mặc định",
          stt_model_conversaflex: "Conversaflex AI",
          setting_tts_speed: "Tốc độ nói",
          tts_speed_050: "Rất chậm (0.5x)",
          tts_speed_075: "Chậm hơn (0.75x)",
          tts_speed_085: "Hơi chậm hơn (0.85x)",
          tts_speed_100: "Bình thường (1x)",
          setting_hide_assistant_text: "Ẩn văn bản trợ lý",
          setting_preferred_pronoun: "Đại từ ưa thích",
          pronoun_unspecified: "Chưa xác định",
          pronoun_masculine: "Giống đực (il)",
          pronoun_feminine: "Giống cái (elle)",
          pronoun_nonbinary: "Phi nhị phân (họ)",
          sign_out: "Đăng xuất",
          confirm_variant_change_title: "Thay đổi biến thể tiếng Pháp?",
          confirm_variant_change_body: "Thay đổi biến thể tiếng Pháp trong thời gian thực sẽ bắt đầu một cuộc trò chuyện mới. Bạn có muốn tiếp tục không?",
          confirm_restart: "Đồng ý, khởi động lại",
          back: 'Quay lại',
          close: 'Đóng',
          last: 'Cuối cùng',
          next: 'Tiếp theo',
          nextLabelWithProgress: 'Tiếp theo (Bước {step} của {steps})',
          open: 'Mở hộp thoại',
          welcomeMessage: "Chào mừng đến với Madame AI!",
          practiceMessage: "Luyện tập hội thoại tiếng Pháp hàng ngày mà không sợ bị đánh giá!",
          ageLabel: "Tuổi:",
          motherTongueLabel: "Tiếng mẹ đẻ:",
          levelLabel: "Cấp độ:",
          getStarted: "Bắt đầu",
          beginner: "Người mới bắt đầu",
          intermediate: "Trung cấp",
          advanced: "Avancé",
          termsMessage: "Tôi đã đọc các điều khoản và chính sách bảo mật",
          startTutorial: "Bắt đầu hướng dẫn",
          skip: "Bỏ qua",
          infoTooltip: "Chúng tôi lưu âm thanh và dữ liệu hội thoại nếu bạn là người lớn để tùy chỉnh AI cho bạn và lưu thông tin liên quan. Chúng tôi không lưu âm thanh khi người học dưới 18 tuổi.",
          adult: "Người lớn",
          step1Content: "Bonjour! Đây là Madame AI. Cô ấy sẽ giúp bạn học và thực hành tiếng Pháp!",
          step2Content: "Ở trên cùng là các tùy chọn. Đi đến đây để bắt đầu một hoạt động, một cuộc trò chuyện tự do, hoặc thay đổi một số cài đặt. Chế độ Realtime là một cuộc trò chuyện mượt mà hơn, được khuyến nghị cho những người học nâng cao hơn.",
          step3Content: "Khi bạn đã sẵn sàng để bắt đầu, nhấp vào \"Commencer\"",
          activities_work: "Hoạt động công việc",
          activities_basic: "Tình huống hằng ngày",
          activities_pitch: "Bài chào hàng của bạn",
          activity_pitch_practice: "Luyện bài chào hàng của bạn",
          activity_travail: "Bắt đầu một cuộc thảo luận liên quan đến công việc",
          activity_learning_path: "Tiếp tục lộ trình học của bạn",
          activities_test_practices: "Luyện tập một bài kiểm tra",
          // Everyday-situation activities
          activity_culture_social_norms: "Văn hóa và chuẩn mực xã hội",
          activity_greetings_introductions: "Chào hỏi và giới thiệu",
          activity_numbers: "Số, ngày và giờ",
          activity_people_relationships: "Con người và các mối quan hệ",
          activity_food_drinks: "Đồ ăn và thức uống",
          activity_places_directions_transport: "Địa điểm, chỉ đường và di chuyển",
          activity_appointments: "Hẹn gặp và gọi điện thoại",
          activity_weather_small_talk: "Thời tiết và chuyện trò",
          activity_questions_requests_clarification: "Câu hỏi, yêu cầu và làm rõ",
          activity_shopping_errands: "Mua sắm và việc hằng ngày",
          activity_colors: "Màu sắc và miêu tả",
          fallback_no_audio: "Không nghe được, vui lòng thử lại.",
          browser_not_supported: "Trình duyệt của bạn không hỗ trợ tính năng âm thanh. Vui lòng thử sử dụng Chrome, Safari hoặc Firefox.",
          no_audio_detected: "Không phát hiện âm thanh. Vui lòng thử nói lại.",
          audio_decode_error: "Không thể xử lý tệp âm thanh. Vui lòng thử ghi lại.",
          recording_timeout: "Kết nối đã hết thời gian chờ. Vui lòng thử ghi lại.",
          activity_start_timeout: "Chúng tôi không thể bắt đầu hoạt động của bạn. Vui lòng kiểm tra kết nối và thử lại.",
          microphone_not_working: "Chúng tôi không nhận được micrô của bạn. Vui lòng tải lại trang và bắt đầu lại.",
          connection_error: "Lỗi kết nối. Vui lòng thử lại.",
          sending_for_analysis: "Đang gửi cuộc trò chuyện để phân tích...",
          max_recording_reached: "Đã đạt thời gian ghi âm tối đa 50 giây. Âm thanh của bạn đã được gửi.",
          recording_start_label: "Bắt đầu ghi âm",
          recording_stop_label: "Dừng ghi âm",
          recording_hint_idle: "Nhấn để ghi âm, hoặc giữ nút trong khi bạn nói.",
          recording_hint_stop: "Nhấn lại khi bạn nói xong.",
          recording_hint_release: "Thả nút ra khi bạn nói xong.",
          recording_too_short: "Quá ngắn để gửi. Nhấn một lần để bắt đầu ghi âm.",
          placement_test_needed_title: "Trước khi bạn bắt đầu",
          placement_test_needed_body: "Để Madame AI có thể chính xác hơn, chúng tôi khuyến khích bạn làm bài kiểm tra trình độ của chúng tôi. Bạn có muốn bắt đầu ngay bây giờ không?",
          placement_test_duration_note: "Việc này thường mất khoảng 15 phút.",
          test_results_title: "Bài kiểm tra hoàn thành",
          test_results_body: "Kết quả của bạn đang được phân tích. Bạn sẽ nhận được kết quả chi tiết qua email và chúng sẽ sớm có sẵn trên bảng điều khiển của bạn.",
          test_results_ok: "Đã hiểu!",
          daily_limit_message: "Bạn đã hoàn thành bài kiểm tra này hôm nay rồi. Đây là kết quả mới nhất của bạn. Hãy quay lại vào ngày mai để thử lại!",
          trial_session_limit_title: "Bạn đã đạt giới hạn hôm nay",
          trial_session_limit_message: "Bạn có thể luyện tập 2 buổi miễn phí mỗi ngày. Học vì công việc? Hãy liên hệ với chúng tôi để được hỗ trợ; nếu không, hãy quay lại vào ngày mai.",
          trial_session_limit_cta: "Liên hệ với chúng tôi",
          exam_results_pending: "Bài thi của bạn đang được phân tích! Bạn sẽ nhận được kết quả chi tiết qua email trong thời gian ngắn. Hãy làm mới trang này sau để xem kết quả của bạn.",
          conversation_feedback_title: "Cuộc trò chuyện của bạn thế nào?",
          star: "sao",
          stars: "sao",
          submit: "Gửi",
          feedback_tags_label: "Điều gì đã sai?",
          feedback_tag_bug: "Có lỗi xảy ra",
          feedback_tag_not_understood: "Cô ấy không hiểu tôi",
          feedback_tag_not_interesting: "Chủ đề không thú vị",
          feedback_tag_too_hard: "Quá khó",
          feedback_tag_too_easy: "Quá dễ",
          feedback_tags_label_positive: "Bạn thích điều gì?",
          feedback_tag_audio_issues: "Vấn đề âm thanh",
          feedback_tag_too_slow: "Quá chậm",
          feedback_tag_repetitive: "Quá lặp lại",
          feedback_tag_good_topic: "Chủ đề hay",
          feedback_tag_right_level: "Độ khó phù hợp",
          feedback_tag_good_feedback: "Phản hồi tốt",
          feedback_tag_understood_me: "Hiểu tôi",
          feedback_tag_learned_something: "Học được điều mới",
          feedback_tag_fun: "Thú vị",
          feedback_tag_natural_conversation: "Hội thoại tự nhiên",
          feedback_comment_placeholder: "Bình luận thêm? (tùy chọn)",
          feedback_required_hint: "Vui lòng chọn một thẻ hoặc thêm bình luận để chúng tôi cải thiện.",
          feedback_sent: "Phản hồi của bạn đã được gửi!",
          error_no_selection_to_save: "Không có văn bản nào được chọn để lưu.",
          error_user_not_found_for_library: "Không tìm thấy người dùng. Không thể lưu vào thư viện.",
          text_saved_to_library_success: "Đã lưu vào thư viện của bạn!",
          error_saving_to_library: "Không thể lưu vào thư viện. {{message}}",
          save_to_library_button: "Lưu vào thư viện",
          error_no_conversation_id_for_vocab: "Không tìm thấy ngữ cảnh cuộc trò chuyện. Mục sẽ được lưu mà không có nó.",
          save_banner_prompt: "Lưu \"<strong>{{snippet}}</strong>\"?",
          loading_learning_plan: "Đang tải kế hoạch học của bạn...",
          generating_learning_plan: "Đang Tạo Kế Hoạch Học Tập Của Bạn",
          objective_generating: "Chúng tôi đang xây dựng kế hoạch học tập cá nhân hóa cho bạn...",
          objective_ready: "Kế hoạch học tập của bạn đã sẵn sàng!",
          objective_continue: "Bắt đầu học",
          objective_timeout: "Việc tạo kế hoạch mất quá lâu. Bạn có thể tạo một kế hoạch từ ứng dụng.",
          objective_activities_count: "{{count}} hoạt động được lên kế hoạch cho bạn",
          planner_generation_message: "Vui lòng đợi trong khi chúng tôi tạo kế hoạch học tập cá nhân hóa cho bạn. Bạn có thể luyện tập với cuộc trò chuyện tự do trong lúc chờ!",
          generating: "Đang tạo",
          continue_learning_objective: "Tiếp tục mục tiêu học tập của bạn",
          custom_activities_for_you: "Hoạt động tùy chỉnh cho bạn",
          loading: "Đang tải...",
          generating_activity: "Đang tạo hoạt động của bạn...",
          loading_test_results: "Đang tải kết quả kiểm tra...",
          your_learning_goal: "Mục Tiêu Học Tập Của Bạn",
          edit_goal: "Chỉnh Sửa Mục Tiêu",
          your_learning_activities: "Các Hoạt Động Học Tập Của Bạn",
          planner_activities_description: "Hoàn thành các hoạt động này theo thứ tự để đạt được mục tiêu học tập của bạn.",
          activity: "Hoạt Động",
          view_learning_plan: "Xem Kế Hoạch Học Tập",
          test_learning_path: "Kiểm tra lộ trình học tập của bạn",
          learning_progress: "Tiến Trình Học Tập",
          activity_progress_label: "Tiến độ hoạt động",
          activity_details_button: "Chi tiết",
          activity_details_vocabulary: "Từ vựng",
          activity_details_focus: "Trọng tâm",
          activity_details_format: "Hình thức",
          activity_details_current: "Phiên này",
          activity_details_best: "Kết quả tốt nhất",
          activity_details_attempts: "Số lần thử",
          activity_details_summary: "Tóm tắt cho đến nay",
          activity_details_acquired: "Đã nắm được",
          activity_details_error: "Lỗi",
          conversation_highlights: "Điểm nổi bật khi học",
          conversation_highlight_legend: "Chú giải phần đánh dấu trong bản ghi",
          conversation_legend_error: "Lỗi của bạn",
          conversation_legend_correction: "Sửa lỗi",
          conversation_legend_learned: "Học lần đầu",
          conversation_legend_reviewed: "Đã ôn lại",
          conversation_summary_title: "Tóm tắt cuộc trò chuyện",
          conversation_top_errors: "Các lỗi chính",
          conversation_no_errors: "Không phát hiện lỗi nào cần ưu tiên.",
          conversation_top_concepts: "Các khái niệm đã học chính",
          conversation_no_concepts: "Chưa phát hiện khái niệm nào.",
          conversation_learned_marker: "Học lần đầu",
          conversation_reviewed_marker: "Đã ôn lại",
          activities: "hoạt động",
          start_your_first_activity: "Bắt đầu hoạt động đầu tiên để theo dõi tiến trình",
          all_activities_started: "Tiến trình tuyệt vời! Bạn đã bắt đầu tất cả hoạt động",
          learning_plan_complete_title: "Bạn đã hoàn thành lộ trình học của mình!",
          learning_plan_complete_description: "Tuyệt vời — bạn đã hoàn thành tất cả {{count}} hoạt động. Hãy làm bài đánh giá để biết trình độ hiện tại và nên luyện gì tiếp theo, rồi đặt mục tiêu mới dựa trên đó.",
          create_new_learning_plan: "Đặt mục tiêu học mới",
          practice_activity_again: "Luyện lại một hoạt động",
          learning_plan_complete_hint: "Đã hoàn thành lộ trình! Đặt mục tiêu mới để tiếp tục",
          confirmNewLearningPlanAfterCompletion: "Bạn đã hoàn thành lộ trình này. Tạo lộ trình mới sẽ thay thế bằng các hoạt động mới. Tiếp tục?",
          keep_practicing_activities: "Tiếp tục! Hãy tiếp tục luyện tập các hoạt động để cải thiện",
          no_learning_goal_message: "Thiết lập một kế hoạch học tập cá nhân hóa để theo dõi tiến trình của bạn và đạt được các mục tiêu học tiếng Pháp của bạn.",
          set_your_learning_goal: "Đặt Mục Tiêu Học Tập Của Bạn",
          microphone_permission_required: "Yêu cầu quyền truy cập micro",
          microphone_permission_message: "Ứng dụng này yêu cầu quyền truy cập micro để hoạt động ở chế độ thời gian thực.",
          allow_microphone: "Cho phép micro",
          // Header tooltips for accessibility
          tooltip_activities: "Hoạt Động",
          tooltip_planner: "Kế Hoạch Học Tập",
          tooltip_feedback: "Gửi Phản Hồi",
          tooltip_more: "Tùy Chọn Khác",
          // Modal accessibility (WCAG)
          close_modal: "Đóng hộp thoại",
          // Skip link and main content (WCAG)
          skip_to_main: "Chuyển đến nội dung chính",
          main_content_label: "Nội dung chính",
          // Speech Profile
          speech_profile_title: "Hồ sơ giọng nói",
          speech_profile_instructions: "Đọc to từng câu để chúng tôi hiệu chỉnh hồ sơ giọng nói của bạn.",
          speech_profile_no_sentences: "Các câu chẩn đoán chưa có sẵn cho tổ hợp ngôn ngữ của bạn. Vui lòng quay lại sau.",
          speech_profile_back_to_app: "Quay lại ứng dụng",
          speech_profile_listen: "Nghe",
          speech_profile_playing: "Đang phát...",
          speech_profile_record: "Ghi âm",
          speech_profile_stop: "Dừng",
          speech_profile_analyzing: "Đang phân tích phát âm...",
          speech_profile_analysis_error: "Không thể phân tích phát âm. Vui lòng thử lại.",
          speech_profile_try_again: "Thử lại",
          speech_profile_next: "Tiếp theo",
          speech_profile_complete_title: "Đã cập nhật hồ sơ giọng nói!",
          speech_profile_complete_message: "Hồ sơ giọng nói của bạn đã được cập nhật. AI sẽ hiểu phát âm của bạn tốt hơn.",
          speech_profile_overall_score: "Điểm phát âm tổng thể",
          speech_profile_start_practicing: "Bắt đầu luyện tập",
          // Activity page & test results
          analyzing_conversation: "Đang phân tích kết quả của bạn...",
          analyzing_may_take_time: "Quá trình này có thể mất đến một phút",
          results_score_overview: "Điểm của bạn",
          results_cefr_level: "Cấp độ CEFR",
          results_oral_comprehension: "Nghe hiểu",
          results_oral_production: "Nói",
          results_feedback: "Nhận xét cá nhân",
          results_learning_plan: "Các bước tiếp theo được đề xuất",
          results_audio_analysis: "Phân tích âm thanh",
          results_pronunciation: "Phát âm",
          results_vocabulary: "Sử dụng từ vựng",
          results_grammar: "Ngữ pháp & chia động từ",
          results_fluency: "Độ trôi chảy",
          results_email_prompt: "Nhập email của bạn để nhận kết quả chi tiết",
          contact_cta_heading: "Sẵn sàng thúc đẩy việc học ngôn ngữ của đội ngũ?",
          contact_cta_text: "Liên hệ để đưa đội ngũ của bạn tham gia",
          activity_not_found: "Không tìm thấy hoạt động",
          // Onboarding translations
          ...onboardingTranslations.vi,
          objective_generating_1: "Đang phân tích mục tiêu học tập của bạn...",
          objective_generating_2: "Đang chọn các hoạt động phù hợp với trình độ của bạn...",
          objective_generating_3: "Đang xây dựng chương trình học riêng cho bạn...",
          objective_generating_4: "Đang chọn từ vựng liên quan đến công việc của bạn...",
          objective_generating_5: "Đang sắp xếp lộ trình học của bạn theo từng bước...",
          objective_generating_6: "Sắp xong! Đang hoàn thiện kế hoạch của bạn...",
          confirmReplaceLearningPlan: "Thao tác này sẽ thay thế lộ trình học hiện tại và toàn bộ tiến độ của bạn. Bạn có chắc không?",
          yes: "Có",
          confirm_mode_change_title: "Đổi chế độ hội thoại?",
          confirm_mode_change_body: "Thao tác này sẽ đặt lại hoạt động hiện tại và bắt đầu một cuộc hội thoại mới. Bạn có muốn tiếp tục không?",
          feedback_question: "Chúng tôi có thể làm gì để cải thiện sản phẩm?",
          feedback_description: "Hãy cung cấp càng nhiều chi tiết càng tốt!",
          submit_feedback: "Gửi phản hồi",
          planner_question: "Mục tiêu học tập của bạn là gì?",
          planner_description: "Hãy giải thích ngắn gọn! Ví dụ: thuyết trình với khách hàng.",
          submit_objective: "Gửi mục tiêu",
          termsAgreement: "Tôi đã đọc và đồng ý với",
          tutorial_completed_title: "Magnifique!",
          tutorial_completed_message: "Chúc mừng bạn đã hoàn thành hoạt động.\nBạn sẽ sớm nhận được email tóm tắt.\nHãy quay lại danh sách hoạt động để tiếp tục hành trình học tập.",
          go_to_activities: "Đến danh sách hoạt động",
          native_language_label: "Tiếng mẹ đẻ của bạn là gì?",
          email_capture_title: "Bắt đầu thôi!",
          email_capture_message: "Trước tiên, hãy nhập email công việc của bạn để tiếp tục:",
          first_name_placeholder: "Nhập tên của bạn",
          email_placeholder: "Nhập email công việc của bạn",
          continue_button: "Tiếp tục",
          invalid_email: "Vui lòng nhập địa chỉ email hợp lệ",
          email_consent_label: "Tôi đồng ý nhận email theo dõi về dịch vụ này.",
          email_newsletter_label: "Đăng ký nhận bản tin",
          email_newsletter_note: "Thỉnh thoảng gửi email khi có bài kiểm tra mới.",
          email_work_hint: "Email công việc là tốt nhất để chúng tôi điều chỉnh nội dung theo môi trường làm việc của bạn. Bạn vẫn có thể tiếp tục với email này.",
          work_intent_title: "Bạn học ngôn ngữ này cho công việc phải không?",
          work_intent_current_job: "Có, cho công việc hiện tại của tôi",
          work_intent_career: "Có, cho sự nghiệp hoặc việc tìm việc làm",
          work_intent_team: "Tôi đang tìm hiểu cho nhóm của mình (Nhân sự / Đào tạo)",
          work_intent_personal: "Không, đây là mục đích cá nhân / học tập / luyện thi",
          col_free_test: "Bài kiểm tra miễn phí",
          col_full_platform: "Nền tảng đầy đủ",
          col_lite_version: "Phiên bản Lite",
          col_full_version: "Phiên bản đầy đủ",
          feature_lite_preview: "Xem trước cách việc học ngôn ngữ đang thay đổi",
          feature_lite_slower: "Tương tác chậm hơn",
          feature_lite_one_activity: "Chỉ một hoạt động động",
          feature_lite_no_domain: "Không điều chỉnh theo lĩnh vực công việc",
          feature_lite_no_test: "Không có bài kiểm tra xếp lớp hoặc phân tích",
          feature_lite_no_memory: "Không có bộ nhớ",
          feature_lite_no_feedback: "Không có phân tích hay phản hồi từ các cuộc hội thoại",
          feature_activity_free_test: "Làm bài kiểm tra xếp lớp miễn phí",
          feature_activity_retake: "Làm lại bài kiểm tra và theo dõi tiến độ",
          feature_activity_personalized: "Hoạt động học tập được cá nhân hóa",
          feature_activity_domain: "Phù hợp với lĩnh vực công việc của bạn",
          feature_activity_memory: "Bộ nhớ AI xuyên suốt các cuộc hội thoại",
          feature_activity_feedback: "Phản hồi và phân tích chi tiết",
          email_capture_terms_prefix: "Khi tiếp tục, bạn đồng ý với",
          email_capture_terms_link: "Điều khoản dịch vụ",
          email_capture_terms_and: "và",
          email_capture_privacy_link: "Chính sách bảo mật",
          placement_test_completed: "Bạn đã hoàn thành hoạt động, hãy xem kết quả nhé!",
          placement_test_title: "Hết giờ!",
          plannerBeingCreated: "Lộ trình học của bạn đang được tạo. Vui lòng đợi một lát...",
          planner_pending_objective: "Đang tạo lộ trình cho: \"{{objective}}\"",
          plannerCreationTimeout: "Quá thời gian tạo lộ trình học. Vui lòng thử lại.",
          continue_learning_path: "Tiếp tục lộ trình học",
          generating_activity_1: "Đang chuẩn bị một hoạt động riêng cho bạn...",
          generating_activity_2: "Đang tạo điều gì đó đặc biệt cho trình độ của bạn...",
          generating_activity_3: "Đang cá nhân hóa thử thách tiếp theo của bạn...",
          generating_activity_4: "Đang xây dựng hoạt động dựa trên sở thích của bạn...",
          generating_activity_5: "Đang chuẩn bị một bài tập thú vị cho bạn...",
          generating_activity_6: "Đang thiết kế bài học riêng của bạn...",
          generating_activity_7: "Đang tạo một hoạt động độc đáo cho mục tiêu của bạn...",
          generating_activity_8: "Đang chuẩn bị hành trình học tiếp theo của bạn...",
        },
      },
      hi: {
        translation: {
          confirmReset: "क्या आप वाकई रीसेट करना चाहते हैं?",
          confirmEndActivity: "आपकी वर्तमान गतिविधि समाप्त हो जाएगी। क्या आप सुनिश्चित हैं?",
          confirmEndOnboarding: "हम इस परिचयात्मक बातचीत को पूरा करने की सलाह देते हैं — इसमें केवल कुछ मिनट लगते हैं और यह हमें आपकी सीखने की प्रक्रिया को वैयक्तिकृत करने में मदद करता है। क्या आप वाकई समाप्त करना चाहते हैं?",
          onboarding_complete_title: "बहुत अच्छा!",
          onboarding_complete_body: "आपने अपनी पहली बातचीत पूरी कर ली। हम अब आपके बारे में जो कुछ भी सीखा उसके आधार पर आपकी व्यक्तिगत शिक्षण योजना बना रहे हैं।",
          planner_edit_hint: "आप इस लक्ष्य को कभी भी संपादित कर सकते हैं।",
          commencer: "शुरू करें",
          remind_me_later: "मुझे बाद में याद दिलाएँ",
          continue_your_learning_plan: "अपनी सीखने की योजना जारी रखें",
          start_your_learning_plan: "अपनी सीखने की योजना शुरू करें",
          commencer_select_activity: "सभी गतिविधियाँ देखें",
          or: "या",
          warning_volume: "सुनिश्चित करें कि आपकी वॉल्यूम चालू है!",
          ai_can_make_mistakes: "Madame AI भी सीख रही है! कई AI टूल्स की तरह, वह भी गलतियाँ कर सकती है",
          initial_text: "हरा बटन दबाएँ और कहें \"{{targetGreeting}}\"",
          end_conversation: "यह बातचीत समाप्त कर देगा। क्या आप सुनिश्चित हैं?",
          end_conversation_action: "मेरी शिक्षण योजना देखें",
          preparing_your_plan: "आपकी योजना तैयार की जा रही है…",
          time_remaining: "शेष समय",
          end_label: "समाप्त",
          activity_start_conversation: "एक बातचीत शुरू करें",
          activity_coming_soon: "जल्द आ रहा है",
          activity_tutorial: "ट्यूटोरियल",
          cancel: "रद्द करें",
          new_conversation: "नई बातचीत",
          reset_conversation: "सब कुछ रीसेट करें",
          restart_onboarding: "परिचय फिर से शुरू करें",
          status_disconnected: "आप डिस्कनेक्ट हो गए हैं। फिर से बोलने का प्रयास करें या पृष्ठ को रीफ्रेश करें",
          status_listening: "सुन रहा है...",
          status_speaking: "बोल रहा है!",
          status_prefix: "मैडम AI है",
          setting_native_language: "मातृभाषा",
          setting_target_language: "सीखने की भाषा",
          setting_realtime: "वास्तविक समय की बातचीत",
          setting_microphone: "माइक्रोफ़ोन",
          setting_group_conversation: "बातचीत",
          setting_microphone_system_default: "सिस्टम डिफ़ॉल्ट",
          setting_microphone_change_hint_chrome: "अपना माइक्रोफ़ोन बदलने के लिए, पता बार के बाईं ओर के आइकन पर क्लिक करें और साइट सेटिंग्स → माइक्रोफ़ोन खोलें, या इस पते को नए टैब में खोलें:",
          setting_microphone_change_hint_generic: "अपना माइक्रोफ़ोन बदलने के लिए, अपने ब्राउज़र की साइट सेटिंग्स (पता बार के बाईं ओर का आइकन) → माइक्रोफ़ोन खोलें, या अपने सिस्टम की ध्वनि सेटिंग्स में डिफ़ॉल्ट इनपुट बदलें।",
          setting_use_custom_agent: "कस्टम एजेंट का उपयोग करें",
          premium_feature: "प्रीमियम सुविधा",
          language_variant: "फ्रेंच वैरिएंट",
          language_france: "फ्रांस",
          language_quebec: "क्यूबेक",
          setting_echo_cancellation: "प्रतिध्वनि रद्दीकरण",
          setting_noise_suppression: "शोर दमन",
          setting_auto_gain: "स्वचालित लाभ नियंत्रण",
          setting_audio_model: "ऑडियो मॉडल",
          stt_model_default: "डिफ़ॉल्ट",
          stt_model_conversaflex: "Conversaflex AI",
          setting_tts_speed: "बोलने की गति",
          tts_speed_050: "बहुत धीमा (0.5x)",
          tts_speed_075: "धीमा (0.75x)",
          tts_speed_085: "थोड़ा धीमा (0.85x)",
          tts_speed_100: "सामान्य (1x)",
          setting_hide_assistant_text: "सहायक का पाठ छुपाएं",
          setting_preferred_pronoun: "पसंदीदा सर्वनाम",
          pronoun_unspecified: "अनिर्दिष्ट",
          pronoun_masculine: "पुल्लिंग (il)",
          pronoun_feminine: "स्त्रीलिंग (elle)",
          pronoun_nonbinary: "नॉन-बाइनरी (वे)",
          sign_out: "साइन आउट",
          confirm_variant_change_title: "फ्रेंच वैरिएंट बदलें?",
          confirm_variant_change_body: "रीयलटाइम में फ्रेंच वैरिएंट बदलने से नई बातचीत शुरू होगी। क्या आप जारी रखना चाहते हैं?",
          confirm_restart: "हाँ, पुनः प्रारंभ करें",
          back: 'वापस',
          close: 'बंद करें',
          last: 'अंतिम',
          next: 'अगला',
          nextLabelWithProgress: 'अगला (चरण {step} का {steps})',
          open: 'संवाद खोलें',
          welcomeMessage: "Madame AI में आपका स्वागत है!",
          practiceMessage: "बिना किसी डर के रोज़ाना फ्रेंच वार्तालाप का अभ्यास करें!",
          ageLabel: "आयु:",
          motherTongueLabel: "मातृभाषा:",
          levelLabel: "स्तर:",
          getStarted: "शुरू करें",
          beginner: "शुरुआती",
          intermediate: "मध्यम",
          advanced: "Avancé",
          termsMessage: "मैंने शर्तें और गोपनीयता नीति पढ़ी है",
          startTutorial: "ट्यूटोरियल शुरू करें",
          skip: "छोड़ें",
          infoTooltip: "यदि आप वयस्क हैं, तो हम आपके लिए AI को अनुकूलित करने और प्रासंगिक जानकारी सहेजने के लिए ऑडियो और वार्तालाप डेटा सहेजते हैं। जब शिक्षार्थी 18 वर्ष से कम उम्र का होता है, तो हम ऑडियो सहेजते नहीं हैं।",
          adult: "वयस्क",
          step1Content: "Bonjour! यह Madame AI है। वह आपको फ्रेंच सीखने और अभ्यास करने में मदद करेगी!",
          step2Content: "ऊपर विकल्प हैं। यहां जाएं एक गतिविधि शुरू करने के लिए, एक स्वतंत्र बातचीत, या कुछ सेटिंग्स बदलने के लिए। रीयलटाइम मोड एक अधिक सुचारू बातचीत है, जो अधिक उन्नत शिक्षार्थियों के लिए अनुशंसित है।",
          step3Content: "जब आप शुरू करने के लिए तैयार हों, तो \"Commencer\" पर क्लिक करें",
          activities_work: "कार्य गतिविधियाँ",
          activities_basic: "रोज़मर्रा की स्थितियाँ",
          activities_pitch: "आपकी सेल्स पिच",
          activity_pitch_practice: "अपनी सेल्स पिच का अभ्यास करें",
          activity_travail: "काम से संबंधित चर्चा शुरू करें",
          activity_learning_path: "अपनी सीखने की योजना जारी रखें",
          activities_test_practices: "परीक्षा का अभ्यास करें",
          // Everyday-situation activities
          activity_culture_social_norms: "संस्कृति और सामाजिक शिष्टाचार",
          activity_greetings_introductions: "अभिवादन और परिचय",
          activity_numbers: "संख्याएँ, तारीखें और समय",
          activity_people_relationships: "लोग और रिश्ते",
          activity_food_drinks: "खाना और पेय",
          activity_places_directions_transport: "स्थान, रास्ता और यातायात",
          activity_appointments: "अपॉइंटमेंट और फ़ोन कॉल",
          activity_weather_small_talk: "मौसम और हल्की बातचीत",
          activity_questions_requests_clarification: "सवाल, अनुरोध और स्पष्टीकरण",
          activity_shopping_errands: "खरीदारी और रोज़ के काम",
          activity_colors: "रंग और वर्णन",
          fallback_no_audio: "सुनाई नहीं दिया, कृपया फिर से प्रयास करें।",
          browser_not_supported: "आपका ब्राउज़र ऑडियो सुविधाओं का समर्थन नहीं करता। कृपया Chrome, Safari या Firefox का उपयोग करें।",
          no_audio_detected: "कोई ऑडियो नहीं मिला। कृपया फिर से बोलने का प्रयास करें।",
          audio_decode_error: "ऑडियो फ़ाइल संसाधित नहीं की जा सकी। कृपया फिर से रिकॉर्ड करने का प्रयास करें।",
          recording_timeout: "कनेक्शन का समय समाप्त हो गया। कृपया फिर से रिकॉर्ड करने का प्रयास करें।",
          activity_start_timeout: "हम आपकी गतिविधि शुरू नहीं कर सके। कृपया अपना कनेक्शन जाँचें और फिर से प्रयास करें।",
          microphone_not_working: "आपका माइक्रोफ़ोन हम तक नहीं पहुँच रहा है। कृपया पेज को फिर से लोड करें और दोबारा शुरू करें।",
          connection_error: "कनेक्शन त्रुटि। कृपया पुनः प्रयास करें।",
          sending_for_analysis: "विश्लेषण के लिए बातचीत भेजी जा रही है...",
          max_recording_reached: "50 सेकंड की अधिकतम रिकॉर्डिंग समय पहुँच गई। आपकी ऑडियो सबमिट की जा चुकी है।",
          recording_start_label: "रिकॉर्डिंग शुरू करें",
          recording_stop_label: "रिकॉर्डिंग बंद करें",
          recording_hint_idle: "रिकॉर्ड करने के लिए टैप करें, या बोलते समय बटन दबाए रखें।",
          recording_hint_stop: "जब आप पूरा कर लें तो दोबारा टैप करें।",
          recording_hint_release: "जब आप पूरा कर लें तो बटन छोड़ दें।",
          recording_too_short: "भेजने के लिए बहुत छोटा है। रिकॉर्डिंग शुरू करने के लिए एक बार टैप करें।",
          placement_test_needed_title: "शुरू करने से पहले",
          placement_test_needed_body: "मैडम एआई को और भी सटीक बनाने के लिए, हम आपको हमारी प्लेसमेंट परीक्षा देने के लिए प्रोत्साहित करते हैं। क्या आप अभी शुरू करना चाहेंगे?",
          placement_test_duration_note: "इसमें आमतौर पर लगभग 15 मिनट लगते हैं।",
          test_results_title: "परीक्षा पूर्ण",
          test_results_body: "आपके परिणामों का विश्लेषण किया जा रहा है। आपको अपने विस्तृत परिणाम ईमेल द्वारा प्राप्त होंगे और वे जल्द ही आपके डैशबोर्ड में उपलब्ध होंगे।",
          test_results_ok: "समझ गया!",
          daily_limit_message: "आपने आज यह परीक्षा पहले ही पूरी कर ली है। यहाँ आपके नवीनतम परिणाम हैं। दोबारा प्रयास करने के लिए कल वापस आएँ!",
          trial_session_limit_title: "आप आज की सीमा तक पहुँच गए हैं",
          trial_session_limit_message: "आप हर दिन 2 निःशुल्क सत्रों का अभ्यास कर सकते हैं। काम के लिए सीख रहे हैं? हमसे संपर्क करें और हम आपकी मदद करेंगे; अन्यथा कल वापस आएँ।",
          trial_session_limit_cta: "हमसे संपर्क करें",
          exam_results_pending: "आपकी परीक्षा का विश्लेषण किया जा रहा है! आपको जल्द ही ईमेल द्वारा विस्तृत परिणाम प्राप्त होंगे। अपने परिणाम देखने के लिए बाद में इस पृष्ठ को रीफ़्रेश करें।",
          conversation_feedback_title: "आपकी बातचीत कैसी रही?",
          star: "तारा",
          stars: "तारे",
          submit: "जमा करें",
          feedback_tags_label: "क्या गलत हुआ?",
          feedback_tag_bug: "एक त्रुटि थी",
          feedback_tag_not_understood: "वह मुझे समझ नहीं पाती",
          feedback_tag_not_interesting: "विषय दिलचस्प नहीं था",
          feedback_tag_too_hard: "बहुत कठिन",
          feedback_tag_too_easy: "बहुत आसान",
          feedback_tags_label_positive: "आपको क्या पसंद आया?",
          feedback_tag_audio_issues: "ऑडियो समस्याएं",
          feedback_tag_too_slow: "बहुत धीमा",
          feedback_tag_repetitive: "बहुत दोहराव",
          feedback_tag_good_topic: "अच्छा विषय",
          feedback_tag_right_level: "सही स्तर",
          feedback_tag_good_feedback: "अच्छी प्रतिक्रिया",
          feedback_tag_understood_me: "मुझे समझा",
          feedback_tag_learned_something: "कुछ नया सीखा",
          feedback_tag_fun: "मज़ेदार",
          feedback_tag_natural_conversation: "स्वाभाविक बातचीत",
          feedback_comment_placeholder: "कोई अतिरिक्त टिप्पणी? (वैकल्पिक)",
          feedback_required_hint: "कृपया एक टैग चुनें या टिप्पणी जोड़ें ताकि हम सुधार कर सकें।",
          feedback_sent: "आपका प्रतिप्रयास सफल हुआ है!",
          error_no_selection_to_save: "सहेजने के लिए कोई टेक्स्ट चयनित नहीं है।",
          error_user_not_found_for_library: "उपयोगकर्ता नहीं मिला। लाइब्रेरी में सहेजा नहीं जा सका।",
          text_saved_to_library_success: "आपकी लाइब्रेरी में सहेजा गया!",
          error_saving_to_library: "लाइब्रेरी में सहेजा नहीं जा सका। {{message}}",
          save_to_library_button: "लाइब्रेरी में सहेजें",
          error_no_conversation_id_for_vocab: "वार्तालाप संदर्भ नहीं मिला। आइटम इसके बिना सहेजा जाएगा।",
          save_banner_prompt: "\"<strong>{{snippet}}</strong>\" सहेजें?",
          loading_learning_plan: "आपकी अधिगम योजना लोड की जा रही है...",
          generating_learning_plan: "आपकी अधिगम योजना तैयार की जा रही है",
          objective_generating: "हम आपकी व्यक्तिगत अधिगम योजना बना रहे हैं...",
          objective_ready: "आपकी अधिगम योजना तैयार है!",
          objective_continue: "सीखना शुरू करें",
          objective_timeout: "योजना बनाने में बहुत समय लगा। आप ऐप से एक बना सकते हैं।",
          objective_activities_count: "आपके लिए {{count}} गतिविधियाँ नियोजित",
          planner_generation_message: "कृपया प्रतीक्षा करें जब तक हम आपकी व्यक्तिगत अधिगम योजना बनाते हैं। आप इस बीच मुक्त वार्तालाप का अभ्यास कर सकते हैं!",
          generating: "तैयार हो रहा है",
          continue_learning_objective: "अपने अधिगम उद्देश्य को जारी रखें",
          custom_activities_for_you: "आपके लिए अनुकूलित गतिविधियाँ",
          loading: "लोड हो रहा है...",
          generating_activity: "आपकी गतिविधि तैयार हो रही है...",
          loading_test_results: "परीक्षा परिणाम लोड हो रहे हैं...",
          your_learning_goal: "आपका अधिगम लक्ष्य",
          edit_goal: "लक्ष्य संपादित करें",
          your_learning_activities: "आपकी अधिगम गतिविधियाँ",
          planner_activities_description: "अपने अधिगम लक्ष्य को प्राप्त करने के लिए इन गतिविधियों को क्रम में पूरा करें।",
          activity: "गतिविधि",
          view_learning_plan: "अधिगम योजना देखें",
          test_learning_path: "अपने सीखने के पथ का परीक्षण करें",
          learning_progress: "अधिगम प्रगति",
          activity_progress_label: "गतिविधि प्रगति",
          activity_details_button: "विवरण",
          activity_details_vocabulary: "शब्दावली",
          activity_details_focus: "फोकस",
          activity_details_format: "प्रारूप",
          activity_details_current: "इस सत्र में",
          activity_details_best: "सर्वश्रेष्ठ परिणाम",
          activity_details_attempts: "प्रयास",
          activity_details_summary: "अब तक का सारांश",
          activity_details_acquired: "सीख लिया",
          activity_details_error: "गलती",
          conversation_highlights: "सीखने की मुख्य बातें",
          conversation_highlight_legend: "ट्रांसक्रिप्ट हाइलाइट संकेत-सूची",
          conversation_legend_error: "आपकी गलती",
          conversation_legend_correction: "सुधार",
          conversation_legend_learned: "पहली बार सीखा",
          conversation_legend_reviewed: "दोहराया गया",
          conversation_summary_title: "बातचीत का सारांश",
          conversation_top_errors: "मुख्य गलतियाँ",
          conversation_no_errors: "कोई प्राथमिक गलती नहीं मिली।",
          conversation_top_concepts: "मुख्य सीखी गई अवधारणाएँ",
          conversation_no_concepts: "अभी तक कोई अवधारणा नहीं मिली।",
          conversation_learned_marker: "पहली बार सीखा",
          conversation_reviewed_marker: "दोहराया गया",
          activities: "गतिविधियाँ",
          start_your_first_activity: "प्रगति को ट्रैक करने के लिए अपनी पहली गतिविधि शुरू करें",
          all_activities_started: "बहुत अच्छी प्रगति! आपने सभी गतिविधियाँ शुरू की हैं",
          learning_plan_complete_title: "आपने अपनी लर्निंग प्लान पूरी कर ली है!",
          learning_plan_complete_description: "बहुत बढ़िया — आपने सभी {{count}} गतिविधियाँ पूरी कर लीं। मूल्यांकन करें ताकि पता चले कि आप कहाँ हैं और आगे क्या अभ्यास करना है, फिर उसी के आधार पर नया लक्ष्य तय करें।",
          create_new_learning_plan: "नया लक्ष्य तय करें",
          practice_activity_again: "कोई गतिविधि दोबारा करें",
          learning_plan_complete_hint: "योजना पूरी! आगे बढ़ने के लिए नया लक्ष्य तय करें",
          confirmNewLearningPlanAfterCompletion: "आपने यह योजना पूरी कर ली है। नई योजना बनाने पर यह नई गतिविधियों से बदल जाएगी। जारी रखें?",
          keep_practicing_activities: "जारी रखें! सुधार के लिए गतिविधियों का अभ्यास जारी रखें",
          no_learning_goal_message: "अपनी प्रगति को ट्रैक करने और अपने फ्रेंच सीखने के लक्ष्यों को प्राप्त करने के लिए एक व्यक्तिगत अधिगम योजना सेट अप करें।",
          set_your_learning_goal: "अपना अधिगम लक्ष्य निर्धारित करें",
          microphone_permission_required: "माइक्रोफ़ोन की अनुमति आवश्यक है",
          microphone_permission_message: "इस एप्लिकेशन को रीयल-टाइम मोड में काम करने के लिए माइक्रोफ़ोन एक्सेस की आवश्यकता है।",
          allow_microphone: "माइक्रोफ़ोन की अनुमति दें",
          // Header tooltips for accessibility
          tooltip_activities: "गतिविधियाँ",
          tooltip_planner: "अधिगम योजना",
          tooltip_feedback: "फीडबैक भेजें",
          tooltip_more: "अधिक विकल्प",
          // Modal accessibility (WCAG)
          close_modal: "संवाद बंद करें",
          // Skip link and main content (WCAG)
          skip_to_main: "मुख्य सामग्री पर जाएं",
          main_content_label: "मुख्य सामग्री",
          // Speech Profile
          speech_profile_title: "वाक् प्रोफ़ाइल",
          speech_profile_instructions: "अपनी वाक् प्रोफ़ाइल को कैलिब्रेट करने के लिए प्रत्येक वाक्य को ज़ोर से पढ़ें।",
          speech_profile_no_sentences: "आपके भाषा संयोजन के लिए नैदानिक वाक्य अभी उपलब्ध नहीं हैं। कृपया बाद में पुनः प्रयास करें।",
          speech_profile_back_to_app: "ऐप पर वापस जाएं",
          speech_profile_listen: "सुनें",
          speech_profile_playing: "चल रहा है...",
          speech_profile_record: "रिकॉर्ड",
          speech_profile_stop: "रोकें",
          speech_profile_analyzing: "उच्चारण का विश्लेषण हो रहा है...",
          speech_profile_analysis_error: "उच्चारण का विश्लेषण नहीं हो सका। कृपया पुनः प्रयास करें।",
          speech_profile_try_again: "पुनः प्रयास करें",
          speech_profile_next: "अगला",
          speech_profile_complete_title: "वाक् प्रोफ़ाइल अपडेट हो गई!",
          speech_profile_complete_message: "आपकी वाक् प्रोफ़ाइल अपडेट हो गई है। AI अब आपके उच्चारण को बेहतर समझेगा।",
          speech_profile_overall_score: "कुल उच्चारण स्कोर",
          speech_profile_start_practicing: "अभ्यास शुरू करें",
          // Activity page & test results
          analyzing_conversation: "आपके परिणामों का विश्लेषण हो रहा है...",
          analyzing_may_take_time: "इसमें एक मिनट तक लग सकता है",
          results_score_overview: "आपका स्कोर",
          results_cefr_level: "CEFR स्तर",
          results_oral_comprehension: "मौखिक समझ",
          results_oral_production: "मौखिक उत्पादन",
          results_feedback: "व्यक्तिगत प्रतिक्रिया",
          results_learning_plan: "अनुशंसित अगले कदम",
          results_audio_analysis: "ऑडियो विश्लेषण",
          results_pronunciation: "उच्चारण",
          results_vocabulary: "शब्दावली उपयोग",
          results_grammar: "व्याकरण और क्रिया रूप",
          results_fluency: "प्रवाह",
          results_email_prompt: "विस्तृत परिणाम प्राप्त करने के लिए अपना ईमेल दर्ज करें",
          contact_cta_heading: "क्या आप अपनी टीम की भाषा सीखने की गति बढ़ाने के लिए तैयार हैं?",
          contact_cta_text: "अपनी टीम को शामिल करने के लिए हमसे संपर्क करें",
          activity_not_found: "गतिविधि नहीं मिली",
          // Onboarding translations
          ...onboardingTranslations.hi,
          objective_generating_1: "आपके सीखने के लक्ष्यों का विश्लेषण किया जा रहा है...",
          objective_generating_2: "आपके स्तर के अनुसार गतिविधियाँ चुनी जा रही हैं...",
          objective_generating_3: "आपके लिए व्यक्तिगत पाठ्यक्रम तैयार किया जा रहा है...",
          objective_generating_4: "आपके काम से जुड़ी शब्दावली चुनी जा रही है...",
          objective_generating_5: "आपका सीखने का मार्ग चरण दर चरण व्यवस्थित किया जा रहा है...",
          objective_generating_6: "बस हो ही गया! आपकी योजना अंतिम रूप ले रही है...",
          confirmReplaceLearningPlan: "इससे आपकी मौजूदा शिक्षण योजना और सारी प्रगति बदल जाएगी। क्या आप निश्चित हैं?",
          yes: "हाँ",
          confirm_mode_change_title: "बातचीत का मोड बदलें?",
          confirm_mode_change_body: "इससे आपकी मौजूदा गतिविधि रीसेट हो जाएगी और नई बातचीत शुरू होगी। क्या आप जारी रखना चाहते हैं?",
          feedback_question: "हम अपने उत्पाद को बेहतर बनाने के लिए क्या कर सकते हैं?",
          feedback_description: "कृपया जितना हो सके उतना विवरण दें!",
          submit_feedback: "प्रतिक्रिया भेजें",
          planner_question: "आपका सीखने का उद्देश्य क्या है?",
          planner_description: "कुछ शब्दों में बताएं! उदाहरण के लिए, किसी ग्राहक के सामने प्रस्तुति देना।",
          submit_objective: "उद्देश्य भेजें",
          termsAgreement: "मैंने पढ़ लिया है और मैं सहमत हूँ",
          tutorial_completed_title: "Magnifique!",
          tutorial_completed_message: "गतिविधि पूरी करने पर बधाई।\nआपको जल्द ही सारांश के साथ एक ईमेल मिलेगा।\nअपनी सीखने की यात्रा जारी रखने के लिए गतिविधियों की सूची पर लौटें।",
          go_to_activities: "गतिविधियों पर जाएँ",
          native_language_label: "आपकी मातृभाषा क्या है?",
          email_capture_title: "चलिए शुरू करते हैं!",
          email_capture_message: "जारी रखने के लिए पहले अपना कार्यालयी ईमेल दर्ज करें:",
          first_name_placeholder: "अपना पहला नाम दर्ज करें",
          email_placeholder: "अपना कार्यालयी ईमेल दर्ज करें",
          continue_button: "जारी रखें",
          invalid_email: "कृपया एक मान्य ईमेल पता दर्ज करें",
          email_consent_label: "मैं इस सेवा के बारे में फ़ॉलो-अप ईमेल प्राप्त करने के लिए सहमत हूँ।",
          email_newsletter_label: "मुझे न्यूज़लेटर में जोड़ें",
          email_newsletter_note: "नए टेस्ट उपलब्ध होने पर कभी-कभार ईमेल।",
          email_work_hint: "कार्यालयी ईमेल सबसे अच्छा है ताकि हम इसे आपके कार्यस्थल के अनुसार ढाल सकें। आप इसी ईमेल के साथ भी आगे बढ़ सकते हैं।",
          work_intent_title: "क्या आप यह भाषा काम के लिए सीख रहे हैं?",
          work_intent_current_job: "हाँ, अपनी मौजूदा नौकरी के लिए",
          work_intent_career: "हाँ, अपने करियर या नौकरी की तलाश के लिए",
          work_intent_team: "मैं इसे अपनी टीम के लिए देख रहा/रही हूँ (HR / प्रशिक्षण)",
          work_intent_personal: "नहीं, यह व्यक्तिगत / स्कूल / परीक्षा की तैयारी के लिए है",
          col_free_test: "निःशुल्क टेस्ट",
          col_full_platform: "पूर्ण प्लेटफ़ॉर्म",
          col_lite_version: "लाइट संस्करण",
          col_full_version: "पूर्ण संस्करण",
          feature_lite_preview: "देखें कि भाषा सीखना कैसे बदल रहा है",
          feature_lite_slower: "धीमी बातचीत",
          feature_lite_one_activity: "केवल एक गतिशील गतिविधि",
          feature_lite_no_domain: "कार्यक्षेत्र के अनुसार अनुकूलित नहीं",
          feature_lite_no_test: "कोई प्लेसमेंट टेस्ट या विश्लेषण नहीं",
          feature_lite_no_memory: "कोई मेमोरी नहीं",
          feature_lite_no_feedback: "आपकी बातचीत का कोई विश्लेषण या प्रतिक्रिया नहीं",
          feature_activity_free_test: "निःशुल्क प्लेसमेंट टेस्ट दें",
          feature_activity_retake: "टेस्ट दोबारा दें और प्रगति देखें",
          feature_activity_personalized: "व्यक्तिगत शिक्षण गतिविधियाँ",
          feature_activity_domain: "आपके कार्यक्षेत्र के अनुसार अनुकूलित",
          feature_activity_memory: "बातचीत के बीच एआई मेमोरी",
          feature_activity_feedback: "विस्तृत प्रतिक्रिया और विश्लेषण",
          email_capture_terms_prefix: "जारी रखने पर, आप हमारी",
          email_capture_terms_link: "सेवा की शर्तें",
          email_capture_terms_and: "और",
          email_capture_privacy_link: "गोपनीयता नीति",
          placement_test_completed: "आपने गतिविधि पूरी कर ली, आइए आपके परिणाम देखें!",
          placement_test_title: "समय समाप्त!",
          plannerBeingCreated: "आपकी शिक्षण योजना बनाई जा रही है। कृपया थोड़ा प्रतीक्षा करें...",
          planner_pending_objective: "इसके लिए योजना बनाई जा रही है: \"{{objective}}\"",
          plannerCreationTimeout: "शिक्षण योजना बनाने में समय समाप्त हो गया। कृपया पुनः प्रयास करें।",
          continue_learning_path: "शिक्षण मार्ग जारी रखें",
          generating_activity_1: "आपके लिए खास एक गतिविधि तैयार की जा रही है...",
          generating_activity_2: "आपके स्तर के लिए कुछ खास बनाया जा रहा है...",
          generating_activity_3: "आपकी अगली चुनौती को व्यक्तिगत बनाया जा रहा है...",
          generating_activity_4: "आपकी रुचियों के आसपास एक गतिविधि बनाई जा रही है...",
          generating_activity_5: "आपके लिए एक मज़ेदार अभ्यास तैयार किया जा रहा है...",
          generating_activity_6: "आपका व्यक्तिगत पाठ डिज़ाइन किया जा रहा है...",
          generating_activity_7: "आपके लक्ष्यों के लिए एक अनोखी गतिविधि बनाई जा रही है...",
          generating_activity_8: "आपका अगला शिक्षण सफ़र तैयार किया जा रहा है...",
        },
      },
      pl: {
        translation: {
          confirmReset: "Czy na pewno chcesz zresetować?",
          confirmEndActivity: "Twoja bieżąca aktywność zostanie zakończona. Czy na pewno?",
          confirmEndOnboarding: "Zalecamy ukończenie tej rozmowy wprowadzającej — zajmie to tylko kilka minut i pomoże nam spersonalizować Twoją naukę. Czy na pewno chcesz zakończyć?",
          onboarding_complete_title: "Świetna robota!",
          onboarding_complete_body: "Ukończyłeś swoją pierwszą rozmowę. Tworzymy teraz Twój spersonalizowany plan nauki na podstawie wszystkiego, czego się o Tobie dowiedzieliśmy.",
          planner_edit_hint: "Będziesz mógł edytować ten cel w dowolnym momencie.",
          commencer: "Rozpocznij",
          remind_me_later: "Przypomnij mi później",
          continue_your_learning_plan: "Kontynuuj swój plan nauki",
          start_your_learning_plan: "Rozpocznij swój plan nauki",
          commencer_select_activity: "Zobacz wszystkie aktywności",
          or: "lub",
          warning_volume: "Upewnij się, że głośniki są WŁĄCZONE!",
          ai_can_make_mistakes: "Madame AI też się uczy! Jak wiele narzędzi AI, może popełniać błędy",
          initial_text: "Kliknij zielony przycisk i powiedz \"{{targetGreeting}}\"",
          end_conversation: "To zakończy rozmowę. Czy jesteś pewien?",
          end_conversation_action: "Zobacz mój plan nauki",
          preparing_your_plan: "Przygotowywanie Twojego planu…",
          time_remaining: "Pozostały czas",
          end_label: "Zakończ",
          activity_start_conversation: "Rozpocznij rozmowę",
          activity_coming_soon: "Wkrótce",
          activity_tutorial: "Samouczek",
          cancel: "Anuluj",
          new_conversation: "Nowa rozmowa",
          reset_conversation: "Zresetuj wszystko",
          restart_onboarding: "Uruchom ponownie wprowadzenie",
          status_disconnected: "Zostałeś rozłączony. Spróbuj mówić ponownie lub odśwież stronę",
          status_listening: "słuchanie...",
          status_speaking: "mówienie!",
          status_prefix: "Madame AI jest",
          setting_native_language: "Język ojczysty",
          setting_target_language: "Język do nauki",
          setting_realtime: "Rozmowa w czasie rzeczywistym",
          setting_microphone: "Mikrofon",
          setting_group_conversation: "Rozmowa",
          setting_microphone_system_default: "Domyślny systemowy",
          setting_microphone_change_hint_chrome: "Aby zmienić mikrofon, kliknij ikonę po lewej stronie paska adresu i otwórz Ustawienia witryny → Mikrofon, lub otwórz ten adres w nowej karcie:",
          setting_microphone_change_hint_generic: "Aby zmienić mikrofon, otwórz ustawienia witryny w przeglądarce (ikona po lewej stronie paska adresu) → Mikrofon, lub zmień domyślne urządzenie wejściowe w ustawieniach dźwięku systemu.",
          setting_use_custom_agent: "Użyj niestandardowego agenta",
          premium_feature: "Funkcja premium",
          language_variant: "Wariant języka francuskiego",
          language_france: "Francja",
          language_quebec: "Québec",
          setting_echo_cancellation: "Usuwanie echa",
          setting_noise_suppression: "Tłumienie szumów",
          setting_auto_gain: "Automatyczna kontrola wzmocnienia",
          setting_audio_model: "Model audio",
          stt_model_default: "Domyślny",
          stt_model_conversaflex: "Conversaflex AI",
          setting_tts_speed: "Prędkość mowy",
          tts_speed_050: "Bardzo wolno (0.5x)",
          tts_speed_075: "Wolniej (0.75x)",
          tts_speed_085: "Nieco wolniej (0.85x)",
          tts_speed_100: "Normalnie (1x)",
          setting_hide_assistant_text: "Ukryj tekst asystenta",
          setting_preferred_pronoun: "Preferowany zaimek",
          pronoun_unspecified: "Nieokreślony",
          pronoun_masculine: "Męski (il)",
          pronoun_feminine: "Żeński (elle)",
          pronoun_nonbinary: "Niebinarny (oni)",
          sign_out: "Wyloguj się",
          confirm_variant_change_title: "Zmień wariant języka francuskiego?",
          confirm_variant_change_body: "Zmiana wariantu francuskiego w trybie czasu rzeczywistego rozpocznie nową rozmowę. Czy chcesz kontynuować?",
          confirm_restart: "Tak, uruchom ponownie",
          back: 'Wstecz',
          close: 'Zamknij',
          last: 'Ostatni',
          next: 'Następny',
          nextLabelWithProgress: 'Następny (Krok {step} z {steps})',
          open: 'Otwórz dialog',
          welcomeMessage: "Witamy w Madame AI!",
          practiceMessage: "Ćwicz codzienne rozmowy po francusku bez obawy przed oceną!",
          ageLabel: "Wiek:",
          motherTongueLabel: "Język ojczysty:",
          levelLabel: "Poziom:",
          getStarted: "Zaczynaj",
          beginner: "Początkujący",
          intermediate: "Średniozaawansowany",
          advanced: "Avancé",
          termsMessage: "Przeczytałem warunki i politykę prywatności",
          startTutorial: "Rozpocznij samouczek",
          skip: "Pomiń",
          infoTooltip: "Zapisujemy dźwięk i dane rozmowy, jeśli jesteś dorosły, aby dostosować AI do Ciebie i zapisać odpowiednie informacje. Nie zapisujemy dźwięku, gdy uczeń ma mniej niż 18 lat.",
          adult: "Dorosły",
          step1Content: "Bonjour! To jest Madame AI. Ona pomoże Ci nauczyć się i ćwiczyć francuski!",
          step2Content: "Na górze są opcje. Idź tutaj, aby rozpocząć aktywność, swobodną rozmowę lub zmienić niektóre ustawienia. Tryb Realtime to płynniejsza rozmowa, zalecana dla bardziej zaawansowanych uczniów.",
          step3Content: "Kiedy będziesz gotowy do rozpoczęcia, kliknij \"Commencer\"",
          activities_work: "Działania związane z pracą",
          activities_basic: "Sytuacje codzienne",
          activities_pitch: "Twoja prezentacja sprzedażowa",
          activity_pitch_practice: "Przećwicz swoją prezentację sprzedażową",
          activity_travail: "Rozpocznij dyskusję związaną z pracą",
          activity_learning_path: "Kontynuuj swój plan nauki",
          activities_test_practices: "Przećwicz test",
          // Everyday-situation activities
          activity_culture_social_norms: "Kultura i normy społeczne",
          activity_greetings_introductions: "Powitania i przedstawianie się",
          activity_numbers: "Liczby, daty i godziny",
          activity_people_relationships: "Ludzie i relacje",
          activity_food_drinks: "Jedzenie i napoje",
          activity_places_directions_transport: "Miejsca, kierunki i transport",
          activity_appointments: "Wizyty i rozmowy telefoniczne",
          activity_weather_small_talk: "Pogoda i rozmowa towarzyska",
          activity_questions_requests_clarification: "Pytania, prośby i wyjaśnienia",
          activity_shopping_errands: "Zakupy i codzienne sprawy",
          activity_colors: "Kolory i opisy",
          fallback_no_audio: "Nie usłyszałem, spróbuj ponownie.",
          browser_not_supported: "Twoja przeglądarka nie obsługuje funkcji audio. Wypróbuj Chrome, Safari lub Firefox.",
          no_audio_detected: "Nie wykryto dźwięku. Spróbuj ponownie mówić.",
          audio_decode_error: "Nie można przetworzyć pliku audio. Spróbuj nagrać ponownie.",
          recording_timeout: "Przekroczono limit czasu połączenia. Spróbuj nagrać ponownie.",
          activity_start_timeout: "Nie udało się rozpocząć aktywności. Sprawdź połączenie i spróbuj ponownie.",
          microphone_not_working: "Twój mikrofon do nas nie dociera. Odśwież stronę i zacznij od nowa.",
          connection_error: "Błąd połączenia. Spróbuj ponownie.",
          sending_for_analysis: "Wysyłanie rozmowy do analizy...",
          max_recording_reached: "Osiągnięto maksymalny czas nagrywania wynoszący 50 sekund. Twoje audio zostało przesłane.",
          recording_start_label: "Rozpocznij nagrywanie",
          recording_stop_label: "Zatrzymaj nagrywanie",
          recording_hint_idle: "Dotknij, aby nagrać, lub przytrzymaj przycisk podczas mówienia.",
          recording_hint_stop: "Dotknij ponownie, gdy skończysz.",
          recording_hint_release: "Puść przycisk, gdy skończysz.",
          recording_too_short: "Za krótkie, by wysłać. Dotknij raz, aby rozpocząć nagrywanie.",
          placement_test_needed_title: "Zanim zaczniesz",
          placement_test_needed_body: "Aby Madame AI była jeszcze dokładniejsza, zachęcamy do wykonania naszego testu poziomującego. Chcesz zacząć teraz?",
          placement_test_duration_note: "Zwykle zajmuje to około 15 minut.",
          test_results_title: "Test zakończony",
          test_results_body: "Twoje wyniki są analizowane. Otrzymasz szczegółowe wyniki e-mailem i wkrótce będą dostępne w Twoim panelu.",
          test_results_ok: "Rozumiem!",
          daily_limit_message: "Już dzisiaj ukończyłeś ten test. Oto Twoje najnowsze wyniki. Wróć jutro, aby spróbować ponownie!",
          trial_session_limit_title: "Osiągnąłeś dzisiejszy limit",
          trial_session_limit_message: "Możesz ćwiczyć 2 bezpłatne sesje dziennie. Uczysz się do pracy? Skontaktuj się z nami, a wszystko skonfigurujemy — w przeciwnym razie wróć jutro.",
          trial_session_limit_cta: "Skontaktuj się z nami",
          exam_results_pending: "Twój egzamin jest analizowany! Wkrótce otrzymasz szczegółowe wyniki e-mailem. Odśwież tę stronę później, aby zobaczyć swoje wyniki.",
          conversation_feedback_title: "Jak przebiegła Twoja rozmowa?",
          star: "gwiazdka",
          stars: "gwiazdki",
          submit: "Wyślij",
          feedback_tags_label: "Co poszło nie tak?",
          feedback_tag_bug: "Wystąpił błąd",
          feedback_tag_not_understood: "Nie rozumie mnie",
          feedback_tag_not_interesting: "Temat nie był interesujący",
          feedback_tag_too_hard: "Za trudne",
          feedback_tag_too_easy: "Za łatwe",
          feedback_tags_label_positive: "Co Ci się podobało?",
          feedback_tag_audio_issues: "Problemy z dźwiękiem",
          feedback_tag_too_slow: "Za wolno",
          feedback_tag_repetitive: "Zbyt powtarzalne",
          feedback_tag_good_topic: "Dobry temat",
          feedback_tag_right_level: "Odpowiedni poziom",
          feedback_tag_good_feedback: "Dobra informacja zwrotna",
          feedback_tag_understood_me: "Mnie zrozumiała",
          feedback_tag_learned_something: "Nauczyłem się czegoś",
          feedback_tag_fun: "Zabawne",
          feedback_tag_natural_conversation: "Naturalna rozmowa",
          feedback_comment_placeholder: "Dodatkowe komentarze? (opcjonalnie)",
          feedback_required_hint: "Wybierz tag lub dodaj komentarz, abyśmy mogli się poprawić.",
          feedback_sent: "Twoja opinia została wysłana!",
          error_no_selection_to_save: "Nie wybrano tekstu do zapisania.",
          error_user_not_found_for_library: "Nie znaleziono użytkownika. Nie można zapisać w bibliotece.",
          text_saved_to_library_success: "Zapisano w Twojej bibliotece!",
          error_saving_to_library: "Nie można było zapisać w bibliotece. {{message}}",
          save_to_library_button: "Zapisz w bibliotece",
          error_no_conversation_id_for_vocab: "Nie znaleziono kontekstu rozmowy. Element zostanie zapisany bez niego.",
          save_banner_prompt: "Zapisać \"<strong>{{snippet}}</strong>\"?",
          loading_learning_plan: "Ładowanie Twojego planu nauki...",
          generating_learning_plan: "Generowanie Twojego Planu Nauki",
          objective_generating: "Tworzymy Twój spersonalizowany plan nauki...",
          objective_ready: "Twój plan nauki jest gotowy!",
          objective_continue: "Zacznij się uczyć",
          objective_timeout: "Generowanie planu trwało zbyt długo. Możesz utworzyć plan w aplikacji.",
          objective_activities_count: "{{count}} aktywności zaplanowanych dla Ciebie",
          planner_generation_message: "Proszę czekać, tworzymy Twój spersonalizowany plan nauki. Możesz w międzyczasie ćwiczyć swobodną konwersację!",
          generating: "Generowanie",
          continue_learning_objective: "Kontynuuj swój cel nauki",
          custom_activities_for_you: "Niestandardowe aktywności dla Ciebie",
          loading: "Ładowanie...",
          generating_activity: "Generowanie Twojej aktywności...",
          loading_test_results: "Ładowanie wyników testu...",
          your_learning_goal: "Twój Cel Nauki",
          edit_goal: "Edytuj Cel",
          your_learning_activities: "Twoje Aktywności Nauki",
          planner_activities_description: "Ukończ te aktywności w kolejności, aby osiągnąć swój cel nauki.",
          activity: "Aktywność",
          view_learning_plan: "Zobacz Plan Nauki",
          test_learning_path: "Przetestuj swoją ścieżkę nauki",
          learning_progress: "Postęp w Nauce",
          activity_progress_label: "Postęp aktywności",
          activity_details_button: "Szczegóły",
          activity_details_vocabulary: "Słownictwo",
          activity_details_focus: "Zakres",
          activity_details_format: "Format",
          activity_details_current: "Ta sesja",
          activity_details_best: "Najlepszy wynik",
          activity_details_attempts: "Próby",
          activity_details_summary: "Podsumowanie dotychczas",
          activity_details_acquired: "Opanowane",
          activity_details_error: "Błąd",
          conversation_highlights: "Najważniejsze z nauki",
          conversation_highlight_legend: "Legenda wyróżnień w transkrypcji",
          conversation_legend_error: "Twój błąd",
          conversation_legend_correction: "Poprawka",
          conversation_legend_learned: "Nauczone po raz pierwszy",
          conversation_legend_reviewed: "Powtórzone",
          conversation_summary_title: "Podsumowanie rozmowy",
          conversation_top_errors: "Najważniejsze błędy",
          conversation_no_errors: "Nie wykryto błędów wymagających uwagi.",
          conversation_top_concepts: "Najważniejsze poznane zagadnienia",
          conversation_no_concepts: "Nie wykryto jeszcze żadnych zagadnień.",
          conversation_learned_marker: "Nauczone po raz pierwszy",
          conversation_reviewed_marker: "Powtórzone",
          activities: "aktywności",
          start_your_first_activity: "Rozpocznij swoją pierwszą aktywność, aby śledzić postępy",
          all_activities_started: "Świetny postęp! Rozpocząłeś wszystkie aktywności",
          learning_plan_complete_title: "Ukończyłeś swój plan nauki!",
          learning_plan_complete_description: "Świetna robota — ukończyłeś wszystkie aktywności ({{count}}). Zrób ocenę, aby zobaczyć, na jakim jesteś etapie i nad czym pracować dalej, a potem ustaw nowy cel na jej podstawie.",
          create_new_learning_plan: "Ustaw nowy cel nauki",
          practice_activity_again: "Powtórz aktywność",
          learning_plan_complete_hint: "Plan ukończony! Ustaw nowy cel, aby kontynuować",
          confirmNewLearningPlanAfterCompletion: "Ukończyłeś ten plan. Utworzenie nowego zastąpi go nowymi aktywnościami. Kontynuować?",
          keep_practicing_activities: "Tak trzymaj! Kontynuuj ćwiczenie aktywności, aby się poprawić",
          no_learning_goal_message: "Skonfiguruj spersonalizowany plan nauki, aby śledzić swoje postępy i osiągnąć cele nauki francuskiego.",
          set_your_learning_goal: "Ustaw Swój Cel Nauki",
          microphone_permission_required: "Wymagane zezwolenie na mikrofon",
          microphone_permission_message: "Ta aplikacja wymaga dostępu do mikrofonu, aby działać w trybie rzeczywistym.",
          allow_microphone: "Zezwól na mikrofon",
          // Header tooltips for accessibility
          tooltip_activities: "Aktywności",
          tooltip_planner: "Plan Nauki",
          tooltip_feedback: "Wyślij Opinię",
          tooltip_more: "Więcej Opcji",
          // Modal accessibility (WCAG)
          close_modal: "Zamknij okno dialogowe",
          // Skip link and main content (WCAG)
          skip_to_main: "Przejdź do głównej treści",
          main_content_label: "Główna treść",
          // Speech Profile
          speech_profile_title: "Profil głosowy",
          speech_profile_instructions: "Przeczytaj każde zdanie na głos, abyśmy mogli skalibrować Twój profil głosowy.",
          speech_profile_no_sentences: "Zdania diagnostyczne nie są jeszcze dostępne dla Twojej kombinacji językowej. Sprawdź ponownie później.",
          speech_profile_back_to_app: "Powrót do aplikacji",
          speech_profile_listen: "Posłuchaj",
          speech_profile_playing: "Odtwarzanie...",
          speech_profile_record: "Nagraj",
          speech_profile_stop: "Zatrzymaj",
          speech_profile_analyzing: "Analizowanie wymowy...",
          speech_profile_analysis_error: "Nie udało się przeanalizować wymowy. Spróbuj ponownie.",
          speech_profile_try_again: "Spróbuj ponownie",
          speech_profile_next: "Dalej",
          speech_profile_complete_title: "Profil głosowy zaktualizowany!",
          speech_profile_complete_message: "Twój profil głosowy został zaktualizowany. AI będzie teraz lepiej rozumiała Twoją wymowę.",
          speech_profile_overall_score: "Ogólna ocena wymowy",
          speech_profile_start_practicing: "Zacznij ćwiczyć",
          // Activity page & test results
          analyzing_conversation: "Analizowanie wyników...",
          analyzing_may_take_time: "To może potrwać do minuty",
          results_score_overview: "Twój wynik",
          results_cefr_level: "Poziom CEFR",
          results_oral_comprehension: "Rozumienie ze słuchu",
          results_oral_production: "Produkcja ustna",
          results_feedback: "Spersonalizowany komentarz",
          results_learning_plan: "Zalecane następne kroki",
          results_audio_analysis: "Analiza audio",
          results_pronunciation: "Wymowa",
          results_vocabulary: "Użycie słownictwa",
          results_grammar: "Gramatyka i koniugacja",
          results_fluency: "Płynność",
          results_email_prompt: "Podaj swój e-mail, aby otrzymać szczegółowe wyniki",
          contact_cta_heading: "Gotowy, aby przyspieszyć naukę języków w swoim zespole?",
          contact_cta_text: "Skontaktuj się z nami, aby dołączyć swój zespół",
          activity_not_found: "Nie znaleziono aktywności",
          // Onboarding translations
          ...onboardingTranslations.pl,
          objective_generating_1: "Analizujemy Twoje cele nauki...",
          objective_generating_2: "Dobieramy aktywności dopasowane do Twojego poziomu...",
          objective_generating_3: "Tworzymy program nauki specjalnie dla Ciebie...",
          objective_generating_4: "Wybieramy słownictwo przydatne w Twojej pracy...",
          objective_generating_5: "Układamy Twoją ścieżkę nauki krok po kroku...",
          objective_generating_6: "Już prawie! Finalizujemy Twój plan...",
          confirmReplaceLearningPlan: "Spowoduje to zastąpienie obecnego planu nauki i całego postępu. Czy na pewno?",
          yes: "Tak",
          confirm_mode_change_title: "Zmienić tryb rozmowy?",
          confirm_mode_change_body: "Spowoduje to zresetowanie bieżącej aktywności i rozpoczęcie nowej rozmowy. Chcesz kontynuować?",
          feedback_question: "Co możemy zrobić, aby ulepszyć nasz produkt?",
          feedback_description: "Podaj jak najwięcej szczegółów!",
          submit_feedback: "Wyślij opinię",
          planner_question: "Jaki jest Twój cel nauki?",
          planner_description: "Wyjaśnij w kilku słowach! Na przykład: przeprowadzić prezentację dla klienta.",
          submit_objective: "Wyślij cel",
          termsAgreement: "Przeczytałem(-am) i akceptuję",
          tutorial_completed_title: "Magnifique!",
          tutorial_completed_message: "Gratulacje z okazji ukończenia aktywności.\nWkrótce otrzymasz e-mail z podsumowaniem.\nWróć do listy aktywności, aby kontynuować naukę.",
          go_to_activities: "Przejdź do aktywności",
          native_language_label: "Jaki jest Twój język ojczysty?",
          email_capture_title: "Zaczynajmy!",
          email_capture_message: "Najpierw podaj swój służbowy adres e-mail, aby kontynuować:",
          first_name_placeholder: "Wpisz swoje imię",
          email_placeholder: "Wpisz swój służbowy adres e-mail",
          continue_button: "Kontynuuj",
          invalid_email: "Podaj prawidłowy adres e-mail",
          email_consent_label: "Zgadzam się na otrzymywanie wiadomości e-mail dotyczących tej usługi.",
          email_newsletter_label: "Zapisz mnie do newslettera",
          email_newsletter_note: "Okazjonalne wiadomości, gdy pojawią się nowe testy.",
          email_work_hint: "Służbowy adres e-mail jest najlepszy, abyśmy mogli dopasować usługę do Twojego miejsca pracy. Możesz też kontynuować z tym adresem.",
          work_intent_title: "Czy uczysz się tego języka do pracy?",
          work_intent_current_job: "Tak, do mojej obecnej pracy",
          work_intent_career: "Tak, dla kariery lub poszukiwania pracy",
          work_intent_team: "Sprawdzam to dla mojego zespołu (HR / szkolenia)",
          work_intent_personal: "Nie, to do celów prywatnych / szkoły / przygotowania do egzaminu",
          col_free_test: "Darmowy test",
          col_full_platform: "Pełna platforma",
          col_lite_version: "Wersja Lite",
          col_full_version: "Pełna wersja",
          feature_lite_preview: "Zobacz, jak zmienia się nauka języków",
          feature_lite_slower: "Wolniejsze interakcje",
          feature_lite_one_activity: "Tylko jedna dynamiczna aktywność",
          feature_lite_no_domain: "Brak dopasowania do branży zawodowej",
          feature_lite_no_test: "Brak testu poziomującego i analizy",
          feature_lite_no_memory: "Brak pamięci",
          feature_lite_no_feedback: "Brak analizy i informacji zwrotnej z Twoich rozmów",
          feature_activity_free_test: "Wykonaj darmowy test poziomujący",
          feature_activity_retake: "Powtarzaj testy i śledź postępy",
          feature_activity_personalized: "Spersonalizowane aktywności edukacyjne",
          feature_activity_domain: "Dopasowane do Twojej branży zawodowej",
          feature_activity_memory: "Pamięć AI pomiędzy rozmowami",
          feature_activity_feedback: "Szczegółowa informacja zwrotna i analiza",
          email_capture_terms_prefix: "Kontynuując, akceptujesz nasz",
          email_capture_terms_link: "Regulamin",
          email_capture_terms_and: "oraz",
          email_capture_privacy_link: "Politykę prywatności",
          placement_test_completed: "Ukończono aktywność, zobaczmy Twoje wyniki!",
          placement_test_title: "Czas minął!",
          plannerBeingCreated: "Twój plan nauki jest tworzony. Poczekaj chwilę...",
          planner_pending_objective: "Tworzenie planu dla: \"{{objective}}\"",
          plannerCreationTimeout: "Przekroczono czas tworzenia planu nauki. Spróbuj ponownie.",
          continue_learning_path: "Kontynuuj ścieżkę nauki",
          generating_activity_1: "Przygotowujemy aktywność specjalnie dla Ciebie...",
          generating_activity_2: "Tworzymy coś wyjątkowego na Twój poziom...",
          generating_activity_3: "Personalizujemy Twoje kolejne wyzwanie...",
          generating_activity_4: "Budujemy aktywność wokół Twoich zainteresowań...",
          generating_activity_5: "Przygotowujemy dla Ciebie ciekawe ćwiczenie...",
          generating_activity_6: "Projektujemy Twoją spersonalizowaną lekcję...",
          generating_activity_7: "Tworzymy wyjątkową aktywność dla Twoich celów...",
          generating_activity_8: "Przygotowujemy Twoją kolejną przygodę z nauką...",
        },
      },
      tr: {
        translation: {
          confirmReset: "Sıfırlamak istediğinizden emin misiniz?",
          confirmEndActivity: "Mevcut etkinliğiniz sona erecek. Emin misiniz?",
          confirmEndOnboarding: "Bu tanıtım konuşmasını tamamlamanızı öneririz — sadece birkaç dakika sürer ve öğrenmenizi kişiselleştirmemize yardımcı olur. Bitirmek istediğinizden emin misiniz?",
          onboarding_complete_title: "Harika iş!",
          onboarding_complete_body: "İlk konuşmanızı tamamladınız. Sizin hakkınızda öğrendiğimiz her şeye dayanarak kişiselleştirilmiş öğrenme planınızı oluşturuyoruz.",
          planner_edit_hint: "Bu hedefi istediğiniz zaman düzenleyebilirsiniz.",
          commencer: "Başla",
          remind_me_later: "Daha sonra hatırlat",
          continue_your_learning_plan: "Öğrenme planınıza devam edin",
          start_your_learning_plan: "Öğrenme planınızı başlatın",
          commencer_select_activity: "Tüm etkinlikleri gör",
          or: "veya",
          warning_volume: "Ses seviyenizi ve mikrofonunuzu AÇTIĞINIZDAN emin olun!",
          ai_can_make_mistakes: "Madame AI de öğreniyor! Birçok yapay zekâ aracı gibi o da hata yapabilir",
          initial_text: "Yeşil düğmeye tıklayın ve \"{{targetGreeting}}\" deyin",
          end_conversation: "Bu sohbeti sonlandıracak. Emin misiniz?",
          end_conversation_action: "Öğrenme planımı gör",
          preparing_your_plan: "Planınız hazırlanıyor…",
          time_remaining: "Kalan Süre",
          end_label: "Bitir",
          activity_start_conversation: "Serbest sohbet",
          activity_coming_soon: "Yakında",
          activity_tutorial: "Eğitim",
          cancel: "İptal",
          new_conversation: "Yeni Sohbet",
          reset_conversation: "Her Şeyi Sıfırla",
          restart_onboarding: "Tanıtımı Yeniden Başlat",
          status_disconnected: "Bağlantınız kesildi. Tekrar konuşmayı deneyin veya sayfayı yenileyin",
          status_listening: "dinliyor...",
          status_speaking: "konuşuyor!",
          status_prefix: "Madame AI",
          setting_native_language: "Ana dil",
          setting_target_language: "Öğrenilecek dil",
          setting_realtime: "Gerçek zamanlı sohbet",
          setting_microphone: "Mikrofon",
          setting_group_conversation: "Sohbet",
          setting_microphone_system_default: "Sistem varsayılanı",
          setting_microphone_change_hint_chrome: "Mikrofonunuzu değiştirmek için adres çubuğunun solundaki simgeye tıklayın ve Site ayarları → Mikrofon bölümünü açın veya bu adresi yeni bir sekmede açın:",
          setting_microphone_change_hint_generic: "Mikrofonunuzu değiştirmek için tarayıcınızın site ayarlarını açın (adres çubuğunun solundaki simge) → Mikrofon veya sisteminizin ses ayarlarında varsayılan girişi değiştirin.",
          setting_use_custom_agent: "Özel temsilci kullan",
          premium_feature: "Premium özellik",
          language_variant: "Fransızca varyantı",
          language_france: "Fransızca",
          language_quebec: "Quebec Fransızca",
          setting_echo_cancellation: "Yankı iptali",
          setting_noise_suppression: "Gürültü bastırma",
          setting_auto_gain: "Otomatik kazanç kontrolü",
          setting_audio_model: "Ses modeli",
          stt_model_default: "Varsayılan",
          stt_model_conversaflex: "Conversaflex AI",
          setting_tts_speed: "Konuşma hızı",
          tts_speed_050: "Çok yavaş (0.5x)",
          tts_speed_075: "Yavaş (0.75x)",
          tts_speed_085: "Biraz yavaş (0.85x)",
          tts_speed_100: "Normal (1x)",
          setting_hide_assistant_text: "Asistan metnini gizle",
          setting_preferred_pronoun: "Tercih edilen zamir",
          pronoun_unspecified: "Belirtilmemiş",
          pronoun_masculine: "Eril (il)",
          pronoun_feminine: "Dişil (elle)",
          pronoun_nonbinary: "İkili olmayan (o)",
          sign_out: "Çıkış yap",
          confirm_variant_change_title: "Fransızca varyantını değiştir?",
          confirm_variant_change_body: "Gerçek zamanlı olarak Fransızca varyantını değiştirmek yeni bir sohbet başlatır. Devam etmek istiyor musunuz?",
          confirm_restart: "Evet, yeniden başlat",
          back: 'Geri',
          close: 'Kapat',
          last: 'Pratik yapmaya başla!',
          next: 'İleri',
          nextLabelWithProgress: 'İleri (Adım {step} / {steps})',
          open: 'Diyalogu aç',
          welcomeMessage: "Madame AI'ya hoş geldiniz!",
          practiceMessage: "Yargılanma korkusu olmadan günlük Fransızca konuşma pratiği yapın!",
          ageLabel: "Yaş:",
          motherTongueLabel: "Ana dil:",
          levelLabel: "Seviye:",
          getStarted: "Başla",
          beginner: "Başlangıç",
          intermediate: "Orta",
          advanced: "İleri",
          termsMessage: "Şartları ve gizlilik politikasını okudum",
          termsAgreement: "Şartları ve gizlilik politikasını okudum ve kabul ediyorum",
          startTutorial: "Eğitimi başlat",
          skip: "Atla",
          infoTooltip: "Yetişkin iseniz, size özel AI ayarlamak ve ilgili bilgileri kaydetmek için ses ve sohbet verilerinizi kaydederiz. Öğrenci 18 yaş altındaysa ses kaydetmeyiz.",
          adult: "Yetişkin",
          step1Content: "Bonjour! Bu Madame AI. Size Fransızca öğrenme ve pratik yapmada yardımcı olacak!",
          step2Content: "Üstte seçenekler var. Bir etkinlik başlatmak, serbest sohbet etmek veya bazı ayarları değiştirmek için buraya gidin. Gerçek zamanlı mod daha akıcı bir sohbettir, daha ileri öğrenciler için önerilir.",
          step3Content: "Başlamaya hazır olduğunuzda, \"Commencer\" düğmesine tıklayın",
          tutorial_completed_title: "Mükemmel!",
          tutorial_completed_message: "Etkinliği tamamladığınız için tebrikler.\nKısa süre sonra bir özet e-postası alacaksınız.\nÖğrenme yolculuğunuzu sürdürmek için lütfen etkinlik listesine geri dönün.",
          go_to_activities: "Etkinliklere Git",
          native_language_label: "Ana diliniz nedir?",
          email_capture_title: "Hadi başlayalım!",
          email_capture_message: "Önce devam etmek için iş e-postanızı girin:",
          first_name_placeholder: "Adınızı girin",
          email_placeholder: "İş e-postanızı girin",
          continue_button: "Devam Et",
          invalid_email: "Lütfen geçerli bir e-posta adresi girin",
          email_work_hint: "Deneyimi iş yerinize göre uyarlayabilmemiz için iş e-postası en iyisidir. Yine de bu e-postayla devam edebilirsiniz.",
          col_free_test: "Ücretsiz Test",
          col_full_platform: "Tam Platform",
          col_lite_version: "Lite Sürüm",
          col_full_version: "Tam Sürüm",
          feature_lite_preview: "Dil öğreniminin nasıl geliştiğini görün",
          feature_lite_slower: "Daha yavaş etkileşimler",
          feature_lite_one_activity: "Yalnızca bir dinamik etkinlik",
          feature_lite_no_domain: "Çalışma alanına uyarlanmamış",
          feature_lite_no_test: "Seviye testi veya analiz yok",
          feature_lite_no_memory: "Hafıza yok",
          feature_lite_no_feedback: "Etkileşimlerinizden analiz veya geri bildirim yok",
          feature_activity_free_test: "Ücretsiz seviye testi yapın",
          feature_activity_retake: "Testleri tekrarlayın ve ilerlemenizi takip edin",
          feature_activity_personalized: "Kişiselleştirilmiş öğrenme etkinlikleri",
          feature_activity_domain: "Çalışma alanınıza uyarlanmış",
          feature_activity_memory: "Konuşmalar arası yapay zeka hafızası",
          feature_activity_feedback: "Ayrıntılı geri bildirim ve analiz",
          email_capture_terms_prefix: "Devam ederek,",
          email_capture_terms_link: "Hizmet Şartlarımızı",
          email_capture_terms_and: "ve",
          email_capture_privacy_link: "Gizlilik Politikamızı kabul etmiş olursunuz",
          activities_work: "İş etkinlikleri",
          activities_basic: "Günlük durumlar",
          activities_pitch: "Satış konuşmanız",
          activity_pitch_practice: "Satış konuşmanızı çalışın",
          activity_travail: "İşle ilgili bir tartışma başlat",
          activity_learning_path: "Öğrenme planınızı sürdürün",
          activities_test_practices: "Bir test pratiği yap",
          // Everyday-situation activities
          activity_culture_social_norms: "Kültür ve toplumsal kurallar",
          activity_greetings_introductions: "Selamlaşma ve tanışma",
          activity_numbers: "Sayılar, tarihler ve saatler",
          activity_people_relationships: "İnsanlar ve ilişkiler",
          activity_food_drinks: "Yemek ve içecek",
          activity_places_directions_transport: "Yerler, yol tarifi ve ulaşım",
          activity_appointments: "Randevular ve telefon görüşmeleri",
          activity_weather_small_talk: "Hava durumu ve sohbet",
          activity_questions_requests_clarification: "Sorular, ricalar ve açıklama isteme",
          activity_shopping_errands: "Alışveriş ve günlük işler",
          activity_colors: "Renkler ve tanımlamalar",
          placement_test_completed: "Etkinliği tamamladınız, sonuçlarınızı görelim!",
          placement_test_title: "Süre bitti!",
          fallback_no_audio: "Duyamadım, lütfen tekrar deneyin.",
          browser_not_supported: "Tarayıcınız ses özelliklerini desteklemiyor. Lütfen Chrome, Safari veya Firefox kullanmayı deneyin.",
          no_audio_detected: "Ses algılanmadı. Lütfen tekrar konuşmayı deneyin.",
          audio_decode_error: "Ses dosyası işlenemedi. Lütfen tekrar kaydetmeyi deneyin.",
          recording_timeout: "Bağlantı zaman aşımına uğradı. Lütfen tekrar kaydetmeyi deneyin.",
          activity_start_timeout: "Etkinliğiniz başlatılamadı. Lütfen bağlantınızı kontrol edip tekrar deneyin.",
          microphone_not_working: "Mikrofonunuz bize ulaşmıyor. Lütfen sayfayı yenileyip yeniden başlayın.",
          connection_error: "Bağlantı hatası. Lütfen tekrar deneyin.",
          sending_for_analysis: "Konuşma analiz için gönderiliyor...",
          max_recording_reached: "50 saniyelik maksimum kayıt süresine ulaşıldı. Sesiniz gönderildi.",
          recording_start_label: "Kaydı başlat",
          recording_stop_label: "Kaydı durdur",
          recording_hint_idle: "Kaydetmek için dokunun veya konuşurken düğmeyi basılı tutun.",
          recording_hint_stop: "Bitirdiğinizde tekrar dokunun.",
          recording_hint_release: "Bitirdiğinizde düğmeyi bırakın.",
          recording_too_short: "Göndermek için çok kısa. Kayda başlamak için bir kez dokunun.",
          placement_test_needed_title: "Başlamadan önce",
          placement_test_needed_body: "Madame AI'nın daha da doğru olması için seviye belirleme testimizi yapmanızı öneriyoruz. Şimdi başlamak ister misiniz?",
          placement_test_duration_note: "Bu genellikle yaklaşık 15 dakika sürer.",
          test_results_title: "Test Tamamlandı",
          test_results_body: "Sonuçlarınız analiz ediliyor. Ayrıntılı sonuçlarınızı e-posta ile alacaksınız ve kısa süre içinde kontrol panelinizde mevcut olacaklar.",
          test_results_ok: "Anladım!",
          daily_limit_message: "Bu testi bugün zaten tamamladınız. İşte en son sonuçlarınız. Tekrar denemek için yarın geri gelin!",
          trial_session_limit_title: "Bugünün sınırına ulaştınız",
          trial_session_limit_message: "Günde 2 ücretsiz oturum çalışabilirsiniz. İş için mi öğreniyorsunuz? Bizimle iletişime geçin, sizi hazırlayalım; aksi halde yarın tekrar gelin.",
          trial_session_limit_cta: "Bize ulaşın",
          exam_results_pending: "Sınavınız analiz ediliyor! Ayrıntılı sonuçlarınızı kısa süre içinde e-posta ile alacaksınız. Sonuçlarınızı görmek için bu sayfayı daha sonra yenileyin.",
          conversation_feedback_title: "Konuşmanız nasıl geçti?",
          star: "yıldız",
          stars: "yıldız",
          submit: "Gönder",
          feedback_tags_label: "Ne yanlış gitti?",
          feedback_tag_bug: "Bir hata oluştu",
          feedback_tag_not_understood: "Beni anlamıyor",
          feedback_tag_not_interesting: "Konu ilgi çekici değildi",
          feedback_tag_too_hard: "Çok zor",
          feedback_tag_too_easy: "Çok kolay",
          feedback_tags_label_positive: "Neyi beğendiniz?",
          feedback_tag_audio_issues: "Ses sorunları",
          feedback_tag_too_slow: "Çok yavaş",
          feedback_tag_repetitive: "Çok tekrarlı",
          feedback_tag_good_topic: "İyi konu",
          feedback_tag_right_level: "Doğru seviye",
          feedback_tag_good_feedback: "İyi geri bildirim",
          feedback_tag_understood_me: "Beni anladı",
          feedback_tag_learned_something: "Yeni bir şey öğrendim",
          feedback_tag_fun: "Eğlenceli",
          feedback_tag_natural_conversation: "Doğal konuşma",
          feedback_comment_placeholder: "Ek yorumlarınız var mı? (isteğe bağlı)",
          feedback_required_hint: "Geliştirebilmemiz için lütfen bir etiket seçin veya yorum ekleyin.",
          feedback_sent: "Geri bildiriminiz gönderildi!",
          error_no_selection_to_save: "Kaydedilecek metin seçilmedi.",
          error_user_not_found_for_library: "Kullanıcı bulunamadı. Kütüphaneye kaydedilemez.",
          text_saved_to_library_success: "Kütüphanenize kaydedildi!",
          error_saving_to_library: "Kütüphaneye kaydedilemedi. {{message}}",
          save_to_library_button: "Kütüphaneye Kaydet",
          error_no_conversation_id_for_vocab: "Sohbet bağlamı bulunamadı. Öğe bu olmadan kaydedilecek.",
          save_banner_prompt: "\"<strong>{{snippet}}</strong>\" kaydedilsin mi?",
          loading_learning_plan: "Öğrenme planınız yükleniyor...",
          generating_learning_plan: "Öğrenme Planınız Oluşturuluyor",
          objective_generating: "Kişiselleştirilmiş öğrenme planınızı oluşturuyoruz...",
          objective_ready: "Öğrenme planınız hazır!",
          objective_continue: "Öğrenmeye başla",
          objective_timeout: "Plan oluşturma çok uzun sürdü. Uygulamadan bir plan oluşturabilirsiniz.",
          objective_activities_count: "Sizin için {{count}} aktivite planlandı",
          planner_generation_message: "Lütfen kişiselleştirilmiş öğrenme planınızı oluştururken bekleyin. Bu arada serbest bir konuşma pratiği yapabilirsiniz!",
          generating: "Oluşturuluyor",
          continue_learning_objective: "Öğrenme hedefinize devam edin",
          custom_activities_for_you: "Sizin için özel etkinlikler",
          loading: "Yükleniyor...",
          generating_activity: "Etkinliğiniz oluşturuluyor...",
          loading_test_results: "Test sonuçları yükleniyor...",
          your_learning_goal: "Öğrenme Hedefiniz",
          edit_goal: "Hedefi Düzenle",
          your_learning_activities: "Öğrenme Aktiviteleriniz",
          planner_activities_description: "Öğrenme hedefinize ulaşmak için bu aktiviteleri sırasıyla tamamlayın.",
          activity: "Aktivite",
          view_learning_plan: "Öğrenme Planını Görüntüle",
          test_learning_path: "Öğrenme yolunuzu test edin",
          learning_progress: "Öğrenme İlerlemesi",
          activity_progress_label: "Etkinlik ilerlemesi",
          activity_details_button: "Ayrıntılar",
          activity_details_vocabulary: "Kelime bilgisi",
          activity_details_focus: "Odak",
          activity_details_format: "Biçim",
          activity_details_current: "Bu oturum",
          activity_details_best: "En iyi sonuç",
          activity_details_attempts: "Denemeler",
          activity_details_summary: "Şu ana kadarki özet",
          activity_details_acquired: "Kazanıldı",
          activity_details_error: "Hata",
          conversation_highlights: "Öğrenme öne çıkanları",
          conversation_highlight_legend: "Döküm vurgu açıklamaları",
          conversation_legend_error: "Senin hatan",
          conversation_legend_correction: "Düzeltme",
          conversation_legend_learned: "İlk kez öğrenildi",
          conversation_legend_reviewed: "Tekrar edildi",
          conversation_summary_title: "Konuşma özeti",
          conversation_top_errors: "Başlıca hatalar",
          conversation_no_errors: "Öncelikli bir hata belirlenmedi.",
          conversation_top_concepts: "Başlıca öğrenilen kavramlar",
          conversation_no_concepts: "Henüz bir kavram belirlenmedi.",
          conversation_learned_marker: "İlk kez öğrenildi",
          conversation_reviewed_marker: "Tekrar edildi",
          activities: "aktiviteler",
          start_your_first_activity: "İlerlemeyi takip etmek için ilk aktivitenizi başlatın",
          all_activities_started: "Harika ilerleme! Tüm aktiviteleri başlattınız",
          learning_plan_complete_title: "Öğrenme planınızı tamamladınız!",
          learning_plan_complete_description: "Harika — {{count}} etkinliğin tamamını bitirdiniz. Nerede olduğunuzu ve sırada neyi çalışmanız gerektiğini görmek için değerlendirmeyi yapın, ardından yeni hedefinizi buna göre belirleyin.",
          create_new_learning_plan: "Yeni bir öğrenme hedefi belirle",
          practice_activity_again: "Bir etkinliği tekrar çalış",
          learning_plan_complete_hint: "Plan tamamlandı! Devam etmek için yeni bir hedef belirleyin",
          confirmNewLearningPlanAfterCompletion: "Bu planı tamamladınız. Yeni bir plan oluşturmak onu yeni etkinliklerle değiştirir. Devam edilsin mi?",
          keep_practicing_activities: "Devam edin! Gelişmek için aktivite yapmaya devam edin",
          no_learning_goal_message: "İlerlemenizi takip etmek ve Fransızca öğrenme hedeflerinize ulaşmak için kişiselleştirilmiş bir öğrenme planı oluşturun.",
          set_your_learning_goal: "Öğrenme Hedefinizi Belirleyin",
          microphone_permission_required: "Mikrofon izni gerekli",
          microphone_permission_message: "Bu uygulamanın gerçek zamanlı modda çalışabilmesi için mikrofona erişim izni gerekir.",
          allow_microphone: "Mikrofona izin ver",
          // Header tooltips for accessibility
          tooltip_activities: "Aktiviteler",
          tooltip_planner: "Öğrenme Planı",
          tooltip_feedback: "Geri Bildirim Gönder",
          tooltip_more: "Daha Fazla Seçenek",
          // Modal accessibility (WCAG)
          close_modal: "Diyalogu kapat",
          // Skip link and main content (WCAG)
          skip_to_main: "Ana içeriğe atla",
          main_content_label: "Ana içerik",
          // Speech Profile
          speech_profile_title: "Ses Profili",
          speech_profile_instructions: "Ses profilinizi kalibre etmemiz için her cümleyi yüksek sesle okuyun.",
          speech_profile_no_sentences: "Dil kombinasyonunuz için tanılama cümleleri henüz mevcut değil. Lütfen daha sonra tekrar kontrol edin.",
          speech_profile_back_to_app: "Uygulamaya Dön",
          speech_profile_listen: "Dinle",
          speech_profile_playing: "Oynatılıyor...",
          speech_profile_record: "Kaydet",
          speech_profile_stop: "Durdur",
          speech_profile_analyzing: "Telaffuz analiz ediliyor...",
          speech_profile_analysis_error: "Telaffuz analiz edilemedi. Lütfen tekrar deneyin.",
          speech_profile_try_again: "Tekrar Dene",
          speech_profile_next: "İleri",
          speech_profile_complete_title: "Ses Profili Güncellendi!",
          speech_profile_complete_message: "Ses profiliniz güncellendi. Yapay zeka artık telaffuzunuzu daha iyi anlayacak.",
          speech_profile_overall_score: "Genel telaffuz puanı",
          speech_profile_start_practicing: "Pratik Yapmaya Başla",
          // Activity page & test results
          analyzing_conversation: "Sonuçlarınız analiz ediliyor...",
          analyzing_may_take_time: "Bu bir dakikaya kadar sürebilir",
          results_score_overview: "Puanınız",
          results_cefr_level: "CEFR Seviyesi",
          results_oral_comprehension: "Dinleme Anlama",
          results_oral_production: "Sözlü Üretim",
          results_feedback: "Kişiselleştirilmiş Geri Bildirim",
          results_learning_plan: "Önerilen Sonraki Adımlar",
          results_audio_analysis: "Ses Analizi",
          results_pronunciation: "Telaffuz",
          results_vocabulary: "Kelime Kullanımı",
          results_grammar: "Dilbilgisi ve Çekim",
          results_fluency: "Akıcılık",
          results_email_prompt: "Ayrıntılı sonuçlarınızı almak için e-postanızı girin",
          contact_cta_heading: "Ekibinizin dil öğrenimini hızlandırmaya hazır mısınız?",
          contact_cta_text: "Ekibinizi dahil etmek için bize ulaşın",
          activity_not_found: "Etkinlik bulunamadı",
          // Onboarding translations
          ...onboardingTranslations.tr,
          objective_generating_1: "Öğrenme hedefleriniz analiz ediliyor...",
          objective_generating_2: "Seviyenize uygun etkinlikler seçiliyor...",
          objective_generating_3: "Size özel bir müfredat hazırlanıyor...",
          objective_generating_4: "İşinizle ilgili kelimeler seçiliyor...",
          objective_generating_5: "Öğrenme yolunuz adım adım düzenleniyor...",
          objective_generating_6: "Neredeyse bitti! Planınız tamamlanıyor...",
          confirmReplaceLearningPlan: "Bu işlem mevcut öğrenme planınızı ve tüm ilerlemenizi değiştirecek. Emin misiniz?",
          yes: "Evet",
          confirm_mode_change_title: "Konuşma modu değiştirilsin mi?",
          confirm_mode_change_body: "Bu işlem mevcut etkinliğinizi sıfırlayacak ve yeni bir konuşma başlatacak. Devam etmek istiyor musunuz?",
          feedback_question: "Ürünümüzü geliştirmek için ne yapabiliriz?",
          feedback_description: "Lütfen mümkün olduğunca ayrıntı verin!",
          submit_feedback: "Geri bildirim gönder",
          planner_question: "Öğrenme hedefiniz nedir?",
          planner_description: "Birkaç kelimeyle açıklayın! Örneğin, bir müşteriye sunum yapmak.",
          submit_objective: "Hedefi gönder",
          email_consent_label: "Bu hizmetle ilgili bilgilendirme e-postaları almayı kabul ediyorum.",
          email_newsletter_label: "Beni bültene ekleyin",
          email_newsletter_note: "Yeni testler yayınlandığında ara sıra e-posta gönderilir.",
          work_intent_title: "Bu dili iş için mi öğreniyorsunuz?",
          work_intent_current_job: "Evet, mevcut işim için",
          work_intent_career: "Evet, kariyerim veya iş arayışım için",
          work_intent_team: "Bunu ekibim için değerlendiriyorum (İK / Eğitim)",
          work_intent_personal: "Hayır, kişisel / okul / sınav hazırlığı için",
          plannerBeingCreated: "Öğrenme planınız oluşturuluyor. Lütfen biraz bekleyin...",
          planner_pending_objective: "Şunun için plan oluşturuluyor: \"{{objective}}\"",
          plannerCreationTimeout: "Öğrenme planı oluşturma zaman aşımına uğradı. Lütfen tekrar deneyin.",
          continue_learning_path: "Öğrenme yoluna devam et",
          generating_activity_1: "Size özel bir etkinlik hazırlanıyor...",
          generating_activity_2: "Seviyenize uygun özel bir şey oluşturuluyor...",
          generating_activity_3: "Bir sonraki göreviniz kişiselleştiriliyor...",
          generating_activity_4: "İlgi alanlarınıza göre bir etkinlik hazırlanıyor...",
          generating_activity_5: "Sizin için eğlenceli bir alıştırma hazırlanıyor...",
          generating_activity_6: "Kişiselleştirilmiş dersiniz tasarlanıyor...",
          generating_activity_7: "Hedefleriniz için benzersiz bir etkinlik oluşturuluyor...",
          generating_activity_8: "Bir sonraki öğrenme maceranız hazırlanıyor...",
        },
      },
      ja: {
        translation: {
          confirmReset: "リセットしてもよろしいですか？",
          confirmEndActivity: "現在のアクティビティが終了します。よろしいですか？",
          confirmEndOnboarding: "この紹介会話を完了することをお勧めします — 数分で終わり、あなたの学習をカスタマイズするのに役立ちます。終了してもよろしいですか？",
          onboarding_complete_title: "よくできました！",
          onboarding_complete_body: "最初の会話が完了しました。あなたについて学んだことをもとに、パーソナライズされた学習プランを作成しています。",
          planner_edit_hint: "この目標はいつでも編集できます。",
          objective_generating: "パーソナライズされた学習プランを作成中...",
          objective_generating_1: "学習目標を分析中...",
          objective_generating_2: "レベルに合ったアクティビティを選択中...",
          objective_generating_3: "あなただけのカリキュラムを作成中...",
          objective_generating_4: "仕事に関連する語彙を選択中...",
          objective_generating_5: "学習パスをステップごとに整理中...",
          objective_generating_6: "もう少しです！プランを完成させています...",
          objective_ready: "学習プランの準備ができました！",
          objective_continue: "学習を始める",
          objective_timeout: "プランの生成に時間がかかりすぎました。メインアプリから作成できます。",
          objective_activities_count: "{{count}}のアクティビティが計画されています",
          confirmReplaceLearningPlan: "現在の学習プランとすべての進捗が置き換えられます。よろしいですか？",
          cancel: "キャンセル",
          yes: "はい",
          commencer: "開始",
          remind_me_later: "後で通知する",
          continue_your_learning_plan: "学習プランを続ける",
          start_your_learning_plan: "学習プランを始める",
          commencer_select_activity: "すべてのアクティビティを見る",
          or: "または",
          warning_volume: "音量とマイクがオンになっていることを確認してください！",
          ai_can_make_mistakes: "Madame AI も学習中です！多くの AI ツールと同様に、間違えることがあります",
          initial_text: "緑のボタンをクリックして「{{targetGreeting}}」と言ってください",
          end_conversation: "会話を終了します。よろしいですか？",
          end_conversation_action: "学習プランを見る",
          preparing_your_plan: "プランを準備しています…",
          time_remaining: "残り時間",
          end_label: "終了",
          activity_start_conversation: "フリー会話",
          activity_coming_soon: "近日公開",
          activity_tutorial: "チュートリアル",
          new_conversation: "新しい会話",
          reset_conversation: "すべてリセット",
          restart_onboarding: "オンボーディングをやり直す",
          status_disconnected: "切断されました。もう一度話すか、ページを更新してください",
          status_listening: "聞いています...",
          status_speaking: "話しています！",
          status_prefix: "Madame AI は",
          setting_native_language: "母語",
          setting_target_language: "学習する言語",
          setting_realtime: "リアルタイム会話",
          setting_microphone: "マイク",
          setting_group_conversation: "会話",
          setting_microphone_system_default: "システムのデフォルト",
          setting_microphone_change_hint_chrome: "マイクを変更するには、アドレスバーの左側にあるアイコンをクリックして サイトの設定 → マイク を開くか、このアドレスを新しいタブで開いてください：",
          setting_microphone_change_hint_generic: "マイクを変更するには、ブラウザのサイトの設定（アドレスバーの左側のアイコン）→ マイク を開くか、システムのサウンド設定で既定の入力デバイスを変更してください。",
          setting_use_custom_agent: "カスタムエージェントを使用",
          premium_feature: "プレミアム機能",
          language_variant: "言語バリアント",
          language_france: "フランス",
          language_quebec: "ケベック",
          setting_echo_cancellation: "エコーキャンセル",
          setting_noise_suppression: "ノイズ抑制",
          setting_auto_gain: "自動ゲイン制御",
          setting_audio_model: "音声モデル",
          stt_model_default: "デフォルト",
          stt_model_conversaflex: "Conversaflex AI",
          setting_tts_speed: "話す速度",
          tts_speed_050: "とても遅い (0.5x)",
          tts_speed_075: "遅め (0.75x)",
          tts_speed_085: "やや遅い (0.85x)",
          tts_speed_100: "通常 (1x)",
          setting_hide_assistant_text: "アシスタントのテキストを非表示",
          setting_preferred_pronoun: "希望する代名詞",
          pronoun_unspecified: "未指定",
          pronoun_masculine: "男性 (il)",
          pronoun_feminine: "女性 (elle)",
          pronoun_nonbinary: "ノンバイナリー (they/them)",
          sign_out: "サインアウト",
          confirm_variant_change_title: "言語バリアントを変更しますか？",
          confirm_variant_change_body: "リアルタイムで言語バリアントを変更すると、新しい会話が始まります。続けますか？",
          confirm_mode_change_title: "会話モードを変更しますか？",
          confirm_mode_change_body: "現在のアクティビティがリセットされ、新しい会話が始まります。続けますか？",
          confirm_restart: "はい、やり直す",
          back: '戻る',
          close: '閉じる',
          feedback_question: "製品を改善するためにどうすればよいですか？",
          feedback_description: "できるだけ詳しく教えてください！",
          submit_feedback: "フィードバックを送信",
          planner_question: "学習目標は何ですか？",
          planner_description: "簡単に説明してください！例：クライアントへのプレゼンテーションを行う。",
          submit_objective: "目標を送信",
          last: '練習を始める！',
          next: '次へ',
          nextLabelWithProgress: '次へ (ステップ {step}/{steps})',
          open: 'ダイアログを開く',
          welcomeMessage: "Madame AI へようこそ！",
          practiceMessage: "毎日、評価を気にせず新しい言語で会話を練習しましょう！",
          ageLabel: "年齢：",
          motherTongueLabel: "母語：",
          levelLabel: "レベル：",
          getStarted: "始めましょう",
          beginner: "初心者",
          intermediate: "中級",
          advanced: "上級",
          termsMessage: "利用規約とプライバシーポリシーを確認しました",
          termsAgreement: "以下に同意します：",
          startTutorial: "チュートリアルを開始",
          skip: "スキップ",
          infoTooltip: "成人の場合、AIをカスタマイズし関連情報を保存するために音声と会話データを保存します。18歳未満の学習者の音声は保存しません。",
          adult: "成人",
          step1Content: "Bonjour！こちらはMadame AIです。フランス語の学習と練習をお手伝いします！",
          step2Content: "上部にオプションがあります。アクティビティやフリー会話を始めたり、設定を変更できます。リアルタイムモードはよりスムーズな会話で、上級者におすすめです。",
          step3Content: "準備ができたら、「開始」をクリックしてください",
          tutorial_completed_title: "Magnifique！",
          tutorial_completed_message: "アクティビティの完了おめでとうございます。\nまもなく概要がメールで届きます。\nアクティビティ一覧に戻って学習を続けましょう。",
          go_to_activities: "アクティビティへ",
          native_language_label: "あなたの母語は何ですか？",
          email_capture_title: "始めましょう！",
          email_capture_message: "まず、勤務先のメールアドレスを入力してください：",
          first_name_placeholder: "名前を入力",
          email_placeholder: "勤務先のメールアドレスを入力",
          continue_button: "続ける",
          invalid_email: "有効なメールアドレスを入力してください",
          email_consent_label: "このサービスに関するフォローアップメールの受信に同意します。",
          email_newsletter_label: "ニュースレターに登録する",
          email_newsletter_note: "新しいテストが利用可能になったら、ときどきメールをお送りします。",
          email_work_hint: "勤務先のメールアドレスを使うと、職場に合わせた体験にしやすくなります。このメールアドレスでも続行できます。",
          col_free_test: "無料テスト",
          col_full_platform: "フルプラットフォーム",
          col_lite_version: "ライト版",
          col_full_version: "フル版",
          feature_lite_preview: "言語学習の進化をプレビュー",
          feature_lite_slower: "遅いインタラクション",
          feature_lite_one_activity: "ダイナミックアクティビティ1つのみ",
          feature_lite_no_domain: "業務分野に未対応",
          feature_lite_no_test: "配置テストや分析なし",
          feature_lite_no_memory: "メモリなし",
          feature_lite_no_feedback: "インタラクションの分析やフィードバックなし",
          feature_activity_free_test: "無料配置テストを受ける",
          feature_activity_retake: "テストの再受験と進捗追跡",
          feature_activity_personalized: "パーソナライズされた学習アクティビティ",
          feature_activity_domain: "業務分野に適応",
          feature_activity_memory: "会話間のAIメモリ",
          feature_activity_feedback: "詳細なフィードバックと分析",
          email_capture_terms_prefix: "続けることで、以下に同意します：",
          email_capture_terms_link: "利用規約",
          email_capture_terms_and: "と",
          email_capture_privacy_link: "プライバシーポリシー",
          activities_work: "仕事のアクティビティ",
          activities_basic: "日常の場面",
          activities_pitch: "あなたのセールストーク",
          activity_pitch_practice: "セールストークを練習する",
          activity_travail: "仕事関連のディスカッションを始める",
          activity_learning_path: "学習パスを続ける",
          activities_test_practices: "テスト練習",
          // Everyday-situation activities
          activity_culture_social_norms: "文化と社会のマナー",
          activity_greetings_introductions: "あいさつと自己紹介",
          activity_numbers: "数字・日付・時間",
          activity_people_relationships: "人と人間関係",
          activity_food_drinks: "食べ物と飲み物",
          activity_places_directions_transport: "場所・道案内・交通",
          activity_appointments: "予約と電話",
          activity_weather_small_talk: "天気と世間話",
          activity_questions_requests_clarification: "質問・依頼・聞き返し",
          activity_shopping_errands: "買い物と用事",
          activity_colors: "色と描写",
          placement_test_completed: "アクティビティが完了しました。結果を見てみましょう！",
          placement_test_title: "時間切れです！",
          fallback_no_audio: "聞き取れませんでした。もう一度お試しください。",
          browser_not_supported: "お使いのブラウザは音声機能をサポートしていません。Chrome、Safari、またはFirefoxをお試しください。",
          no_audio_detected: "音声が検出されませんでした。もう一度話してみてください。",
          audio_decode_error: "音声ファイルを処理できませんでした。もう一度録音してください。",
          recording_timeout: "接続がタイムアウトしました。もう一度録音してください。",
          activity_start_timeout: "アクティビティを開始できませんでした。接続を確認してもう一度お試しください。",
          microphone_not_working: "マイクの音声が届いていません。ページを再読み込みしてもう一度お試しください。",
          connection_error: "接続エラー。もう一度お試しください。",
          sending_for_analysis: "会話を分析に送信中...",
          max_recording_reached: "50秒の最大録音時間に達しました。音声が送信されました。",
          recording_start_label: "録音を開始",
          recording_stop_label: "録音を停止",
          recording_hint_idle: "タップして録音するか、話している間ボタンを押し続けてください。",
          recording_hint_stop: "話し終わったらもう一度タップしてください。",
          recording_hint_release: "話し終わったらボタンを離してください。",
          recording_too_short: "短すぎて送信できません。録音を開始するには一度だけタップしてください。",
          generating_activity: "素晴らしいアクティビティを生成中",
          placement_test_needed_title: "始める前に",
          placement_test_needed_body: "Madame AIをさらに正確にするため、配置テストを受けることをお勧めします。今すぐ始めますか？",
          placement_test_duration_note: "通常、約15分かかります。",
          test_results_title: "テスト完了",
          test_results_body: "結果を分析中です。詳細な結果はメールで届き、まもなくダッシュボードで確認できます。",
          test_results_ok: "了解！",
          daily_limit_message: "今日はすでにこのテストを完了しています。最新の結果をご覧ください。明日また挑戦してください！",
          trial_session_limit_title: "本日の上限に達しました",
          trial_session_limit_message: "無料練習は1日2回までです。お仕事のための学習ですか？お問い合わせいただければ設定します。そうでなければ明日またお越しください。",
          trial_session_limit_cta: "お問い合わせ",
          exam_results_pending: "試験を分析中です！詳細な結果はまもなくメールで届きます。後でこのページを更新して結果を確認してください。",
          conversation_feedback_title: "会話はいかがでしたか？",
          star: "星",
          stars: "星",
          submit: "送信",
          feedback_tags_label: "何が問題でしたか？",
          feedback_tag_bug: "バグがありました",
          feedback_tag_not_understood: "理解してもらえなかった",
          feedback_tag_not_interesting: "トピックが面白くなかった",
          feedback_tag_too_hard: "難しすぎた",
          feedback_tag_too_easy: "簡単すぎた",
          feedback_tags_label_positive: "何が良かったですか？",
          feedback_tag_audio_issues: "音声の問題",
          feedback_tag_too_slow: "遅すぎた",
          feedback_tag_repetitive: "繰り返しが多すぎた",
          feedback_tag_good_topic: "良いトピック",
          feedback_tag_right_level: "ちょうどいいレベル",
          feedback_tag_good_feedback: "良いフィードバック",
          feedback_tag_understood_me: "理解してくれた",
          feedback_tag_learned_something: "新しいことを学んだ",
          feedback_tag_fun: "楽しかった",
          feedback_tag_natural_conversation: "自然な会話",
          feedback_comment_placeholder: "追加のコメントはありますか？（任意）",
          feedback_required_hint: "改善できるよう、タグを選ぶかコメントを追加してください。",
          feedback_sent: "フィードバックが送信されました！",
          error_no_selection_to_save: "保存するテキストが選択されていません。",
          error_user_not_found_for_library: "ユーザーが見つかりません。ライブラリに保存できません。",
          text_saved_to_library_success: "ライブラリに保存しました！",
          error_saving_to_library: "ライブラリに保存できませんでした。{{message}}",
          save_to_library_button: "ライブラリに保存",
          error_no_conversation_id_for_vocab: "会話のコンテキストが見つかりません。コンテキストなしで保存されます。",
          save_banner_prompt: "「<strong>{{snippet}}</strong>」を保存しますか？",
          loading_learning_plan: "学習プランを読み込み中...",
          generating_learning_plan: "学習プランを生成中",
          planner_generation_message: "パーソナライズされた学習プランを作成しています。お待ちの間、フリー会話で練習してみてください！",
          generating: "生成中",
          plannerBeingCreated: "学習プランを作成中です。しばらくお待ちください...",
          planner_pending_objective: "プランを作成中：「{{objective}}」",
          plannerCreationTimeout: "学習プランの作成がタイムアウトしました。もう一度お試しください。",
          continue_learning_objective: "学習目標を続ける",
          continue_learning_path: "学習パスを続ける",
          custom_activities_for_you: "あなたのためのカスタムアクティビティ",
          loading: "読み込み中...",
          generating_activity_1: "あなたに合ったアクティビティを調整中...",
          generating_activity_2: "レベルに合わせた特別なものを作成中...",
          generating_activity_3: "次の課題をパーソナライズ中...",
          generating_activity_4: "あなたの興味に合わせたアクティビティを構築中...",
          generating_activity_5: "楽しいエクササイズを準備中...",
          generating_activity_6: "パーソナライズされたレッスンを設計中...",
          generating_activity_7: "目標に合ったユニークなアクティビティを作成中...",
          generating_activity_8: "次の学習アドベンチャーの準備中...",
          loading_test_results: "テスト結果を読み込み中...",
          your_learning_goal: "あなたの学習目標",
          edit_goal: "目標を編集",
          your_learning_activities: "あなたの学習アクティビティ",
          planner_activities_description: "学習目標を達成するために、これらのアクティビティを順番に完了してください。",
          activity: "アクティビティ",
          view_learning_plan: "学習プランを見る",
          test_learning_path: "学習パスをテスト",
          learning_progress: "学習進捗",
          activity_progress_label: "アクティビティの進捗",
          activity_details_button: "詳細",
          activity_details_vocabulary: "語彙",
          activity_details_focus: "フォーカス",
          activity_details_format: "形式",
          activity_details_current: "今回のセッション",
          activity_details_best: "ベスト記録",
          activity_details_attempts: "挑戦回数",
          activity_details_summary: "これまでのまとめ",
          activity_details_acquired: "習得済み",
          activity_details_error: "間違い",
          conversation_highlights: "学習のポイント",
          conversation_highlight_legend: "文字起こしハイライトの凡例",
          conversation_legend_error: "あなたの間違い",
          conversation_legend_correction: "訂正",
          conversation_legend_learned: "初めて学習",
          conversation_legend_reviewed: "復習済み",
          conversation_summary_title: "会話のまとめ",
          conversation_top_errors: "主な間違い",
          conversation_no_errors: "優先して直すべき間違いはありません。",
          conversation_top_concepts: "習得した主な項目",
          conversation_no_concepts: "まだ習得した項目はありません。",
          conversation_learned_marker: "初めて学習",
          conversation_reviewed_marker: "復習済み",
          activities: "アクティビティ",
          start_your_first_activity: "最初のアクティビティを始めて進捗を追跡しましょう",
          all_activities_started: "素晴らしい進捗！すべてのアクティビティを開始しました",
          learning_plan_complete_title: "学習プランを修了しました！",
          learning_plan_complete_description: "お見事です — {{count}} 個のアクティビティをすべて終えました。まず評価を受けて現在の実力と次に取り組むことを確認し、それをもとに新しい目標を設定しましょう。",
          create_new_learning_plan: "新しい学習目標を設定する",
          practice_activity_again: "アクティビティをもう一度練習する",
          learning_plan_complete_hint: "プラン修了！続けるには新しい目標を設定しましょう",
          confirmNewLearningPlanAfterCompletion: "このプランを修了しました。新しいプランを作成すると、新しいアクティビティに置き換わります。続けますか？",
          keep_practicing_activities: "その調子！アクティビティの練習を続けて上達しましょう",
          no_learning_goal_message: "パーソナライズされた学習プランを設定して、進捗を追跡し、フランス語学習の目標を達成しましょう。",
          set_your_learning_goal: "学習目標を設定",
          microphone_permission_required: "マイクの許可が必要です",
          microphone_permission_message: "このアプリケーションはリアルタイムモードで機能するためにマイクへのアクセスが必要です。",
          allow_microphone: "マイクを許可",
          tooltip_activities: "アクティビティ",
          tooltip_planner: "学習プラン",
          tooltip_feedback: "フィードバック",
          tooltip_more: "その他のオプション",
          close_modal: "ダイアログを閉じる",
          skip_to_main: "メインコンテンツへスキップ",
          main_content_label: "メインコンテンツ",
          speech_profile_title: "スピーチプロフィール",
          speech_profile_instructions: "各文を声に出して読んで、スピーチプロフィールを調整してください。",
          speech_profile_no_sentences: "お使いの言語の組み合わせでは、診断文はまだ利用できません。後日お確かめください。",
          speech_profile_back_to_app: "アプリに戻る",
          speech_profile_listen: "聴く",
          speech_profile_playing: "再生中...",
          speech_profile_record: "録音",
          speech_profile_stop: "停止",
          speech_profile_analyzing: "発音を分析中...",
          speech_profile_analysis_error: "発音を分析できませんでした。もう一度お試しください。",
          speech_profile_try_again: "もう一度",
          speech_profile_next: "次へ",
          speech_profile_complete_title: "スピーチプロフィールが更新されました！",
          speech_profile_complete_message: "スピーチプロフィールが更新されました。AIがあなたの発音をより正確に理解するようになります。",
          speech_profile_overall_score: "総合発音スコア",
          speech_profile_start_practicing: "練習を始める",
          analyzing_conversation: "結果を分析中...",
          analyzing_may_take_time: "最大1分かかる場合があります",
          results_score_overview: "あなたのスコア",
          results_cefr_level: "CEFRレベル",
          results_oral_comprehension: "聴解力",
          results_oral_production: "口頭表現",
          results_feedback: "パーソナライズされたフィードバック",
          results_learning_plan: "推奨される次のステップ",
          results_audio_analysis: "音声分析",
          results_pronunciation: "発音",
          results_vocabulary: "語彙の使用",
          results_grammar: "文法と活用",
          results_fluency: "流暢さ",
          results_email_prompt: "詳細な結果を受け取るためにメールアドレスを入力してください",
          contact_cta_heading: "チームの語学学習を加速させる準備はできていますか？",
          contact_cta_text: "チームの参加についてお問い合わせください",
          activity_not_found: "アクティビティが見つかりません",
          // Onboarding translations
          ...onboardingTranslations.ja,
          work_intent_title: "この言語を仕事のために学んでいますか？",
          work_intent_current_job: "はい、現在の仕事のためです",
          work_intent_career: "はい、キャリアや転職活動のためです",
          work_intent_team: "チームのために検討しています（人事・人材育成）",
          work_intent_personal: "いいえ、個人・学校・試験対策のためです",
        },
      },
      tl: {
        translation: tagalogTranslations,
      },
    },
    lng: userLanguageCode, // Initialize with manually detected user language (userLanguage → i18nextLng → querystring → browser → default)
    fallbackLng: 'en', // Fallback language if translation is missing
    debug: false, // Set to true for debugging purposes
    interpolation: {
      escapeValue: false, // React already does escaping
    },
  }).then(() => {
    // Force the language change after initialization
    console.log('🚀 i18next initialized, forcing language to:', userLanguageCode);
    i18n.changeLanguage(userLanguageCode);
  });

// Utility function to synchronize language changes across the app
export const syncLanguageChange = (languageCode) => {
  console.log('🔄 syncLanguageChange called with:', languageCode);

  // Validate language code
  if (!Object.values(languageMap).includes(languageCode)) {
    console.warn(`❌ Invalid language code: ${languageCode}`);
    return;
  }

  // Update both localStorage keys
  localStorage.setItem('userLanguage', languageCode);
  localStorage.setItem('i18nextLng', languageCode);

  // Change i18next language
  i18n.changeLanguage(languageCode);

  // Notify SharedStateContext and other listeners
  window.dispatchEvent(new CustomEvent('userLanguageChanged', { detail: { language: languageCode } }));

  console.log('✅ Language synchronized to:', languageCode);
  console.log('👉 Updated localStorage:', {
    userLanguage: localStorage.getItem('userLanguage'),
    i18nextLng: localStorage.getItem('i18nextLng')
  });
};

export default i18n;
